import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import axios from 'axios';
import { createLatestWorkerRead, workerReadErrorMessage } from '../../lib/latestWorkerRead';
import { reconcileTrainingUploadReceipts } from '../../lib/trainingUploadReceipt';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { isEmploymentConfirmationReceiptCurrent } from '../../lib/employmentConfirmationReceipt';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { 
  CheckCircle, AlertCircle, Clock, Upload, FileText, 
  LogOut, Loader2, AlertTriangle, Calendar, RefreshCw,
  Shield, X, PenTool, Lock, Download, Eye, User, Award, Bell, ChevronDown, ChevronUp,
  ShieldCheck, Circle, Plus, Trash2, Camera, QrCode, Menu, Home, ClipboardCheck, FolderOpen,
} from 'lucide-react';
import { toast } from 'sonner';
import SignaturePad from '../../components/worker/SignaturePad';
import LifecycleStagePill from '../../components/compliance/LifecycleStagePill';
import { Checkbox } from '../../components/ui/checkbox';
import { ScrollArea } from '../../components/ui/scroll-area';
import { Briefcase, GraduationCap, Sparkles, Edit3, Link2, MessageSquare, Receipt } from 'lucide-react';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import ProfileCompletionWizard from '../../components/worker/ProfileCompletionWizard';
import CareCertificateInductionPanel from '../../components/worker/CareCertificateInductionPanel';
import WorkerTrainingRequestButton from '../../components/training/WorkerTrainingRequestButton';
import TrainingCertificateGrid from '../../components/training/TrainingCertificateGrid';
import WorkerDashboardPage from '../../components/worker/WorkerDashboardPage';
import DashboardHeader from '../../components/worker/DashboardHeader';
import WorkerKidCard from '../../components/worker/WorkerKidCard';
import NextActionCard from '../../components/worker/NextActionCard';
import WorkerComplianceDueCard from '../../components/worker/WorkerComplianceDueCard';

// Old static help banner removed in favor of live admin chat support
import WorkerAvailableShiftsCard from '../../components/worker/WorkerAvailableShiftsCard';
import WorkerTodayHome from '../../components/worker/WorkerTodayHome';
import WorkerReadinessNotice from '../../components/worker/WorkerReadinessNotice';
import ShiftMedicationTask from '../../components/worker/ShiftMedicationTask';
import WorkerMyShiftsCard from '../../components/worker/WorkerMyShiftsCard';
import WorkerOngoingObligationsCard from '../../components/worker/WorkerOngoingObligationsCard';
import WorkerAssignedPoliciesCard from '../../components/worker/WorkerAssignedPoliciesCard';
import StructuredAppraisalForm from '../../components/compliance/StructuredAppraisalForm';
import WorkerIncidentsCard from '../../components/worker/WorkerIncidentsCard';
import OnboardingPremiumCard, { OnboardingPriorityPill } from '../../components/worker/OnboardingPremiumCard';
import WorkerPremiumCard, { WorkerPremiumCardHeader } from '../../components/worker/WorkerPremiumCard';
import OnboardingWizardShell from '../../components/worker/OnboardingWizardShell';
import { deriveApplicantNextAction, presentApplicantNextAction } from '../../lib/applicantNextAction';
import InterviewAppointmentCard from '../../components/worker/InterviewAppointmentCard';
import DocumentCaptureField from '../../components/worker/DocumentCaptureField';
import {
  isFormAdminComplete,
  isFormAwaitingReview,
  isFormWorkerActionRequired,
  getFormActionLabel,
  getFormActionButtonClass,
  getFormRowAccentClass,
} from '../../components/worker/workerFormStatus';
import SubmittedDocumentsVault from '../../components/documents/SubmittedDocumentsVault';
import ApplicantHome, { toApplicantPlatformCopy } from '../../components/worker/ApplicantHome';
import { getCvHistorySuggestions } from '../../lib/cvHistorySuggestions';
import InlineDocumentViewer from '../../components/shared/InlineDocumentViewer';
import { canViewDocument, resolveDocumentFetchUrl } from '../../lib/resolveDocumentFetchUrl';
import WorkerRTWShareCode from '../../components/worker/WorkerRTWShareCode';
import {
  WORKER_STUCK_ATTENDANCE_MESSAGE,
  canWorkerEditDailyNoteForItem,
  getWorkerRejectedAttendanceCardLabel,
  getWorkerRejectedAttendanceMessage,
  getWorkerShiftAttendanceStatusFromItem,
  getWorkerShiftListCategory,
  hasWorkerStuckOpenAttendance,
  isWorkerAssignmentActiveAccepted,
  isWorkerShiftPast,
  workerMayResubmitRejectedAttendance,
} from '../../components/worker/workerShiftUtils';
import {
  alertSupportsWorkerUpload,
  buildComplianceDueAlerts,
  getAlertUploadRequirementId,
} from '../../components/worker/workerAlertUtils';
import { getAgreementDisplay, getCvDisplay, getTrainingDisplay } from '../../components/worker/dashboardStatus';
import { deriveEmploymentGapsNeedAction } from '../../lib/employmentGapWorkerAction';
import { partitionEmploymentGaps } from '../../lib/employmentGapReview';
import { humanizeReferenceDifferenceText } from '../../lib/referencePresentation';
import {
  applicantCvNeedsAction,
  isHistoryOnlyReferenceDraft,
  referenceNeedsWorkerAction,
  resolveProfileChecksRepairTask,
  resolveProfileChecksTask,
} from '../../components/worker/referenceWorkerActions';
import { getCanonicalPersonStage, isActiveLifecycleStatus, normalizeLifecycleStatus } from '../../lib/lifecycle';
import { getLatestActiveAgreementById, getLatestActiveContract, resolveLatestContractState } from '../../lib/contractState';
import { CV_UPLOAD_GUIDANCE, validateCvUploadFile } from '../../lib/cvUploadReadiness';
import { resolveInterviewSurface, isInterviewStepConcluded, canProjectJoinInterviewAction } from '../../lib/interviewJourneyVisibility';
import API_BASE from '../../utils/apiBase';
import { updateWorkforceAppMetadata, workforceAppShortName } from '../../utils/workforceAppMetadata';
import WorkerInstallPrompt from '../../components/worker/WorkerInstallPrompt';
import WorkerStaffIdCard from '../../components/worker/WorkerStaffIdCard';
import BodyDiagramPicker from '../../components/body-map/BodyDiagramPicker';
import { regionDefaultView } from '../../components/body-map/bodyMapGeometry';

const API = API_BASE;
const TRAINING_UPLOAD_STORAGE_ERROR = "We couldn't save your certificate right now. No file was received. Keep the original and try again later.";
export function formatWorkerTrainingUploadError(error) {
  const detail = error?.response?.data?.detail;
  const message = typeof detail === 'string' ? detail : '';
  if (error?.response?.status === 503 ||
      /supabase|storage\/v1|name.?resolution|connectionerror|httpconnectionpool|max retries exceeded|internal server error/i.test(message)) {
    return TRAINING_UPLOAD_STORAGE_ERROR;
  }
  if (typeof message === 'string' && message.trim()) return message;
  return 'We could not confirm that your certificate was received. Please check with your administrator before trying again.';
}


export function getWorkerReferenceDifferenceText(message) {
  return humanizeReferenceDifferenceText(message);
}

export function getWorkerDashboardLoadingContent(locationState = {}) {
  if (locationState?.onboardingTransition === 'form_submitted') {
    return {
      eyebrow: 'Onboarding update',
      title: 'Saving your form',
      description: 'We are checking your file and preparing the next step.',
    };
  }

  return {
    eyebrow: 'Worker portal',
    title: 'Opening your workspace',
    description: 'We are getting your latest onboarding progress.',
  };
}

export async function loadWorkerDashboardBranding({
  apiBase = API,
  httpGet = axios.get,
  storage = window.localStorage,
  onBranding,
} = {}) {
  const slug = storage?.getItem?.('workerWorkspaceSlug');
  if (!slug) return null;
  try {
    const response = await httpGet(`${apiBase}/public/workforce/${encodeURIComponent(slug)}`);
    const branding = response.data || {};
    const canonicalSlug = branding.workforce_slug;
    if (canonicalSlug) {
      storage.setItem('workerWorkspaceSlug', canonicalSlug);
    }
    const tenantName = branding.tenant_name || branding.brand_name || '';
    const mapped = {
      organisation_name: tenantName,
      company_name: branding.brand_name || tenantName,
      primary_colour: branding.primary_colour,
      primary_brand_colour: branding.primary_colour,
      logo_url: branding.logo_url,
      support_email: branding.support_email,
      support_phone: branding.support_phone,
      workforce_slug: canonicalSlug || slug,
    };
    if (tenantName && onBranding) {
      onBranding(mapped);
    }
    return mapped;
  } catch (err) {
    console.warn('Could not fetch workforce branding:', err);
    return null;
  }
}

// CS-R2: canonical empty referee form. Frozen + module-level so it is a stable
// identity that never leaks mutable state between reference slots.
const BLANK_REFEREE_FORM = Object.freeze({
  name: '',
  email: '',
  phone: '',
  organisation: '',
  position: '',
  relationship: '',
  slot_reason_type: '',
  slot_reason_note: '',
  change_reason: '',
});

// Build a slot's form purely from ITS OWN persisted draft (never another slot's).
function draftToRefereeForm(slot) {
  const draft = (slot && slot.draft) || {};
  return {
    name: draft.name || '',
    email: draft.email || '',
    phone: draft.phone || '',
    organisation: draft.organisation || '',
    position: draft.position || '',
    relationship: draft.relationship || '',
    slot_reason_type: (slot && slot.draft_slot_reason_type) || '',
    slot_reason_note: (slot && slot.draft_slot_reason_note) || '',
    change_reason: (slot && slot.draft_change_reason) || '',
  };
}

const SLOT_REASON_OPTIONS = [
  { value: 'earlier_employment', label: 'Earlier employer (not most recent)' },
  { value: 'agency_placement', label: 'Agency placement' },
  { value: 'name_change', label: 'Employer name changed / different spelling' },
  { value: 'recent_employer_not_required', label: 'Recent employer reference not required' },
  { value: 'referee_work_email', label: 'Referee will reply from work email' },
  { value: 'employer_name_variation', label: 'Employer name variation' },
  { value: 'other', label: 'Other (explain below)' },
];

// Extract a human-readable message from an axios error, including FastAPI/Pydantic
// 422 responses where `detail` is an array of field-error objects. Without this,
// such errors fall through to a generic message (the original CS-R1 bug where
// applicants only ever saw "Failed to submit referee details").
function readableErrorDetail(err, fallback = 'Something went wrong. Please try again.') {
  const detail = err?.response?.data?.detail;
  if (typeof detail === 'string' && detail.trim()) return detail;
  if (Array.isArray(detail)) {
    const messages = detail
      .map((item) => (typeof item === 'string' ? item : item?.msg))
      .filter(Boolean);
    if (messages.length > 0) return messages.join('; ');
  }
  if (detail && typeof detail === 'object' && detail.message) return detail.message;
  if (detail && typeof detail === 'object' && detail.msg) return detail.msg;
  if (typeof err?.response?.data?.message === 'string') return err.response.data.message;
  return fallback;
}


const WIZARD_ROUTE_TO_STEP = {
  '#forms': 'profile_checks',
  '#checks': 'profile_checks',
  '#checks-card': 'profile_checks',
  '#employment-history': 'profile_checks',
  '#employment-integrity-section': 'profile_checks',
  '#references-section': 'profile_checks',
  '#employment-gaps-section': 'profile_checks',
  '#documents': 'identity_documents',
  '#documents-cv': 'profile_checks',
  '#training': 'mandatory_training',
  '#training-card': 'mandatory_training',
  '#induction': 'care_certificate',
  '#agreements-section': 'agreements',
  '#agreements-contract': 'agreements',
  '#agreements-handbook': 'agreements',
};

// ── Applicant "no dead-ends" journey model ────────────────────────────────
// The applicant dashboard must always answer: "what can I do now?" and never
// leave the applicant on a wait-for-admin screen. These pure helpers classify
// the canonical onboarding wizard steps into two clear buckets — things the
// applicant can act on now, and things we're checking in the background — plus
// a completed list. The interview is presented by its own card and is never
// treated as a blocking step here.
const APPLICANT_ACTIONABLE_STATUSES = new Set(['available', 'in_progress']);
const APPLICANT_CHECKING_STATUSES = new Set(['awaiting_review', 'paused']);
const APPLICANT_STEP_EXCLUDED_FROM_SECTIONS = new Set(['interview']);

// Route each step to the existing in-dashboard surface so a single tap takes
// the applicant straight to that task (handleNextAction switches the wizard
// step and scrolls to it).
export const APPLICANT_STEP_ROUTE = {
  profile_checks: '#checks',
  identity_documents: '#documents',
  mandatory_training: '#training',
  care_certificate: '#induction',
  agreements: '#agreements-section',
};

export function resolveApplicantJourneyStepAction(
  step,
  { applicationGateAction = null, profileChecksTask = null } = {},
) {
  if (step?.id === 'profile_checks') {
    const concreteAction = applicationGateAction || profileChecksTask;
    if (concreteAction?.route || concreteAction?.formId) {
      return concreteAction;
    }
  }

  return {
    route: APPLICANT_STEP_ROUTE[step?.id] || step?.route || `#${step?.id || ''}`,
  };
}

// Plain, phone-friendly one-liners. No recruitment jargon.
const APPLICANT_STEP_HINT = {
  profile_checks: 'Add your details, forms and reference contacts.',
  identity_documents: 'Add clear photos of your ID and right-to-work documents.',
  mandatory_training: 'Add any training certificates you already have.',
  care_certificate: 'Work through your Care Certificate, one part at a time.',
  agreements: 'Read and sign your contract and staff handbook.',
};

// One obvious primary action verb per task — never an ambiguous tiny link.
export function getApplicantStepActionLabel(step) {
  const id = step?.id;
  const status = String(step?.status || '').toLowerCase();
  const started = status === 'in_progress';
  switch (id) {
    case 'identity_documents':
      return started ? 'Continue' : 'Upload';
    case 'mandatory_training':
      return started ? 'Continue' : 'Upload';
    case 'profile_checks':
      return started ? 'Continue' : 'Open form';
    case 'care_certificate':
      return started ? 'Continue' : 'Add details';
    case 'agreements':
      return started ? 'Continue' : 'Complete';
    default:
      return started ? 'Continue' : 'Open';
  }
}

// Quiet, non-blocking status text for the "We're checking these" section. Must
// never tell the applicant they cannot continue.
export function getApplicantCheckingStatusLabel(step) {
  const status = String(step?.status || '').toLowerCase();
  if (status === 'awaiting_review') return 'With the team';
  if (status === 'paused') return 'Being checked';
  return 'Being checked';
}

export const APPLICANT_KEEP_GOING_MESSAGE = "You're done with this part. We're checking it in the background. You can continue with the rest of your application.";
export const APPLICANT_ALL_CHECKING_MESSAGE = "Everything on your side is done. We're checking your file in the background and will email you as things are confirmed — there's nothing you need to do right now.";
// Copy used to replace any backend "wait for admin" pause message inside the
// guided wizard panel so the applicant never sees a hard-stop instruction.
export const APPLICANT_CHECKING_PANEL_MESSAGE = "We're checking this in the background. You can keep going with your other tasks below.";

// Neutralise forbidden wait-state phrasing that may arrive in backend copy.
const APPLICANT_WAIT_COPY_PATTERNS = [
  /sit tight[^.]*\.?/gi,
  /come back later[^.]*\.?/gi,
  /wait until[^.]*\.?/gi,
  /we['’]ll let you know when you can continue[^.]*\.?/gi,
  /we will let you know when you can continue[^.]*\.?/gi,
  /we will (?:email|let you know) you when (?:you can continue|onboarding can continue)[^.]*\.?/gi,
];

export function sanitizeApplicantWaitCopy(text, fallback = '') {
  if (!text) return fallback;
  let out = String(text);
  APPLICANT_WAIT_COPY_PATTERNS.forEach((re) => { out = out.replace(re, ' '); });
  out = out.replace(/\s{2,}/g, ' ').trim();
  return out || fallback;
}

// Classify the wizard steps into the applicant's two-section model. Returns
// null for non-applicants (active employees) so the caller renders nothing new.
export function buildApplicantJourneyModel(wizard) {
  if (!wizard || !wizard.enabled) return null;
  const steps = Array.isArray(wizard.steps) ? wizard.steps : [];
  const forcedStepId = wizard.forced_step_id || null;
  const actionable = [];
  const checking = [];
  const completed = [];

  steps.forEach((step) => {
    if (!step || APPLICANT_STEP_EXCLUDED_FROM_SECTIONS.has(step.id)) return;
    const status = String(step.status || '').toLowerCase();
    const forced = Boolean(forcedStepId && step.id === forcedStepId);
    if (status === 'completed') {
      completed.push(step);
      return;
    }
    if (APPLICANT_ACTIONABLE_STATUSES.has(status) || forced) {
      actionable.push(step);
      return;
    }
    if (APPLICANT_CHECKING_STATUSES.has(status)) {
      checking.push(step);
    }
    // locked / unknown → intentionally omitted (shown later once unlocked)
  });

  return {
    actionable,
    checking,
    completed,
    hasActionable: actionable.length > 0,
    hasChecking: checking.length > 0,
    hasCompleted: completed.length > 0,
  };
}

const APPLICANT_STEP_ICON = {
  profile_checks: Shield,
  identity_documents: FileText,
  mandatory_training: GraduationCap,
  care_certificate: Award,
  agreements: PenTool,
  final_compliance_review: ShieldCheck,
};

// Presentational two-section applicant journey. Pure props → easy to test and
// safe to render as the wizard's always-visible nextActionSlot so it can never
// be dropped when a step is paused (that pause-gate is the old dead-end).
export function ApplicantJourneyOverview({ model, onAction, primaryTaskOverride = null, inlineWorkHistoryVisible = false, providerName = '' }) {
  if (!model) return null;
  const { actionable, checking, completed, hasActionable, hasChecking, hasCompleted } = model;
  if (!hasActionable && !hasChecking && !hasCompleted) return null;

  const primaryAction = actionable[0] || null;
  const otherActions = actionable.slice(1);
  const historyIsAlreadyOnThisScreen = Boolean(inlineWorkHistoryVisible && primaryAction?.id === 'profile_checks');

  const renderActionCard = (step, { secondary = false } = {}) => {
    const Icon = APPLICANT_STEP_ICON[step.id] || Circle;
    // Keep the primary applicant CTA aligned with the current real profile
    // task (work history, missing form, referee, CV), not the generic wizard
    // title. No route or approval state is changed by this presentation.
    const focus = !secondary && step.id === 'profile_checks'
      && primaryTaskOverride?.focus && primaryTaskOverride?.route
      ? primaryTaskOverride : null;
    return (
      <div
        key={step.id}
        data-testid={`applicant-action-${step.id}`}
        className={secondary
          ? 'flex items-center justify-between gap-3 rounded-xl border border-slate-200 bg-white px-3 py-3'
          : 'rounded-2xl border border-[#BFD9D9] bg-gradient-to-br from-white to-[#F4FBFA] p-4 shadow-sm'}
      >
        <div className={secondary ? 'flex min-w-0 items-center gap-3' : 'flex min-w-0 items-start gap-3'}>
          <span className={secondary
            ? 'flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[#0F5C5E]/10 text-[#0F5C5E]'
            : 'flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-[#0F5C5E]/10 text-[#0F5C5E]'
          }>
            <Icon className={secondary ? 'h-4 w-4' : 'h-5 w-5'} />
          </span>
          <div className="min-w-0">
            <p className={secondary ? 'truncate text-sm font-semibold text-slate-900' : 'font-semibold text-slate-900'}>
              {focus?.title || step.title}
            </p>
            {!secondary ? (
              <p className="mt-0.5 text-sm text-slate-600">
                {focus?.description || APPLICANT_STEP_HINT[step.id] || step.subtitle || 'Open this to continue.'}
              </p>
            ) : null}
          </div>
        </div>
        <Button
          size={secondary ? 'sm' : 'lg'}
          variant={secondary ? 'outline' : 'default'}
          className={secondary
            ? 'shrink-0 rounded-xl'
            : 'mt-4 w-full bg-[#0F5C5E] text-base hover:bg-[#0b4a4c]'
          }
          data-testid={`applicant-action-btn-${step.id}`}
          onClick={() => onAction?.(step)}
        >
          {focus?.primaryLabel || getApplicantStepActionLabel(step)}
        </Button>
      </div>
    );
  };

  return (
    <div className="space-y-4" data-testid="applicant-journey-overview">
      {primaryAction && !historyIsAlreadyOnThisScreen ? (
        <section data-testid="applicant-things-to-do">
          <div className="mb-2">
            <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-primary/70">Your next step</p>
          </div>
          {renderActionCard(primaryAction)}
          {otherActions.length > 0 ? (
            <details className="mt-3 rounded-xl border border-slate-200 bg-white" data-testid="applicant-other-actions">
              <summary className="cursor-pointer list-none px-4 py-3 text-sm font-medium text-slate-700">
                {/* Counts onboarding sections (steps), not individual tasks, so it never
                    contradicts Home's per-task count. */}
                {otherActions.length} other section{otherActions.length === 1 ? '' : 's'} to work on
              </summary>
              <div className="space-y-2 border-t border-slate-100 p-3">
                {otherActions.map((step) => renderActionCard(step, { secondary: true }))}
              </div>
            </details>
          ) : null}
        </section>
      ) : null}

      {historyIsAlreadyOnThisScreen && otherActions.length > 0 ? (
        <section className="space-y-2" data-testid="applicant-other-actions-inline">
          <p className="text-xs font-semibold text-slate-600">Other available tasks</p>
          {otherActions.map(step => renderActionCard(step, { secondary: true }))}
        </section>
      ) : null}

      {hasChecking ? (
        <details className="rounded-xl border border-blue-100 bg-blue-50/40" data-testid="applicant-checking">
          <summary className="cursor-pointer list-none px-4 py-3">
            <div className="flex items-center justify-between gap-3">
              <div className="flex min-w-0 items-center gap-2">
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-blue-100 text-blue-600">
                  <Clock className="h-4 w-4" />
                </span>
                <div className="min-w-0">
                  <p className="font-semibold text-slate-800">Waiting on {String(providerName || '').trim() || 'your care provider'}</p>
                  <p className="text-xs text-slate-600">
                    {checking.length} item{checking.length === 1 ? '' : 's'} being checked
                  </p>
                </div>
              </div>
              <Badge className="shrink-0 bg-blue-100 text-blue-700">No action</Badge>
            </div>
          </summary>
          <div className="space-y-2 border-t border-blue-100 px-3 pb-3 pt-3">
            <p
              className="rounded-lg bg-white/70 px-3 py-2 text-xs font-medium text-blue-900"
              data-testid="applicant-keep-going"
            >
              {hasActionable ? APPLICANT_KEEP_GOING_MESSAGE : APPLICANT_ALL_CHECKING_MESSAGE}
            </p>
            {checking.map((step) => {
              const Icon = APPLICANT_STEP_ICON[step.id] || Clock;
              return (
                <div
                  key={step.id}
                  data-testid={`applicant-checking-${step.id}`}
                  className="flex items-center justify-between gap-3 rounded-lg bg-white/70 p-3"
                >
                  <div className="flex min-w-0 items-center gap-3">
                    <Icon className="h-4 w-4 shrink-0 text-blue-600" />
                    <span className="truncate text-sm font-medium text-slate-700">{step.title}</span>
                  </div>
                  <Badge className="shrink-0 bg-blue-100 text-blue-700">{getApplicantCheckingStatusLabel(step)}</Badge>
                </div>
              );
            })}
          </div>
        </details>
      ) : null}

      {hasCompleted ? (
        <details className="rounded-xl border border-green-100 bg-green-50/50" data-testid="applicant-completed">
          <summary className="cursor-pointer list-none px-4 py-3">
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <CheckCircle className="h-5 w-5 text-green-600" />
                <span className="font-semibold text-slate-800">Completed</span>
              </div>
              <Badge className="bg-green-100 text-green-700">
                {completed.length} done
              </Badge>
            </div>
          </summary>
          <div className="space-y-2 border-t border-green-100 px-3 pb-3 pt-3">
            {completed.map((step) => (
              <div
                key={step.id}
                data-testid={`applicant-completed-${step.id}`}
                className="flex items-center gap-3 rounded-lg bg-white/70 p-3"
              >
                <CheckCircle className="h-4 w-4 shrink-0 text-green-600" />
                <span className="truncate text-sm font-medium text-slate-700">{step.title}</span>
                <Badge className="ml-auto shrink-0 bg-green-100 text-green-700">Done</Badge>
              </div>
            ))}
          </div>
        </details>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Next-step consistency (Applicant Home ↔ My tasks ↔ destination screen).
// Pure helpers: they never decide compliance, only which existing task is
// shown and where a tap lands.
// ---------------------------------------------------------------------------
const isPoaDocType = (value) => String(value || '').toLowerCase().startsWith('proof_of_address');

function identityDocRequirementOf(action) {
  if (!action || String(action.wizard_step_id || '') !== 'identity_documents') return '';
  const requirement = String(action.requirement_id || '').trim().toLowerCase();
  if (requirement) return requirement;
  const key = String(action.key || '').toLowerCase();
  const match = key.match(/^doc_(?:upload|reupload|rejected)_(.+)$/);
  return match ? match[1] : '';
}

// The document the identity step should show first: the one the applicant
// tapped (?document=), otherwise the server's forced document action.
export function resolvePreferredIdentityDocType({ documentParam = '', forcedAction = null } = {}) {
  const fromUrl = String(documentParam || '').trim().toLowerCase();
  if (fromUrl) return fromUrl;
  if (forcedAction && forcedAction.classification === 'worker_action' && forcedAction.actor !== 'admin') {
    return identityDocRequirementOf(forcedAction);
  }
  return '';
}

// Rejected documents always come first (they need replacing). Otherwise show
// the preferred document when it is genuinely still missing, else the
// original order. Never invents or hides a requirement.
export function selectGuidedIdentityDocuments({
  rejectedDocuments = [],
  nonPoaMissingSlots = [],
  poaMissingSlots = [],
  preferredType = '',
} = {}) {
  if (rejectedDocuments.length > 0) return rejectedDocuments;
  const preferred = String(preferredType || '').toLowerCase();
  if (preferred) {
    if (isPoaDocType(preferred) && poaMissingSlots.length > 0) return poaMissingSlots;
    const match = nonPoaMissingSlots.find((doc) => (
      String(doc?.type || '').toLowerCase() === preferred
      || String(doc?.requirement_id || '').toLowerCase() === preferred
    ));
    if (match) return [match];
  }
  return nonPoaMissingSlots.length > 0 ? [nonPoaMissingSlots[0]] : poaMissingSlots;
}

// Where a Home task tap lands. Document tasks open the identity step focused
// on THAT document; other tasks keep their existing route behaviour.
export function buildApplicantHomeTaskTarget(action) {
  if (!action) return { route: '/worker/dashboard?step=profile_checks' };
  if (action.route || action.form_id || action.formId) {
    return { route: action.route || '', formId: action.form_id || action.formId };
  }
  const step = action.wizard_step_id || 'profile_checks';
  const doc = identityDocRequirementOf(action);
  const params = new URLSearchParams({ step });
  if (doc) params.set('document', doc);
  return { route: `/worker/dashboard?${params.toString()}` };
}

// Home's "Your next step" uses the same source of truth as My tasks: the
// application gate when the server says no application is on file (and the
// applicant can act on it), otherwise the server's forced worker action.
export function resolveApplicantHomePrimaryAction({ applicationGateAction = null, forcedAction = null } = {}) {
  if (applicationGateAction && applicationGateAction.owner !== 'admin'
    && (applicationGateAction.route || applicationGateAction.formId)) {
    return {
      key: applicationGateAction.key || 'application_form',
      title: applicationGateAction.title,
      description: applicationGateAction.description,
      primary_label: applicationGateAction.primaryLabel || '',
      route: applicationGateAction.route || '',
      form_id: applicationGateAction.formId || undefined,
      classification: 'worker_action',
      actor: 'worker',
    };
  }
  if (forcedAction && forcedAction.classification === 'worker_action' && forcedAction.actor !== 'admin') {
    return forcedAction;
  }
  return null;
}


// Keep a secondary document requirement visible without presenting it as the
// single next action when an unfinished application is the primary task.
export function getApplicantForcedActionBannerTitle(primaryAction, forcedAction) {
  return forcedAction && primaryAction?.key === 'application_form'
    && String(primaryAction.key) !== String(forcedAction.key)
    ? 'Also needed for onboarding'
    : 'Action required before you continue';
}

const IDENTITY_CAPTURE_DOC_TYPES = new Set(['identity', 'right_to_work', 'proof_of_address']);

// CS-R2: the true per-slot identity for a missing document card. Prefer a stable
// backend id; fall back to the human label/name so that two cards sharing the same
// doc.type (e.g. the two Proof-of-Address slots) never cross-lock each other.
const docSlotKey = (doc) => (
  (doc && (doc.requirement_id || doc.id || doc.slot_id || doc.requirement_key || doc.name)) || (doc && doc.type) || ''
);

const CV_REUPLOAD_STATUSES = new Set([
  'rejected',
  'replacement_requested',
  'replacement_required',
  'reupload_required',
]);

function workerMayUploadCv(cvStatus) {
  if (!cvStatus) return false;
  const status = String(cvStatus.cv_status || '').toLowerCase();
  if (cvStatus.has_cv) {
    return Boolean(cvStatus.replacement_required || CV_REUPLOAD_STATUSES.has(status));
  }
  return Boolean(cvStatus.can_upload_cv) && status !== 'under_review' && status !== 'uploaded';
}

function isCvActionKey(key) {
  const normalized = String(key || '').toLowerCase();
  return normalized.startsWith('cv_')
    || normalized === 'cv'
    || normalized.includes('email_request_cv');
}

function normalizeActionKey(key) {
  const normalized = String(key || '').toLowerCase();
  return isCvActionKey(normalized) ? 'cv' : normalized;
}

function getUnresolvedCvNotifications(notifications) {
  return (notifications || []).filter(
    (entry) => (entry.type === 'cv_rejected' || entry.type === 'cv_replacement_requested') && !entry.resolved,
  );
}

const REFERENCE_MISMATCH_NOTIFICATION_TYPES = new Set([
  'reference_mismatch_needs_clarification',
  'reference_mismatch_rejected',
]);

const REFERENCE_REJECTION_NOTIFICATION_TYPES = new Set([
  'reference_rejected',
]);

const EMPLOYMENT_GAP_NOTIFICATION_TYPES = new Set([
  'employment_gap_rejected',
  'gap_info_requested',
]);

function getUnresolvedReferenceMismatchNotifications(notifications) {
  return (notifications || []).filter(
    (entry) => REFERENCE_MISMATCH_NOTIFICATION_TYPES.has(entry.type) && !entry.resolved,
  );
}

function isReferenceMismatchNotificationType(type) {
  return REFERENCE_MISMATCH_NOTIFICATION_TYPES.has(type);
}

function isReferenceRejectionNotificationType(type) {
  return REFERENCE_REJECTION_NOTIFICATION_TYPES.has(type);
}

function isEmploymentGapNotificationType(type) {
  return EMPLOYMENT_GAP_NOTIFICATION_TYPES.has(type);
}

function isEmploymentHistoryAction(action) {
  const text = [
    action?.key,
    action?.type,
    action?.route,
    action?.title,
    action?.label,
    action?.primary_label,
  ].filter(Boolean).join(' ').toLowerCase();
  return text.includes('employment_history') || text.includes('employment_integrity') || text.includes('work history');
}

function navigateFromNotification(notif, { navigate, handleNextAction }) {
  const actionUrl = notif.data?.action_url;
  if (actionUrl) {
    navigate(actionUrl);
    return;
  }
  if (isReferenceMismatchNotificationType(notif.type) || isReferenceRejectionNotificationType(notif.type)) {
    handleNextAction({ route: '#references-section' });
    return;
  }
  if (isEmploymentGapNotificationType(notif.type)) {
    handleNextAction({ route: '#employment-gaps-section' });
  }
}

function actionDuplicatesPrimarySurface(actionKey, displayedAction, forcedAction) {
  const normalized = normalizeActionKey(actionKey);
  if (displayedAction?.key && normalizeActionKey(displayedAction.key) === normalized) return true;
  if (forcedAction?.key && normalizeActionKey(forcedAction.key) === normalized) return true;
  return false;
}

// Canonical training status display config — single vocabulary across admin and worker
const TRAINING_STATUS_CONFIG = {
  verified:        { badge: 'Verified',              badgeCls: 'bg-green-100 text-green-700',   cardBg: 'bg-green-50 border-green-200',   Icon: ShieldCheck,   iconCls: 'text-green-600',  iconBg: 'bg-green-100',  showUpload: false },
  completed:       { badge: 'Awaiting Verification', badgeCls: 'bg-blue-100 text-blue-700',     cardBg: 'bg-blue-50 border-blue-200',     Icon: CheckCircle,   iconCls: 'text-blue-600',   iconBg: 'bg-blue-100',   showUpload: false },
  awaiting_review: { badge: 'With the team', badgeCls: 'bg-purple-100 text-purple-700', cardBg: 'bg-purple-50 border-purple-200', Icon: Clock,         iconCls: 'text-purple-600', iconBg: 'bg-purple-100', showUpload: false },
  needs_information:{ badge: 'Needs a bit more info', badgeCls: 'bg-orange-100 text-orange-700', cardBg: 'bg-orange-50 border-orange-200', Icon: AlertCircle, iconCls: 'text-orange-600', iconBg: 'bg-orange-100', showUpload: true, uploadLabel: 'Add info' },
  processing:      { badge: 'Uploaded — processing', badgeCls: 'bg-blue-100 text-blue-700', cardBg: 'bg-blue-50 border-blue-200', Icon: Clock, iconCls: 'text-blue-600', iconBg: 'bg-blue-100', showUpload: false },
  due_soon:        { badge: 'Expiring Soon',         badgeCls: 'bg-amber-100 text-amber-700',   cardBg: 'bg-amber-50 border-amber-200',   Icon: Clock,         iconCls: 'text-amber-600',  iconBg: 'bg-amber-100',  showUpload: true, uploadLabel: 'Renew' },
  expired:         { badge: 'Expired',               badgeCls: 'bg-red-100 text-red-700',       cardBg: 'bg-red-50 border-red-200',       Icon: AlertTriangle, iconCls: 'text-red-600',    iconBg: 'bg-red-100',    showUpload: true, uploadLabel: 'Re-upload' },
  rejected:        { badge: 'Rejected',              badgeCls: 'bg-red-100 text-red-700',       cardBg: 'bg-red-50 border-red-200',       Icon: AlertCircle,   iconCls: 'text-red-600',    iconBg: 'bg-red-100',    showUpload: true, uploadLabel: 'Re-upload' },
  missing:         { badge: 'Required',              badgeCls: 'bg-slate-100 text-slate-600',   cardBg: 'bg-slate-50 border-slate-200',   Icon: Circle,        iconCls: 'text-slate-400',  iconBg: 'bg-slate-100',  showUpload: true, uploadLabel: 'Upload' },
};

// Format date helper — guards against `new Date('bad')` which yields NaN and
// renders literally as "Invalid Date" if passed to toLocaleDateString().
const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return '-';
  return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
};

const formatDateTime = (dateStr) => {
  if (!dateStr) return '-';
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return '-';
  return date.toLocaleString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const getWorkerIncidentStatusMeta = (status) => {
  switch ((status || 'open').toLowerCase()) {
    case 'closed':
      return { className: 'bg-green-100 text-green-700', label: 'Closed' };
    case 'resolved':
      return { className: 'bg-[#D8E6D5] text-[#1F8F5F]', label: 'Resolved' };
    case 'under_review':
    case 'investigating':
      return { className: 'bg-amber-100 text-amber-700', label: 'Under review' };
    default:
      return { className: 'bg-blue-100 text-blue-700', label: 'Submitted' };
  }
};

const getDueState = (dateStr, soonWindowDays = 7) => {
  if (!dateStr) return null;
  const target = new Date(dateStr);
  if (isNaN(target.getTime())) return null;
  const now = new Date();
  const targetStart = new Date(target.getFullYear(), target.getMonth(), target.getDate());
  const nowStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const deltaDays = Math.ceil((targetStart - nowStart) / (1000 * 60 * 60 * 24));
  if (deltaDays < 0) return 'overdue';
  if (deltaDays <= soonWindowDays) return 'due_soon';
  return 'upcoming';
};

// Operational document guidance text
const getDocumentGuidance = (docType, providerName = 'your care provider') => {
  const guidance = {
    right_to_work: "Upload your UK passport, biometric residence permit, or share code screenshot from GOV.UK. Share code must be valid for at least 30 days.",
    dbs: "Upload your Enhanced DBS certificate (issued within last 3 years). If subscribed to DBS Update Service, please confirm.",
    identity: `Passport photo page, or UK driving licence (front and back together). Upload a clear scan or photo now. ${providerName} will confirm the original before final approval. Separate from Right to Work.`,
    proof_of_address: 'First address document — dated within the last 12 months, showing your full name and current address (utility bill, bank statement, council tax, etc.).',
    proof_of_address_2: `Second address document - must be a different type from the first (e.g. bank statement if the first was a utility bill). ${providerName} will confirm originals before final approval.`,
    training: "Upload PDF or photo of your training certificate. AI will automatically extract the training name, completion date, and expiry date.",
    nmc_registration: "Upload your NMC PIN card or registration letter. Your registration will be verified online.",
    professional_indemnity: "Upload your professional indemnity insurance certificate. Must be valid for current year."
  };
  return guidance[docType] || "Upload a clear copy of the required document.";
};

// Accepted file formats text
const ACCEPTED_FORMATS = "PDF, JPG or PNG, up to 10MB.";
const DAILY_NOTE_TAG_OPTIONS = ['nutrition', 'mood', 'incident', 'medication'];

const DOCUMENT_WORKFLOW_UI = {
  missing: { label: 'Missing', className: 'bg-red-100 text-red-700' },
  awaiting_review: { label: 'With the team', className: 'bg-amber-100 text-amber-700' },
  reupload_required: { label: 'Re-upload Required', className: 'bg-red-100 text-red-700' },
  check_required: { label: 'Check Required', className: 'bg-orange-100 text-orange-700' },
  check_in_progress: { label: 'Check In Progress', className: 'bg-blue-100 text-blue-700' },
  proof_required: { label: 'Proof Required', className: 'bg-purple-100 text-purple-700' },
  verified: { label: 'Verified', className: 'bg-green-100 text-green-700' },
};

const RECURRING_STATUS_UI = {
  overdue: {
    label: 'Overdue',
    badge: 'bg-red-100 text-red-700'
  },
  due: {
    label: 'Due now',
    badge: 'bg-amber-100 text-amber-700'
  },
  upcoming: {
    label: 'Upcoming',
    badge: 'bg-blue-100 text-blue-700'
  },
  scheduled: {
    label: 'Scheduled',
    badge: 'bg-slate-100 text-slate-700'
  },
};

const RECURRING_TYPE_LABELS = {
  supervision: 'Supervision',
  competency_assessment: 'Competency assessment',
  spot_check: 'Spot check',
  training_refresh: 'Training refresh',
  report_followup: 'Report follow-up',
};

const isCvDocumentRequirement = (doc) => {
  const type = String(doc?.type || doc?.requirement_id || doc?.id || '').trim().toLowerCase();
  const name = String(doc?.name || doc?.label || '').trim().toLowerCase();
  return ['cv', 'resume', 'curriculum_vitae'].includes(type) || /\b(cv|resume|curriculum vitae)\b/.test(name);
};

const getDocumentWorkflowBadgeMeta = (doc) => {
  const status = (doc?.status || '').toLowerCase();
  if (doc?.verified) return DOCUMENT_WORKFLOW_UI.verified;
  return DOCUMENT_WORKFLOW_UI[status] || DOCUMENT_WORKFLOW_UI.awaiting_review;
};

const getCompletedDocumentDisplayStatus = (doc) => {
  const status = String(doc?.status || '').trim().toLowerCase();
  if (status) return status;
  return doc?.verified ? 'verified' : 'awaiting_review';
};

const getCompletedTrainingBadgeMeta = (training) => {
  const status = String(training?.status || '').trim().toLowerCase();
  if (status && TRAINING_STATUS_CONFIG[status]) return TRAINING_STATUS_CONFIG[status];
  return training?.verified ? TRAINING_STATUS_CONFIG.verified : TRAINING_STATUS_CONFIG.awaiting_review;
};

const isReferenceComplete = (status) => status === 'verified';

const getApplicantReferenceStatusLabel = (ref = {}) => {
  const status = String(ref.status || '').toLowerCase();
  if (status === 'verified') return 'Reference received';
  if (status === 'sent') return 'Request sent';
  if (status === 'response_received' || status.includes('pending_admin_review')) return 'Being checked';
  if (status === 'rejected' || status === 'needs_new_input') return 'Details needed';
  if (status === 'declared') return 'Ready to send';
  return 'Details needed';
};

const getReferencesProgressSummary = (refs) => {
  const list = refs || [];
  const verified = list.filter((r) => isReferenceComplete(r?.status)).length;
  if (verified >= 2) return { label: '2/2 verified', tone: 'success' };
  const responded = list.filter((r) => {
    const s = String(r?.status || '').toLowerCase();
    return s.includes('response_received') || s === 'verified';
  }).length;
  const sent = list.filter((r) => {
    const s = String(r?.status || '').toLowerCase();
    return s === 'sent' || s.includes('response_received') || s.includes('pending_admin');
  }).length;
  if (sent > 0) return { label: `Sent - ${responded}/2 received`, tone: 'info' };
  const declared = list.filter((r) => String(r?.status || '').toLowerCase() === 'declared').length;
  if (declared > 0) return { label: `${declared}/2 ready to send`, tone: 'info' };
  return { label: `${verified}/2 verified`, tone: verified > 0 ? 'info' : 'warning' };
};

const BLOCKER_KEYS_REFERENCES = new Set(['reference_1', 'reference_2', 'references']);

const normalizeBlockerValue = (value) => String(value || '').trim().toLowerCase();

const blankEmploymentEntry = () => ({
  employer_name: '',
  job_title: '',
  start_date: '',
  end_date: '',
  is_current: false,
  duties: '',
  reason_for_leaving: '',
  employer_address: '',
  employer_phone: '',
  can_contact: true,
});

function EmploymentIntegrityCard({ integrity, onUpdated = null, embedded = false }) {
  const [notes, setNotes] = useState({});
  const [submitting, setSubmitting] = useState(null);
  const [confirmationChecked, setConfirmationChecked] = useState(false);

  if (!integrity) return null;
  const state = String(integrity.state || '').toLowerCase();
  const prerequisites = integrity.prerequisites_missing || [];
  if (state === 'not_ready' && prerequisites.some((key) => ['application', 'declarations', 'cv'].includes(key))) {
    return null;
  }

  const unresolved = integrity.unresolved_material_discrepancies || [];
  const gapSummary = integrity.gap_summary || {};

  const recordLabel = (record) => {
    if (!record || typeof record !== 'object') return 'Not listed';
    const employer = record.employer_name || record.employer || record.company || record.organisation || 'Employer not listed';
    const role = record.job_title || record.role || record.position || '';
    const start = record.start_date || record.from_date || '';
    const end = record.end_date || record.to_date || (record.is_current ? 'Present' : '');
    return [employer, role, [start, end].filter(Boolean).join(' – ')].filter(Boolean).join(' · ');
  };

  const postResolution = async (item, action) => {
    const explanation = String(notes[item.id] || '').trim();
    if (action !== 'cv_needs_updating' && explanation.length < 3) {
      toast.error('Please add a short explanation first.');
      return;
    }
    setSubmitting(item.id);
    try {
      const token = localStorage.getItem('workerToken');
      await axios.post(
        `${API}/worker/employment-integrity/discrepancies/${encodeURIComponent(item.id)}/resolve`,
        { action, explanation },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (action === 'cv_needs_updating') {
        toast.success('CV update selected. Upload your updated CV below.');
      } else {
        toast.success('Difference recorded. CareOps is rechecking your history.');
      }
      if (typeof onUpdated === 'function') await onUpdated();
    } catch (err) {
      toast.error(readableErrorDetail(err, 'Could not save this employment-history response.'));
    } finally {
      setSubmitting(null);
    }
  };

  const confirmHistory = async () => {
    if (!confirmationChecked) {
      toast.error('Please tick the confirmation first.');
      return;
    }
    setSubmitting('confirm');
    try {
      const token = localStorage.getItem('workerToken');
      const response = await axios.post(
        `${API}/worker/employment-integrity/confirm`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
      // Do not show a successful confirmation for a stale/rechecking record.
      // It can otherwise ask the applicant to confirm again on every login.
      if (!isEmploymentConfirmationReceiptCurrent(response?.data?.employment_integrity)) {
        throw new Error('Your confirmation was not saved as current. Refresh your application or contact recruitment.');
      }
      toast.success('Work history confirmed. Your recruitment team will review it.');
      setConfirmationChecked(false);
      if (typeof onUpdated === 'function') await onUpdated();
    } catch (err) {
      toast.error(readableErrorDetail(err, 'Your employment history is not ready to confirm yet.'));
    } finally {
      setSubmitting(null);
    }
  };

  const runCheck = async () => {
    setSubmitting('evaluate');
    try {
      const token = localStorage.getItem('workerToken');
      await axios.post(
        `${API}/worker/employment-integrity/evaluate`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (typeof onUpdated === 'function') await onUpdated();
    } catch (err) {
      toast.error(readableErrorDetail(err, 'CareOps could not complete the check. Please try again.'));
    } finally {
      setSubmitting(null);
    }
  };

  const tone =
    state === 'approved' ? 'success'
      : state === 'awaiting_admin_review' ? 'success'
      : ['action_required', 'error'].includes(state) ? 'warning'
      : 'info';
  const title =
    state === 'checking' ? 'Checking your employment history…'
      : state === 'error' ? 'We couldn’t finish checking your employment history'
      : state === 'action_required' ? 'Check your work history'
      : state === 'confirmation_required' ? 'Review your employment history'
      : state === 'awaiting_admin_review' ? 'Employment history sent for review'
      : state === 'approved' ? 'Employment history approved'
      : 'Complete your official employment history';

  const Container = embedded ? 'section' : OnboardingPremiumCard;
  return (
    <div id="employment-integrity-section" data-testid="employment-integrity-section">
      <Container
        {...(embedded
          ? { className: 'mt-4 border-t border-slate-200 pt-4 space-y-4', 'data-testid': 'work-history-final-step' }
          : {
              tone,
              title,
              description: integrity.reason || 'Review your work history and make any changes before Osabea checks it.',
              icon: state === 'checking' ? Loader2
                : state === 'approved' || state === 'awaiting_admin_review' ? CheckCircle : Briefcase,
              action: state === 'checking'
                ? <Badge className="bg-blue-100 text-blue-700"><Loader2 className="h-3 w-3 mr-1 animate-spin" />Checking</Badge>
                : state === 'awaiting_admin_review'
                  ? <Badge className="bg-blue-100 text-blue-700"><Clock className="h-3 w-3 mr-1" />With Osabea</Badge>
                  : state === 'approved'
                    ? <Badge className="bg-green-100 text-green-700"><CheckCircle className="h-3 w-3 mr-1" />Approved</Badge>
                    : null,
              contentClassName: 'space-y-4',
            })}
      >
        {embedded && <h3 className="text-sm font-semibold text-slate-900">
          {state === 'confirmation_required' ? 'Confirm your work history'
            : state === 'approved' ? 'Approved by your team' : 'Final review'}
        </h3>}
        {state === 'checking' && (
          <div className="rounded-xl border border-blue-200 bg-blue-50 p-4">
            <p className="font-medium text-blue-950">Please wait while CareOps runs this check.</p>
            <p className="mt-1 text-sm text-blue-800">
              This employment-history step cannot be completed until the latest CV and official history have been checked.
              You can still work on other independent onboarding tasks.
            </p>
            <Button type="button" variant="outline" size="sm" className="mt-3" onClick={runCheck} disabled={submitting === 'evaluate'}>
              {submitting === 'evaluate' ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />}
              Check now
            </Button>
          </div>
        )}

        {state === 'error' && (
          <div className="rounded-xl border border-amber-200 bg-amber-50 p-4">
            <p className="font-medium text-amber-950">Your information is saved.</p>
            <p className="mt-1 text-sm text-amber-900">
              CareOps could not finish the latest employment-history check. Nothing has been marked complete, and you can retry safely.
            </p>
            <Button type="button" variant="outline" size="sm" className="mt-3" onClick={runCheck} disabled={submitting === 'evaluate'}>
              {submitting === 'evaluate' ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />}
              Retry check
            </Button>
          </div>
        )}

        {state === 'action_required' && (
          <>
            {Number(gapSummary.worker_action || 0) > 0 && (
              <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
                {gapSummary.worker_action} employment gap{Number(gapSummary.worker_action) === 1 ? '' : 's'} need your explanation or update.
                Use the gap section below while you are already signed in.
              </div>
            )}

            {unresolved.map((item) => (
              <div key={item.id} className="rounded-xl border border-slate-200 bg-white p-4 space-y-3">
                <div>
                  <p className="text-sm font-semibold text-slate-900">{item.description || 'CV and official history differ'}</p>
                  <div className="mt-2 grid gap-2 md:grid-cols-2">
                    <div className="rounded-lg bg-slate-50 p-3">
                      <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Official history</p>
                      <p className="mt-1 text-sm text-slate-800">{recordLabel(item.structured_data)}</p>
                    </div>
                    <div className="rounded-lg bg-slate-50 p-3">
                      <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Your CV</p>
                      <p className="mt-1 text-sm text-slate-800">{recordLabel(item.cv_data)}</p>
                    </div>
                  </div>
                </div>

                <Textarea
                  value={notes[item.id] || ''}
                  onChange={(event) => setNotes((current) => ({ ...current, [item.id]: event.target.value }))}
                  placeholder="Briefly explain which information is correct and why"
                  className="min-h-[88px]"
                />
                <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap">
                  <Button type="button" size="sm" onClick={() => postResolution(item, 'official_history_correct')} disabled={submitting === item.id}>
                    {submitting === item.id && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    Official history is correct
                  </Button>
                  <Button type="button" size="sm" variant="outline" onClick={() => postResolution(item, 'difference_explained')} disabled={submitting === item.id}>
                    Difference is correct — explain why
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => document.getElementById('employment-history')?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
                  >
                    Correct my employment history
                  </Button>
                  <Button type="button" size="sm" variant="outline" onClick={() => postResolution(item, 'cv_needs_updating')} disabled={submitting === item.id}>
                    My CV needs updating
                  </Button>
                </div>
              </div>
            ))}

            {integrity.invalid_entry_count > 0 && (
              <p className="text-sm text-amber-800">
                {integrity.invalid_entry_count} work-history row{integrity.invalid_entry_count === 1 ? '' : 's'} need valid dates. Edit the official history below.
              </p>
            )}
          </>
        )}

        {state === 'confirmation_required' && (
          <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4">
            <p className="font-semibold text-emerald-950">Final step — confirm your work history</p>
            <p className="mt-1 text-sm text-emerald-900">
              Check the jobs and dates above, then confirm once. Osabea will review your declaration.
            </p>
            <label className="mt-4 flex items-start gap-3 text-sm text-slate-800">
              <Checkbox checked={confirmationChecked} onCheckedChange={(value) => setConfirmationChecked(Boolean(value))} />
              <span>I confirm this employment history is complete and accurate.</span>
            </label>
            <Button className="mt-4" onClick={confirmHistory} disabled={!confirmationChecked || submitting === 'confirm'}>
              {submitting === 'confirm' && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Confirm employment history
            </Button>
          </div>
        )}

        {state === 'awaiting_admin_review' && (
          <p className="text-sm text-slate-700">
            You have finished this step. Osabea is reviewing your employment history now.
          </p>
        )}
      </Container>
    </div>
  );
}

// Employment Gaps Clarification Section
export function EmploymentGapsSection({ providerName = 'your care provider', onUpdated = null, forceWorkHistoryRepair = false, integrity = null }) {
  const [gaps, setGaps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [gapLoadError, setGapLoadError] = useState(false);
  const [hasGaps, setHasGaps] = useState(false);
  const [reasonTypes, setReasonTypes] = useState([]);
  const [editingGap, setEditingGap] = useState(null);
  const [explanation, setExplanation] = useState('');
  const [reasonType, setReasonType] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [coverage, setCoverage] = useState(null);
  const [employmentEntries, setEmploymentEntries] = useState([]);
  const [invalidEntries, setInvalidEntries] = useState([]);
  const [unmatchedNotes, setUnmatchedNotes] = useState([]);
  const [employmentSignedOff, setEmploymentSignedOff] = useState(false);
  const [signedOffByName, setSignedOffByName] = useState(null);
  const [signedOffAt, setSignedOffAt] = useState(null);
  const [employmentArchiveView, setEmploymentArchiveView] = useState(false);
  const [employmentDialogOpen, setEmploymentDialogOpen] = useState(false);
  const [employmentDraft, setEmploymentDraft] = useState(blankEmploymentEntry());
  const [editingEmploymentEntryId, setEditingEmploymentEntryId] = useState(null);
  const [savingEmploymentEntry, setSavingEmploymentEntry] = useState(false);
  const [cvSuggestionStatus, setCvSuggestionStatus] = useState(null);
  const [correctionReason, setCorrectionReason] = useState('');
  const [correctionPending, setCorrectionPending] = useState(false);
  const [pendingCorrectionRequests, setPendingCorrectionRequests] = useState([]);

  useEffect(() => {
    fetchGaps();
  }, []);

  // Suggestions are read-only, tenant-scoped worker data. Failure or slow CV
  // extraction never blocks the existing manual history/gap form.
  useEffect(() => {
    let mounted = true;
    const loadSuggestions = async () => {
      try {
        const token = localStorage.getItem('workerToken');
        if (!token) return;
        const result = await axios.get(`${API}/worker/cv-extraction-status`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (mounted) setCvSuggestionStatus(result.data || null);
      } catch {
        if (mounted) setCvSuggestionStatus(null);
      }
    };
    loadSuggestions();
    return () => { mounted = false; };
  }, []);

  const fetchGaps = async () => {
    try {
      const token = localStorage.getItem('workerToken');
      // Use /worker/employment-gaps which reads directly from db.employment_gaps
      // with stable UUIDs — prevents gap lookup failures on resubmit after admin reopen
      const res = await axios.get(`${API}/worker/employment-gaps`, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 15000,
      });
      setGaps(res.data.gaps || []);
      setHasGaps(res.data.has_gaps);
      setReasonTypes(res.data.reason_types || []);
      setCoverage(res.data.coverage || null);
      setEmploymentEntries(res.data.employment_entries || []);
      setInvalidEntries(res.data.invalid_entries || []);
      setUnmatchedNotes(res.data.unmatched_notes || []);
      setEmploymentSignedOff(Boolean(res.data.employment_review_signed_off));
      setSignedOffByName(res.data.employment_review_signed_off_by_name || null);
      setSignedOffAt(res.data.employment_review_signed_off_at || null);
      setEmploymentArchiveView(Boolean(res.data.employment_archive_view));
      if (res.data.employment_archive_view) {
        try {
          const corrections = await axios.get(`${API}/worker/employment-history/correction-requests`, {
            headers: { Authorization: `Bearer ${token}` },
            timeout: 10000,
          });
          setPendingCorrectionRequests(corrections.data?.requests || []);
        } catch (_error) {
          // Never invent an empty inbox when the status read fails.
        }
      } else {
        setPendingCorrectionRequests([]);
      }
      setGapLoadError(false);
    } catch {
      // Failed reads are not evidence that there are no gaps. Keep existing
      // records and explanation drafts visible, and allow an explicit retry.
      setGapLoadError(true);
    } finally {
      setLoading(false);
    }
  };

  const handleExplain = async (gapId) => {
    if (!explanation.trim()) {
      toast.error('Please provide an explanation');
      return;
    }
    setSubmitting(true);
    const submittedExplanation = explanation.trim();
    const submittedReasonType = reasonType || null;
    try {
      const token = localStorage.getItem('workerToken');
      await axios.post(`${API}/worker/employment-gaps/${gapId}/explain`,
        { explanation: submittedExplanation, reason_type: submittedReasonType },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      // The server accepted the explanation. Keep the applicant's local task
      // out of the editable queue even if the follow-up GET is unavailable.
      // This is "submitted", never "verified"; the backend owns final review.
      setGaps(current => current.map(gap =>
        (gap.id || gap.gap_id) === gapId
          ? { ...gap, explanation: submittedExplanation, reason_type: submittedReasonType,
              status: 'explained', worker_state: 'awaiting_admin_review' }
          : gap
      ));
      toast.success('Gap explanation submitted');
      setEditingGap(null);
      setExplanation('');
      setReasonType('');
      await fetchGaps();
      // The POST already succeeded. Dashboard refresh failures must never
      // tell an applicant the explanation was rejected or ask them to repeat it.
      if (typeof onUpdated === 'function') {
        try { await onUpdated(); } catch { toast.warning('Explanation saved. Dashboard status will refresh shortly.'); }
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Could not confirm your explanation was saved. Check the record before retrying.');
    } finally {
      setSubmitting(false);
    }
  };

  const openEmploymentDialog = (entry = null) => {
    if (employmentArchiveView && pendingCorrectionRequests.some(req => req.entry_id === (entry?.id || null))) {
      toast.info('This correction is already with Osabea.');
      return;
    }
    setEditingEmploymentEntryId(entry?.id || null);
    setCorrectionReason('');
    setEmploymentDraft({
      ...blankEmploymentEntry(),
      ...(entry || {}),
      // Missing a CV end date is not proof of current employment.
      is_current: Boolean(entry?.is_current === true || (entry?.id && entry?.is_current == null && !entry?.end_date)),
      end_date: entry?.is_current ? '' : (entry?.end_date || ''),
    });
    setEmploymentDialogOpen(true);
  };

  const updateEmploymentDraft = (field, value) => {
    setEmploymentDraft((current) => ({
      ...current,
      [field]: value,
      ...(field === 'is_current' && value ? { end_date: '' } : {}),
    }));
  };

  const saveEmploymentEntry = async () => {
    const employerName = employmentDraft.employer_name.trim();
    const jobTitle = employmentDraft.job_title.trim();
    const startDate = employmentDraft.start_date.trim();
    const endDate = employmentDraft.is_current ? null : employmentDraft.end_date.trim();

    if (!employerName || !jobTitle || !startDate || (!employmentDraft.is_current && !endDate)) {
      toast.error('Please add the employer, role and dates before saving.');
      return;
    }

    if (employmentArchiveView && correctionReason.trim().length < 5) {
      toast.error('Briefly explain what needs correcting.');
      return;
    }
    setSavingEmploymentEntry(true);
    try {
      const token = localStorage.getItem('workerToken');
      const response = await axios.post(
        employmentArchiveView
          ? `${API}/worker/employment-history/correction-requests`
          : `${API}/worker/employment-history/amend`,
        {
          entry_id: editingEmploymentEntryId,
          ...(employmentArchiveView ? { reason: correctionReason.trim() } : {}),
          entry: {
            employer_name: employerName,
            job_title: jobTitle,
            start_date: startDate,
            end_date: endDate,
            is_current: Boolean(employmentDraft.is_current),
            duties: employmentDraft.duties?.trim() || '',
            reason_for_leaving: employmentDraft.is_current ? '' : (employmentDraft.reason_for_leaving?.trim() || ''),
            employer_address: employmentDraft.employer_address?.trim() || '',
            employer_phone: employmentDraft.employer_phone?.trim() || '',
            can_contact: Boolean(employmentDraft.can_contact),
          },
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success(employmentArchiveView
        ? 'Correction sent to Osabea. Your approved history remains unchanged until reviewed.'
        : 'Work history saved. Your timeline has been updated.');
      if (employmentArchiveView) {
        setCorrectionPending(true);
        setPendingCorrectionRequests(old => [...old, {
          id: response.data?.request_id, entry_id: editingEmploymentEntryId, status: 'pending',
        }]);
      }
      setEmploymentDialogOpen(false);
      setEmploymentDraft(blankEmploymentEntry());
      setEditingEmploymentEntryId(null);
      await fetchGaps();
      if (typeof onUpdated === 'function') onUpdated();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Could not save this work history yet.');
    } finally {
      setSavingEmploymentEntry(false);
    }
  };

  if (loading) return null;
  if (!integrity && !forceWorkHistoryRepair && !hasGaps && !coverage && !invalidEntries.length && !unmatchedNotes.length && cvSuggestionStatus?.has_cv !== true && !gapLoadError) return null;

  const { actionable: unresolvedGaps, awaitingReview: awaitingAdminGaps, verified: verifiedGaps } = partitionEmploymentGaps(gaps);

  const formatGapDate = (d) => {
    if (!d) return '?';
    const date = new Date(d);
    if (isNaN(date.getTime())) return '?';
    return date.toLocaleDateString('en-GB', { month: 'short', year: 'numeric' });
  };

  const statusBadge = (gap) => {
    // Prefer worker_state from canonical review; fall back to raw status for legacy gaps
    const workerState = gap.worker_state;
    if (workerState === 'reviewed') return <Badge className="bg-green-100 text-green-700 text-xs"><CheckCircle className="h-3 w-3 mr-1" />Verified</Badge>;
    if (workerState === 'awaiting_admin_review') return <Badge className="bg-blue-100 text-blue-700 text-xs"><Clock className="h-3 w-3 mr-1" />With the team</Badge>;
    if (workerState === 'update_needed') return <Badge className="bg-red-100 text-red-700 text-xs"><AlertCircle className="h-3 w-3 mr-1" />Update needed</Badge>;
    if (workerState === 'action_required') return <Badge className="bg-slate-100 text-slate-600 text-xs">Action required</Badge>;
    // Legacy / direct db fallback
    const s = (gap.status || gap.verification_status || 'pending').toLowerCase();
    if (s === 'verified') return <Badge className="bg-green-100 text-green-700 text-xs"><CheckCircle className="h-3 w-3 mr-1" />Verified</Badge>;
    if (s === 'explained') return <Badge className="bg-blue-100 text-blue-700 text-xs"><Clock className="h-3 w-3 mr-1" />Awaiting review</Badge>;
    if (s === 'reopened') return <Badge className="bg-red-100 text-red-700 text-xs"><AlertCircle className="h-3 w-3 mr-1" />Update needed</Badge>;
    if (s === 'needs_more_info') return <Badge className="bg-amber-100 text-amber-700 text-xs"><AlertTriangle className="h-3 w-3 mr-1" />More info needed</Badge>;
    if (s === 'rejected') return <Badge className="bg-red-100 text-red-700 text-xs"><AlertCircle className="h-3 w-3 mr-1" />Rejected</Badge>;
    return <Badge className="bg-slate-100 text-slate-600 text-xs">Action required</Badge>;
  };

  const reasonLabel = (t) => {
    if (!t) return null;
    return t.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  const coveragePct = coverage?.coverage_percent ?? 0;
  const coverageMet = (coverage?.meets_10_year_requirement ?? false) && coveragePct > 0;
  const showGapExplanation = unresolvedGaps.length > 0 && !employmentArchiveView;
  const missingCvHints = getCvHistorySuggestions(cvSuggestionStatus, employmentEntries).filter(hint => !hint.existing);

  return (
    <section id="employment-history" className="rounded-2xl border border-slate-200 bg-white p-4 space-y-4 shadow-sm"
      data-testid="employment-history-section" aria-label="Work history">
      <header>
        <h2 className="text-lg font-semibold text-slate-900">Work history</h2>
        <p className="mt-1 text-sm text-slate-600">
          Check your jobs and dates, correct anything missing, then confirm once.
        </p>
      </header>
      {gapLoadError && !employmentArchiveView && (
        <div role="alert" className="rounded-xl border border-amber-200 bg-amber-50 p-4" data-testid="employment-gaps-retry">
          <p className="text-sm font-medium text-amber-900">We could not load your latest work history and gap details.</p>
          <p className="mt-1 text-xs text-amber-800">Do not resubmit anything you already sent. Try refreshing these details.</p>
          <Button type="button" size="sm" variant="outline" className="mt-3 min-h-11" onClick={fetchGaps} disabled={loading}>
            {loading ? 'Refreshing…' : 'Retry loading history'}
          </Button>
        </div>
      )}
      {employmentArchiveView && !coverage && (
        <div className="rounded-xl border border-teal-200 bg-teal-50 p-4 space-y-2"
          data-testid="employment-history-verified-section">
          <p className="text-sm font-semibold text-teal-900">Your work history is approved.</p>
          <p className="text-xs text-teal-800">The approved version cannot be edited directly.</p>
          <Button type="button" size="sm" variant="outline" className="min-h-11" onClick={() => openEmploymentDialog()}>
            Request correction
          </Button>
        </div>
      )}
      {awaitingAdminGaps.length > 0 && !employmentArchiveView && !coverage && (
        <div className="rounded-xl border border-blue-100 bg-blue-50 px-4 py-3 text-sm text-blue-900">
          {awaitingAdminGaps.length} submitted gap explanation{awaitingAdminGaps.length === 1 ? '' : 's'} with Osabea. No action needed.
        </div>
      )}
      {forceWorkHistoryRepair && !coverage && !employmentArchiveView && (
        <section data-testid="employment-coverage-section" className="space-y-3">
          <div className="rounded-xl border border-blue-100 bg-blue-50 p-4">
            <p className="text-sm font-semibold text-slate-900">Start with your most recent role</p>
            <p className="mt-1 text-sm text-slate-600">
              Add each job, care role, agency placement, education, training, caring break or unemployment period until the last 10 years are covered.
            </p>
          </div>
          <Button type="button" size="sm" className="min-h-11 w-full" onClick={() => openEmploymentDialog()}>
            <Plus className="mr-2 h-4 w-4" /> Add first job
          </Button>
        </section>
      )}

      {/* 10-Year Employment Coverage Summary */}
      {coverage && (
        <section data-testid="employment-coverage-section" className="space-y-3">
            {employmentArchiveView && (
              <div className="rounded-xl border border-teal-200 bg-teal-50 p-3 space-y-2"
                data-testid="employment-history-verified-section">
                <p className="text-sm font-semibold text-teal-950">Approved by Osabea</p>
                <p className="text-xs text-teal-800">Your signed-off record stays protected. Changes need a new review.</p>
                {signedOffByName && <p className="text-xs text-slate-600">
                  Approved by {signedOffByName}{signedOffAt ? ` on ${formatDate(signedOffAt)}` : ''}
                </p>}
                {(correctionPending || pendingCorrectionRequests.length > 0) && <p role="status" className="text-sm text-teal-900">
                  Correction request sent. Your approved history has not changed.
                </p>}
                <Button type="button" size="sm" variant="outline" className="min-h-11"
                  onClick={() => openEmploymentDialog()}
                  disabled={pendingCorrectionRequests.some(req => !req.entry_id)}>
                  {pendingCorrectionRequests.some(req => !req.entry_id) ? 'New role correction requested' : 'Request correction or add missing role'}
                </Button>
              </div>
            )}
            <div>
              <div className="flex justify-between text-xs text-slate-500 mb-1">
                <span>{coverage.coverage_start ? formatDate(coverage.coverage_start) : ''}</span>
                <span>{coverage.coverage_end ? formatDate(coverage.coverage_end) : 'Present'}</span>
              </div>
              <Progress value={coveragePct} className="h-2.5" />
              <p className="text-xs text-slate-500 mt-1">
                {coverage.total_days_covered?.toLocaleString() || 0} of {coverage.total_days_required?.toLocaleString() || 0} timeline days covered — staff verification is separate
              </p>
            </div>

            <div className="space-y-2 rounded-xl border border-slate-200 bg-white p-3"
              data-testid="employment-records-details">
              <h3 className="font-semibold text-slate-900">
                {employmentEntries.length > 0 ? 'Your jobs and dates' : 'Add your work history'}
              </h3>
              <div className="space-y-2">
                {employmentEntries.length > 0 ? (
                  <>
                    {employmentEntries.map((entry) => (
                      <div key={entry.id || entry.employer_name} className="rounded-lg bg-slate-50 p-3 text-sm">
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <div className="flex min-w-0 items-center gap-2">
                              <Briefcase className="h-3.5 w-3.5 shrink-0 text-slate-400" />
                              <span className="truncate font-medium text-slate-800">{entry.employer_name}</span>
                            </div>
                            {entry.job_title ? <p className="mt-1 text-xs text-slate-500">{entry.job_title}</p> : null}
                            <p className="mt-1 text-xs text-slate-500">
                              {formatDate(entry.start_date)} — {entry.is_current ? 'Present' : formatDate(entry.end_date)}
                            </p>
                          </div>
                          <Button size="sm" variant="ghost" className="min-h-10 shrink-0 px-2 text-xs"
                            onClick={() => openEmploymentDialog(entry)}
                            disabled={employmentArchiveView && pendingCorrectionRequests.some(req => req.entry_id === entry.id)}>
                            {employmentArchiveView
                              ? pendingCorrectionRequests.some(req => req.entry_id === entry.id) ? 'Correction requested' : 'Request correction'
                              : 'Edit'}
                          </Button>
                        </div>
                      </div>
                    ))}
                    {!employmentArchiveView ? (
                      <Button type="button" size="sm" variant="outline" onClick={() => openEmploymentDialog()} className="mt-1">
                        <Plus className="mr-1 h-3.5 w-3.5" />
                        Add another role
                      </Button>
                    ) : null}
                  </>
                ) : !employmentArchiveView ? (
                  <div className="rounded-xl border border-blue-100 bg-blue-50 p-4">
                    <p className="text-sm font-semibold text-slate-900">Add your recent work history</p>
                    <p className="mt-1 text-sm text-slate-600">
                      Start with your current or most recent role, then add anything else needed to cover the last 10 years.
                    </p>
                    <Button size="sm" onClick={() => openEmploymentDialog()} className="mt-3 bg-[#0F5C5E] hover:bg-[#0b4a4c]">
                      <Plus className="mr-1 h-3.5 w-3.5" />
                      Add first role
                    </Button>
                  </div>
                ) : null}
              </div>
            </div>
            {missingCvHints.length > 0 && !employmentArchiveView && (
              <div className="space-y-2 rounded-xl border border-teal-200 bg-teal-50/40 p-3"
                data-testid="work-history-cv-missing">
                <h3 className="text-sm font-semibold text-teal-900">
                  {missingCvHints.length} job{missingCvHints.length === 1 ? '' : 's'} from your CV to check
                </h3>
                {missingCvHints.map(hint => (
                  <div key={hint.key} className="flex items-center justify-between gap-2 rounded-lg bg-white p-2 text-sm">
                    <span className="font-medium text-slate-700">{hint.employer || hint.role}</span>
                    <Button type="button" size="sm" variant="outline" className="min-h-10"
                      onClick={() => openEmploymentDialog(hint.draft)}>Add job</Button>
                  </div>
                ))}
                <p className="text-xs text-slate-600">CV suggestions are not official records until you review and save them.</p>
              </div>
            )}
            {awaitingAdminGaps.length > 0 && !employmentArchiveView && (
              <p className="text-sm text-blue-800">
                {awaitingAdminGaps.length} employment gap explanation{awaitingAdminGaps.length === 1 ? '' : 's'} with Osabea for review.
              </p>
            )}

            {!coverageMet && !employmentArchiveView && (
              <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                {hasGaps
                  ? 'Please explain any gaps below. Short, honest notes are enough.'
                  : 'Your timeline does not yet cover the full 10 years. Add any missing work, education, training, caring breaks or unemployment periods in your employment history.'}
              </p>
            )}
        </section>
      )}

      {/* Employment gaps stay immediately actionable when corrections are needed.
          Submitted/verified gaps are optional records, not another task. */}
      {showGapExplanation && (
    <section data-testid="employment-gaps-section" className="space-y-3 border-t border-slate-200 pt-4"><h3 className="text-sm font-semibold text-amber-900">Gaps to explain</h3>
        {unresolvedGaps.map(gap => {
          const gapId = gap.id || gap.gap_id;
          const isEditing = editingGap === gapId;
          const months = gap.duration_months || Math.round((gap.duration_days || 0) / 30);
          return (
            <div key={gapId} className="rounded-xl border border-slate-200/80 bg-gradient-to-br from-white to-slate-50/50 p-4 shadow-sm">
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-medium text-slate-800">
                      {formatGapDate(gap.gap_start)} — {formatGapDate(gap.gap_end)}
                    </span>
                    {months > 0 && <span className="text-xs text-slate-500">({months} month{months !== 1 ? 's' : ''})</span>}
                    {statusBadge(gap)}
                  </div>
                  {gap.previous_employment?.employer_name && (
                    <p className="text-xs text-slate-500 mt-1">
                      Between: {gap.previous_employment.employer_name} → {gap.next_employment?.employer_name || 'next role'}
                    </p>
                  )}
                  {gap.explanation && !isEditing && (
                    <div className="mt-2 p-2 bg-slate-50 rounded text-sm text-slate-700">
                      {gap.reason_type && <span className="text-xs font-medium text-slate-500 block mb-1">{reasonLabel(gap.reason_type)}</span>}
                      {gap.explanation}
                    </div>
                  )}
                  {gap.admin_notes && (
                    <div className="mt-2 p-2 bg-amber-50 border border-amber-200 rounded text-sm text-amber-800">
                      <span className="text-xs font-medium block mb-1">What we need</span>
                      {gap.admin_notes || gap.verification_notes}
                    </div>
                  )}
                  {!gap.admin_notes && gap.reopen_reason && (
                    <div className="mt-2 p-2 bg-orange-50 border border-orange-200 rounded text-sm text-orange-800">
                      <span className="text-xs font-medium block mb-1">Reopened for review</span>
                      {gap.reopen_reason}
                    </div>
                  )}
                </div>
                {/* Only actionable gaps are rendered here. A reopened correction
                    must stay editable even when its legacy status is 'verified'. */}
                {!isEditing && (
                  <Button size="sm" variant="outline" onClick={() => {
                    setEditingGap(gapId);
                    setExplanation(gap.explanation || '');
                    setReasonType(gap.reason_type || '');
                  }}>
                    <Edit3 className="h-3.5 w-3.5 mr-1" />
                    {gap.explanation ? 'Update' : 'Explain'}
                  </Button>
                )}
              </div>
              {isEditing && (
                <div className="mt-3 space-y-3 border-t pt-3">
                  <div>
                    <label className="text-sm font-medium text-slate-700 block mb-1">Reason category</label>
                    <Select value={reasonType} onValueChange={setReasonType}>
                      <SelectTrigger className="w-full"><SelectValue placeholder="Select a category (optional)" /></SelectTrigger>
                      <SelectContent>
                        {reasonTypes.map(rt => (
                          <SelectItem key={rt} value={rt}>{reasonLabel(rt)}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-700 block mb-1">Explanation <span className="text-red-500">*</span></label>
                    <Textarea
                      value={explanation}
                      onChange={e => setExplanation(e.target.value)}
                      placeholder="Please explain what you were doing during this period..."
                      rows={3}
                    />
                  </div>
                  <div className="flex justify-end gap-2">
                    <Button size="sm" variant="ghost" onClick={() => { setEditingGap(null); setExplanation(''); setReasonType(''); }}>Cancel</Button>
                    <Button size="sm" onClick={() => handleExplain(gapId)} disabled={submitting} className="bg-blue-600 hover:bg-blue-700">
                      {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
                      Send explanation
                    </Button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
        {awaitingAdminGaps.length > 0 && (
          <p className="text-xs text-blue-800" data-testid="submitted-gaps-status">
            {awaitingAdminGaps.length} gap explanation{awaitingAdminGaps.length === 1 ? '' : 's'} with Osabea for review — no action needed.
          </p>
        )}
        {verifiedGaps.length > 0 && (
          <p className="text-xs text-emerald-800" data-testid="verified-gaps-status">
            {verifiedGaps.length} earlier gap explanation{verifiedGaps.length === 1 ? '' : 's'} verified and saved in your records.
          </p>
        )}
    </section>
      )}

      {/* Invalid Employment Entries */}
      {invalidEntries.length > 0 && (
        <section data-testid="invalid-entries-section" className="space-y-2 border-t border-slate-200 pt-4"><h3 className="text-sm font-semibold text-red-800">Dates that need correcting</h3>
            {invalidEntries.map((entry, i) => (
              <div key={entry.id || i} className="py-2 px-3 bg-red-50 border border-red-200 rounded-lg text-sm">
                <div className="flex items-center gap-2">
                  <Badge className="bg-red-100 text-red-700 text-xs">Correction required</Badge>
                  <span className="font-medium text-slate-800">{entry.employer_name || entry.organisation || 'Unknown employer'}</span>
                </div>
                {entry.validation_error && (
                  <p className="text-xs text-red-700 mt-1">{entry.validation_error}</p>
                )}
              </div>
            ))}
        </section>
      )}

      {/* Unmatched Applicant Notes */}
      {unmatchedNotes.length > 0 && (
        <section data-testid="unmatched-notes-section" className="space-y-2 border-t border-slate-200 pt-4"><h3 className="text-sm font-semibold text-slate-900">Other notes on file</h3>
            {unmatchedNotes.map((note, i) => (
              <div key={note.id || i} className="py-2 px-3 bg-white border border-slate-200 rounded-lg text-sm">
                <Badge className="bg-slate-100 text-slate-600 text-xs mb-1">Supporting note</Badge>
                <p className="text-slate-700">{note.explanation || note.text}</p>
              </div>
            ))}
        </section>
      )}

      {integrity && <EmploymentIntegrityCard integrity={integrity} onUpdated={onUpdated} embedded />}

      <Dialog open={employmentDialogOpen} onOpenChange={setEmploymentDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{employmentArchiveView ? 'Request a work history correction' : editingEmploymentEntryId ? 'Update work history' : 'Add work history'}</DialogTitle>
            <p className="text-sm text-slate-500">
              {employmentArchiveView
                ? 'Your approved record will not change until Osabea reviews this correction.'
                : 'Add one role at a time. Short, clear details are enough.'}
            </p>
          </DialogHeader>

          <div className="space-y-4">
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label>Employer</Label>
                <Input
                  value={employmentDraft.employer_name}
                  onChange={(event) => updateEmploymentDraft('employer_name', event.target.value)}
                  placeholder="Employer name"
                />
              </div>
              <div className="space-y-1.5">
                <Label>Role</Label>
                <Input
                  value={employmentDraft.job_title}
                  onChange={(event) => updateEmploymentDraft('job_title', event.target.value)}
                  placeholder="Care Assistant"
                />
              </div>
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label>Start month</Label>
                <Input
                  type="month"
                  value={employmentDraft.start_date}
                  onChange={(event) => updateEmploymentDraft('start_date', event.target.value)}
                />
              </div>
              <div className="space-y-1.5">
                <Label>End month</Label>
                <Input
                  type="month"
                  value={employmentDraft.end_date || ''}
                  disabled={employmentDraft.is_current}
                  onChange={(event) => updateEmploymentDraft('end_date', event.target.value)}
                />
              </div>
            </div>

            <label className="flex items-center gap-2 text-sm text-slate-700">
              <Checkbox
                checked={employmentDraft.is_current}
                onCheckedChange={(checked) => updateEmploymentDraft('is_current', Boolean(checked))}
              />
              I still work here
            </label>

            {!employmentDraft.is_current && (
              <div className="space-y-1.5">
                <Label>Reason for leaving</Label>
                <Input
                  value={employmentDraft.reason_for_leaving || ''}
                  onChange={(event) => updateEmploymentDraft('reason_for_leaving', event.target.value)}
                  placeholder="Career move, relocation, contract ended..."
                />
              </div>
            )}

            <div className="space-y-1.5">
              <Label>Main duties</Label>
              <Textarea
                value={employmentDraft.duties || ''}
                onChange={(event) => updateEmploymentDraft('duties', event.target.value)}
                placeholder="A short summary of what you did in this role"
                rows={3}
              />
            </div>

            <label className="flex items-center gap-2 text-sm text-slate-700">
              <Checkbox
                checked={employmentDraft.can_contact}
                onCheckedChange={(checked) => updateEmploymentDraft('can_contact', Boolean(checked))}
              />
              This employer may be contacted for a reference
            </label>
          </div>

          {employmentArchiveView && (
            <div className="space-y-1.5">
              <Label>Why does this record need correcting?</Label>
              <Textarea value={correctionReason}
                onChange={(event) => setCorrectionReason(event.target.value)}
                placeholder="Tell Osabea what changed and why" rows={2} />
            </div>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setEmploymentDialogOpen(false);
                setEmploymentDraft(blankEmploymentEntry());
                setEditingEmploymentEntryId(null);
              }}
              disabled={savingEmploymentEntry}
            >
              Cancel
            </Button>
            <Button onClick={saveEmploymentEntry} disabled={savingEmploymentEntry} className="bg-[#0F5C5E] hover:bg-[#0b4a4c]">
              {savingEmploymentEntry ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
              {employmentArchiveView ? 'Send correction request' : 'Save work history'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </section>
  );
}

// Forms Section Component
function FormsSection({ guidedMode = false, dashboardForms = null } = {}) {
  const [fetchedForms, setFetchedForms] = useState([]);
  const [loading, setLoading] = useState(dashboardForms == null);
  const navigate = useNavigate();
  const forms = dashboardForms ?? fetchedForms;

  useEffect(() => {
    if (dashboardForms != null) {
      setLoading(false);
      return;
    }
    fetchForms();
  }, [dashboardForms]);

  const fetchForms = async () => {
    try {
      const token = localStorage.getItem('workerToken');
      const response = await axios.get(`${API}/worker/forms`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { scope: 'onboarding' },
      });
      setFetchedForms(response.data.forms || []);
    } catch (error) {
      console.error('Failed to fetch forms:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (form) => {
    switch (form.status) {
      case 'submitted':
        if (form.type === 'equal_opportunities') {
          return <Badge className="bg-green-100 text-green-700"><CheckCircle className="h-3 w-3 mr-1" />Completed</Badge>;
        }
        return <Badge className="bg-blue-100 text-blue-700">Sent for review</Badge>;
      case 'verified':
        return <Badge className="bg-green-100 text-green-700"><CheckCircle className="h-3 w-3 mr-1" />Verified</Badge>;
      case 'signed_off':
      case 'reviewed':
      case 'approved':
        return <Badge className="bg-green-100 text-green-700"><CheckCircle className="h-3 w-3 mr-1" />Signed off</Badge>;
      case 'returned_for_correction':
      case 'reopened_for_worker_correction':
      case 'amendment_requested':
        return <Badge className="bg-red-100 text-red-700"><AlertCircle className="h-3 w-3 mr-1" />Correction required</Badge>;
      case 'rejected':
        return <Badge className="bg-red-100 text-red-700"><AlertCircle className="h-3 w-3 mr-1" />Action required</Badge>;
      case 'in_progress':
        return <Badge className="bg-amber-100 text-amber-700"><Clock className="h-3 w-3 mr-1" />{form.progress_percentage}% Done</Badge>;
      default:
        return <Badge className="bg-gray-100 text-gray-600">Not Started</Badge>;
    }
  };

  const handleFormClick = (formId) => {
    navigate(`/worker/forms/${formId}`);
  };

  if (loading) {
    return (
      <OnboardingPremiumCard className="py-8 flex justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </OnboardingPremiumCard>
    );
  }

  const actionRequiredForms = forms.filter((f) => isFormWorkerActionRequired(f.status));
  const awaitingReviewForms = forms.filter((f) => isFormAwaitingReview(f.status));
  const completedForms = forms.filter((f) => isFormAdminComplete(f.status));
  const guidedForms = guidedMode ? actionRequiredForms.slice(0, 1) : [];

  if (!guidedMode && actionRequiredForms.length === 0 && awaitingReviewForms.length === 0 && completedForms.length === 0) {
    return null;
  }

  const renderFormRow = (form, { clickable = true } = {}) => (
    <div
      key={form.id}
      className={`flex items-center justify-between rounded-xl border p-4 shadow-sm transition-colors ${getFormRowAccentClass(form)} ${clickable ? 'cursor-pointer hover:border-primary/20 hover:bg-slate-50/80' : ''}`}
      onClick={clickable ? () => handleFormClick(form.id) : undefined}
      data-testid={`form-${form.id}`}
    >
      <div className="flex items-center gap-3">
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
          isFormAwaitingReview(form.status) ? 'bg-purple-100'
            : ['returned_for_correction', 'reopened_for_worker_correction', 'amendment_requested', 'rejected'].includes(form.status) ? 'bg-red-100'
              : form.status === 'in_progress' ? 'bg-amber-100' : 'bg-blue-100'
        }`}>
          <FileText className={`h-5 w-5 ${
            isFormAwaitingReview(form.status) ? 'text-purple-600'
              : ['returned_for_correction', 'reopened_for_worker_correction', 'amendment_requested', 'rejected'].includes(form.status) ? 'text-red-600'
                : form.status === 'in_progress' ? 'text-amber-600' : 'text-blue-600'
          }`} />
        </div>
        <div>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-medium text-slate-800">{form.name}</span>
            {form.interview_lifecycle && (
              <Badge className="bg-purple-100 text-purple-700 text-xs">Before interview</Badge>
            )}
          </div>
          {isFormAwaitingReview(form.status) && (
            <p className="text-xs text-purple-700">
              Sent for review{form.submitted_at ? ` · ${formatDate(form.submitted_at)}` : ''} — you cannot edit until admin responds.
            </p>
          )}
          {['returned_for_correction', 'reopened_for_worker_correction', 'amendment_requested'].includes(form.status) && (
            <p className="text-xs text-red-600">We need one update. Please amend and resubmit.</p>
          )}
          {form.status === 'in_progress' && form.saved_at && (
            <p className="text-xs text-slate-500">Last saved: {formatDate(form.saved_at)}</p>
          )}
          {!form.required && (
            <p className="text-xs text-slate-400">Optional</p>
          )}
        </div>
      </div>
      <div className="flex items-center gap-2">
        {getStatusBadge(form)}
        <Button size="sm" className={getFormActionButtonClass(form)}>
          {getFormActionLabel(form)}
        </Button>
      </div>
    </div>
  );

  if (guidedMode) {
    if (guidedForms.length === 0) {
      return null;
    }
    return (
      <div className="space-y-2" data-testid="forms-section">
        {guidedForms.map((form) => renderFormRow(form))}
      </div>
    );
  }

  return (
    <OnboardingPremiumCard
      tone="info"
      data-testid="forms-section"
      title="Forms to Complete"
      icon={FileText}
      action={actionRequiredForms.length > 0 ? (
        <Badge className="bg-blue-100 text-blue-700">{actionRequiredForms.length} to do</Badge>
      ) : awaitingReviewForms.length > 0 ? (
        <Badge className="bg-purple-100 text-purple-700">{awaitingReviewForms.length} sent</Badge>
      ) : null}
    >
        <div className="space-y-2">
          {actionRequiredForms.map((form) => renderFormRow(form))}

          {awaitingReviewForms.length > 0 && (
            <>
              {actionRequiredForms.length > 0 && (
                <p className="pt-2 text-xs font-medium uppercase tracking-wide text-slate-500">Sent</p>
              )}
              {awaitingReviewForms.map((form) => renderFormRow(form))}
            </>
          )}
          {/* Completed Forms */}
          {completedForms.length > 0 && (actionRequiredForms.length > 0 || awaitingReviewForms.length > 0) && (
            <div className="border-t border-slate-200 pt-3 mt-3">
              <p className="text-xs text-slate-500 mb-2">Completed</p>
            </div>
          )}
          {completedForms.map((form) => (
            <div 
              key={form.id} 
              className="flex items-center justify-between p-4 bg-green-50 rounded-xl"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                  <CheckCircle className="h-5 w-5 text-green-600" />
                </div>
                <div>
                  <span className="font-medium text-slate-800">{form.name}</span>
                  {form.submitted_at && !['verified', 'signed_off', 'reviewed', 'approved'].includes(form.status) && (
                    <p className="text-xs text-blue-600">Sent {formatDate(form.submitted_at)}</p>
                  )}
                  {['verified', 'signed_off', 'reviewed', 'approved'].includes(form.status) && form.submitted_at && (
                    <p className="text-xs text-green-600">Completed {formatDate(form.submitted_at)}</p>
                  )}
                </div>
              </div>
              {getStatusBadge(form)}
            </div>
          ))}
        </div>
    </OnboardingPremiumCard>
  );
}

export default function WorkerDashboard() {
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const [dashboard, setDashboard] = useState(null);
  const applicationRecoveryAttempted = useRef(false);
  const trainingEvidenceRecoveryAttempted = useRef(false);
  const employmentIntegrityEvaluationAttempted = useRef(null);
  const [loading, setLoading] = useState(true);
  const [dashboardError, setDashboardError] = useState('');
  const dashboardRead = useRef(createLatestWorkerRead());
  const [uploading, setUploading] = useState(null);
  const [trainingUploadFeedback, setTrainingUploadFeedback] = useState(null);

  // CS-R2 (RTW): documents the applicant has JUST submitted this session. Used to
  // immediately lock the upload control and show "submitted / awaiting review"
  // without waiting for the dashboard refetch (prevents an accidental duplicate
  // upload and stops a successful submission from still looking incomplete).
  const [justSubmittedDocs, setJustSubmittedDocs] = useState({});
  const [trainingUploadReceipts, setTrainingUploadReceipts] = useState({});
  // CS-R2: the transient lock is keyed by the document SLOT's true identity
  // (docSlotKey) rather than broad doc.type, because two Proof-of-Address cards
  // share type 'proof_of_address'; keying by identity guarantees one slot's
  // "submitted" badge can never cross-lock the other slot. The lock is released
  // as soon as the canonical dashboard no longer lists that slot as missing
  // (i.e. it moved to awaiting-review/complete), so it bridges the submit ->
  // refetch window without leaving a stale badge or blocking a further slot.
  useEffect(() => {
    const missingSlotKeys = new Set(
      ((dashboard && dashboard.missing_documents) || []).map((d) => docSlotKey(d))
    );
    setJustSubmittedDocs((prev) => {
      const next = {};
      let changed = false;
      Object.keys(prev).forEach((slotKey) => {
        if (missingSlotKeys.has(slotKey)) {
          next[slotKey] = prev[slotKey]; // canonical state still shows it — keep lock
        } else {
          changed = true; // reflected as submitted/complete — release the lock
        }
      });
      return changed ? next : prev;
    });
  }, [dashboard]);
  // A training certificate is NOT a missing_documents slot. Keep its accepted
  // receipt until the canonical training status confirms that review has begun.
  useEffect(() => {
    setTrainingUploadReceipts(previous => reconcileTrainingUploadReceipts(
      previous, dashboard?.all_mandatory_trainings,
    ));
  }, [dashboard]);
  const [showSignaturePad, setShowSignaturePad] = useState(false);
  const [contractEligibility, setContractEligibility] = useState(null);
  // Organization settings for dynamic branding
  const [orgSettings, setOrgSettings] = useState({ organisation_name: '' });
  const tenantDisplayName = orgSettings.organisation_name || orgSettings.company_name || 'Your care provider';
  const providerName = tenantDisplayName;
  const appName = workforceAppShortName(tenantDisplayName);
  // Account settings - password setup
  const [accountStatus, setAccountStatus] = useState({ has_password: false });
  const [showSetPasswordModal, setShowSetPasswordModal] = useState(false);
  const [showReferencesDetails, setShowReferencesDetails] = useState(false);
  const [showAgreementsDetails, setShowAgreementsDetails] = useState(false);
  // Active staff use a single Today / Shifts / Alerts / More navigation.
  const [applicantView, setApplicantView] = useState(() => (
    searchParams?.has('step') || (typeof window !== 'undefined' && Boolean(window.location.hash))
      ? 'tasks' : 'home'
  ));
  const [showApplicantInduction, setShowApplicantInduction] = useState(() => searchParams?.get('induction') === '1');
  const [supportRequest, setSupportRequest] = useState(0);
  const [passwordForm, setPasswordForm] = useState({ new_password: '', confirm_password: '', current_password: '' });
  const [settingPassword, setSettingPassword] = useState(false);
  const [showNmcDialog, setShowNmcDialog] = useState(false);

  // App-like modals for progressive disclosure (Uber-style: keep main view clean)
  const [showEarningsModal, setShowEarningsModal] = useState(false);
  const [showShiftsModal, setShowShiftsModal] = useState(false);
  const [selectedMedShift, setSelectedMedShift] = useState(null);
  const [showComplianceModal, setShowComplianceModal] = useState(false);
  const [showDocumentsModal, setShowDocumentsModal] = useState(false);
  const [showPoliciesModal, setShowPoliciesModal] = useState(false);
  const [showIncidentsModal, setShowIncidentsModal] = useState(false);
  const [showMoreModal, setShowMoreModal] = useState(false);
  // Bottom nav drives real "page" views (not modals) for app-like navigation
  // Secure staff task links open the exact section after the one-use login
  // exchange. Unknown views cannot select privileged or hidden screens.
  const [workerMainView, setWorkerMainView] = useState(() => {
    const requested = searchParams?.get('view');
    return ['shifts', 'compliance', 'file'].includes(requested) ? requested : 'today';
  }); // 'today' | 'shifts' | 'compliance' | 'speakup' | 'file' | 'more'
  const [nmcPin, setNmcPin] = useState('');
  const [nmcExpiry, setNmcExpiry] = useState('');
  const [submittingNmc, setSubmittingNmc] = useState(false);
  // Notifications
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showNotificationsPanel, setShowNotificationsPanel] = useState(false);
  const [showAllNotifications, setShowAllNotifications] = useState(false);
  // Document viewer modal state
  const [viewerOpen, setViewerOpen] = useState(false);
  const [viewerDocument, setViewerDocument] = useState(null);
  const [viewerFetchUrl, setViewerFetchUrl] = useState(null);
  // CV lifecycle states (upload CTA and status only — review/verification is admin-only)
  const [cvStatus, setCvStatus] = useState(null);
  const [cvStatusLoading, setCvStatusLoading] = useState(false);
  // Reference mismatch explanation states
  const [referenceMismatches, setReferenceMismatches] = useState(null);
  const [showMismatchExplanationModal, setShowMismatchExplanationModal] = useState(false);
  const [selectedMismatch, setSelectedMismatch] = useState(null);
    // CS-R2: reference slots 1 and 2 are STRICTLY independent. Each slot owns its
    // own form draft, mismatch prompt and lifecycle phase, keyed by reference
    // number, so slot 1's data can never become the default for slot 2 (and vice
    // versa). Previously a single shared `provideNewForm` leaked between slots.
    const [provideNewRefNum, setProvideNewRefNum] = useState(null);
    const [provideNewFormsBySlot, setProvideNewFormsBySlot] = useState({});
    const [provideNewMismatchBySlot, setProvideNewMismatchBySlot] = useState({});
    // Per-slot lifecycle: 'editing' | 'submitting' | 'saved'.
    const [provideNewPhaseBySlot, setProvideNewPhaseBySlot] = useState({});
    // Slots whose saved draft we've already restored once (avoid clobbering typing).
    const restoredDraftSlotsRef = useRef(new Set());

    const referenceSlotForm = useCallback(
      (refNum) => provideNewFormsBySlot[refNum] || BLANK_REFEREE_FORM,
      [provideNewFormsBySlot],
    );
    const referenceSlotPhase = useCallback(
      (refNum) => provideNewPhaseBySlot[refNum] || 'editing',
      [provideNewPhaseBySlot],
    );
    const setReferenceSlotForm = useCallback((refNum, patch) => {
      setProvideNewFormsBySlot((prev) => ({
        ...prev,
        [refNum]: { ...(prev[refNum] || BLANK_REFEREE_FORM), ...patch },
      }));
    }, []);

    // Open (or toggle closed) a reference slot's inline form. Opening a slot ONLY
    // ever seeds THAT slot's own draft — never another slot's values.
    const openReferenceSlot = useCallback((slot) => {
      const refNum = slot.reference_number;
      setProvideNewRefNum((current) => (current === refNum ? null : refNum));
      setProvideNewFormsBySlot((prev) =>
        (prev[refNum] ? prev : { ...prev, [refNum]: draftToRefereeForm(slot) }));
      setProvideNewMismatchBySlot((prev) =>
        (slot.pending_mismatch
          ? { ...prev, [refNum]: { refNum, ...slot.pending_mismatch } }
          : prev));
    }, []);

    // Restore a saved pending-mismatch draft after refresh/return, PER SLOT, so the
    // applicant continues exactly where they stopped without re-entering details.
    // Placed with the other hooks (before any early return) for rules-of-hooks.
    useEffect(() => {
      const refs = (dashboard && dashboard.references) || [];
      let firstPending = null;
      refs.forEach((slot) => {
        if (!slot || !slot.draft || !slot.pending_mismatch) return;
        const refNum = slot.reference_number;
        if (firstPending === null) firstPending = refNum;
        if (restoredDraftSlotsRef.current.has(refNum)) return;
        restoredDraftSlotsRef.current.add(refNum);
        setProvideNewFormsBySlot((prev) =>
          (prev[refNum] ? prev : { ...prev, [refNum]: draftToRefereeForm(slot) }));
        setProvideNewMismatchBySlot((prev) =>
          (prev[refNum] ? prev : { ...prev, [refNum]: { refNum, ...slot.pending_mismatch } }));
      });
      if (firstPending !== null) {
        setProvideNewRefNum((current) => (current === null ? firstPending : current));
      }
    }, [dashboard]);
  const [mismatchExplanationType, setMismatchExplanationType] = useState('');
  const [mismatchExplanationText, setMismatchExplanationText] = useState('');
  const [submittingMismatchExplanation, setSubmittingMismatchExplanation] = useState(false);
  // Profile completion wizard state
  const [showProfileWizard, setShowProfileWizard] = useState(false);
  const [profileCompletionStatus, setProfileCompletionStatus] = useState(null);
  const [wizardStepOverride, setWizardStepOverride] = useState(() => {
    const step = searchParams?.get('step');
    const hash = typeof window !== 'undefined' ? window.location.hash : '';
    return step || WIZARD_ROUTE_TO_STEP[hash] || null;
  });
  const initialApplicantDeepLinkOpened = useRef(false);
  // A secure task link must OPEN the actual step, not just land on the
  // overview. This includes one-tap employment-history confirmation.
  const [openedWizardTask, setOpenedWizardTask] = useState(() => {
    const step = searchParams?.get('step');
    const hash = typeof window !== 'undefined' ? window.location.hash : '';
    return step || WIZARD_ROUTE_TO_STEP[hash] || null;
  });
  // Handbook acknowledgement modal state
  const [showHandbookAckModal, setShowHandbookAckModal] = useState(false);
  const [handbookAckAgreement, setHandbookAckAgreement] = useState(null);
  const [handbookPdfViewed, setHandbookPdfViewed] = useState(false);
  const [handbookAckConfirmed, setHandbookAckConfirmed] = useState(false);
  const [submittingHandbookAck, setSubmittingHandbookAck] = useState(false);
  const [showRecurringDetails, setShowRecurringDetails] = useState(false);
  const [selfAssessmentOpen, setSelfAssessmentOpen] = useState(false);
  const [activeAppraisalCycleId, setActiveAppraisalCycleId] = useState(null);
  const [appraisalPdfLoading, setAppraisalPdfLoading] = useState(null);
  const [showPastWorkerShifts, setShowPastWorkerShifts] = useState(false);
  const [workerShifts, setWorkerShifts] = useState([]);
  const [workerShiftsLoading, setWorkerShiftsLoading] = useState(false);
  const [earningsSummary, setEarningsSummary] = useState(null);
  const [earningsLoading, setEarningsLoading] = useState(false);
  const [showMyEarnings, setShowMyEarnings] = useState(true);
  const [availableShifts, setAvailableShifts] = useState([]);
  const [availableShiftsLoading, setAvailableShiftsLoading] = useState(false);
  const [availableShiftsError, setAvailableShiftsError] = useState('');
  const availableShiftsRead = useRef(createLatestWorkerRead());
  useEffect(() => {
    const reader = availableShiftsRead.current;
    return () => reader.cancel();
  }, []);
  const [availableShiftApplyingId, setAvailableShiftApplyingId] = useState(null);
  const [selectedWorkerShift, setSelectedWorkerShift] = useState(null);
  const [workerShiftDetailOpen, setWorkerShiftDetailOpen] = useState(false);
  const [workerShiftResponseOpen, setWorkerShiftResponseOpen] = useState(false);
  const [workerShiftResponseMode, setWorkerShiftResponseMode] = useState('accept');
  const [workerShiftResponseItem, setWorkerShiftResponseItem] = useState(null);
  const [workerShiftResponseNote, setWorkerShiftResponseNote] = useState('');
  const [workerShiftResponding, setWorkerShiftResponding] = useState(false);
  const [workerShiftClocking, setWorkerShiftClocking] = useState(false);
  // Site QR verification state — shown before clock-in/out/reclock
  const [siteVerifyOpen, setSiteVerifyOpen] = useState(false);
  const [siteVerifyPending, setSiteVerifyPending] = useState(null); // { item, mode }
  const [siteManualInput, setSiteManualInput] = useState('');
  const [siteScanActive, setSiteScanActive] = useState(false);
  const [siteScanResult, setSiteScanResult] = useState(null);
  const qrScannerRef = useRef(null);
  const [workerDailyNoteText, setWorkerDailyNoteText] = useState('');
  const [workerDailyNoteTags, setWorkerDailyNoteTags] = useState([]);
  const [workerDailyNoteSubmitting, setWorkerDailyNoteSubmitting] = useState(false);
  const [workerDailyNoteCorrectionOpen, setWorkerDailyNoteCorrectionOpen] = useState(false);
  const [workerDailyNoteCorrectionReason, setWorkerDailyNoteCorrectionReason] = useState('');
  const [workerIncidents, setWorkerIncidents] = useState([]);
  const [workerIncidentsLoading, setWorkerIncidentsLoading] = useState(false);
  const [incidentDetailOpen, setIncidentDetailOpen] = useState(false);
  const [selectedIncident, setSelectedIncident] = useState(null);
  const [incidentModalOpen, setIncidentModalOpen] = useState(false);
  const [incidentReportFromPastShift, setIncidentReportFromPastShift] = useState(false);
  const [incidentSubmitting, setIncidentSubmitting] = useState(false);
  const [incidentForm, setIncidentForm] = useState({
    incident_type: 'incident',
    occurred_at: '',
    location_text: '',
    description: '',
    people_involved: '',
    witnesses: '',
    immediate_actions_taken: '',
    injury_or_harm: '',
    safeguarding_concern: false,
    escalation_required: false,
    escalation_details: '',
    learning_outcome: '',
    prevention_actions: '',
    related_shift_id: '',
    service_user_id: '',
    note: '',
  });

  // Protected Speak Up / Raised Concerns (separate from regular incidents)
  const [raisedConcerns, setRaisedConcerns] = useState([]);
  const [raisedConcernsLoading, setRaisedConcernsLoading] = useState(false);
  const [raisedConcernModalOpen, setRaisedConcernModalOpen] = useState(false);
  const [raisedConcernSubmitting, setRaisedConcernSubmitting] = useState(false);
  const [raisedConcernForm, setRaisedConcernForm] = useState({
    categories: [],
    description: '',
    occurred_at: '',
    location_text: '',
    is_anonymous: false,
    contact_preference: 'full', // full | reference_only | none
    pida_ack: false,
  });
  const [showMyRaisedConcerns, setShowMyRaisedConcerns] = useState(true);

  // Body map state
  const [bodyMapModalOpen, setBodyMapModalOpen] = useState(false);
  const [bodyMapSubmitting, setBodyMapSubmitting] = useState(false);
  const [bodyMapForm, setBodyMapForm] = useState({
    related_shift_id: '',
    service_user_id: '',
    injury_date: '',
    reported_to: '',
    additional_information: '',
    marks: [],
  });
  const [bodyMapMarkDraft, setBodyMapMarkDraft] = useState({ region: '', description: '', view: null, x_pct: null, y_pct: null });
  const [admins, setAdmins] = useState([]);
  const navigate = useNavigate();
  const workerLoginPath = () => {
    const slug = localStorage.getItem('workerWorkspaceSlug');
    return slug ? `/workforce/${slug}` : '/worker/login';
  };

  useEffect(() => {
    const slug = localStorage.getItem('workerWorkspaceSlug');
    if (!slug || !tenantDisplayName || tenantDisplayName === 'Your care provider') return;
    updateWorkforceAppMetadata({
      tenantName: tenantDisplayName,
      shortName: appName,
      slug,
      primaryColour: orgSettings.primary_brand_colour || orgSettings.primary_colour,
    });
  }, [tenantDisplayName, appName, orgSettings.primary_brand_colour, orgSettings.primary_colour]);

  // Fetch admins/managers/supervisors for dropdown
  useEffect(() => {
    const fetchAdmins = async () => {
      try {
        const token = localStorage.getItem('workerToken');
        const response = await axios.get(`${API}/admins`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setAdmins(response.data.admins || []);
      } catch (error) {
        console.error('Failed to fetch admins:', error);
      }
    };
    fetchAdmins();
  }, []);

  const fetchDashboard = useCallback(async () => {
    const token = localStorage.getItem('workerToken');
    const read = dashboardRead.current.begin(token);
    const isCurrent = () => read.isCurrent(localStorage.getItem('workerToken'));
    setDashboardError('');
    setLoading(true);
    try {
      if (!token) {
        navigate(workerLoginPath());
        return;
      }

      const response = await axios.get(`${API}/worker/dashboard`, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 25000,
        signal: read.signal
      });
      if (!isCurrent()) return;
      if (!response.data?.employee?.id) throw new Error('Dashboard response incomplete');
      setDashboard(response.data);
      setContractEligibility(response.data?.contract_eligibility || null);
      // The canonical dashboard payload is enough to render the applicant's
      // workspace. Do not hold the whole screen behind secondary notification,
      // branding or account-status requests.
      setLoading(false);

      // ER-S1: the dashboard GET is side-effect free. When it reports a conclusively
      // recoverable historical application, invoke the explicit, idempotent, audited
      // recovery POST exactly once so the applicant never re-types their application.
      // The canonical key returned by the backend is `application_on_file`.
      if (response.data?.application_on_file?.recovery_available
          && !applicationRecoveryAttempted.current) {
        applicationRecoveryAttempted.current = true;
        try {
          const rec = await axios.post(`${API}/worker/application/recover`, {}, {
            headers: { Authorization: `Bearer ${token}` }, timeout: 25000, signal: read.signal
          });
          if (rec.data?.on_file) {
            const refreshed = await axios.get(`${API}/worker/dashboard`, {
              headers: { Authorization: `Bearer ${token}` }, timeout: 25000, signal: read.signal
            });
            if (!isCurrent()) return;
            if (!refreshed.data?.employee?.id) throw new Error('Dashboard response incomplete');
            setDashboard(refreshed.data);
            setContractEligibility(refreshed.data?.contract_eligibility || null);
          }
        } catch (recErr) {
          console.warn('Application auto-recovery skipped:', recErr);
        }
      }

      if (!isCurrent()) return;
      void loadWorkerDashboardBranding({ onBranding: (data) => { if (isCurrent()) setOrgSettings(data); } });
      
      // Fetch account status to check if password is set
      try {
        const accountRes = await axios.get(`${API}/worker/account-status`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (isCurrent()) setAccountStatus(accountRes.data);
      } catch (err) {
        console.warn('Could not fetch account status:', err);
      }
      
      // Fetch notifications
      try {
        const notifRes = await axios.get(`${API}/worker/notifications`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (isCurrent()) {
          setNotifications(notifRes.data.notifications || []);
          setUnreadCount(notifRes.data.unread_count || 0);
        }
      } catch (err) {
        console.warn('Could not fetch notifications:', err);
      }

    } catch (error) {
      if (!isCurrent()) return;
      setDashboard(null);
      setContractEligibility(null);
      setDashboardError(workerReadErrorMessage(error));
      if (error.response?.status === 401 || error.response?.status === 403) {
        localStorage.removeItem('workerToken');
        localStorage.removeItem('workerEmployee');
        toast.error('Session expired. Please login again.');
        navigate(workerLoginPath());
      } else if (error.code === 'ECONNABORTED') {
        toast.error('CareOps is taking too long to open. Please tap Try Again.');
      } else {
        toast.error('Failed to load dashboard');
      }
    } finally {
      if (isCurrent()) setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    fetchDashboard();
    const reader = dashboardRead.current;
    return () => reader.cancel();
  }, [fetchDashboard]);

  useEffect(() => {
    const integrity = dashboard?.employment_integrity;
    const state = String(integrity?.state || '').toLowerCase();
    if (state !== 'checking' || !integrity?.needs_evaluation) {
      if (state !== 'checking') employmentIntegrityEvaluationAttempted.current = null;
      return;
    }

    const evaluationKey = integrity?.input_fingerprint || integrity?.checking_started_at || 'checking';
    if (employmentIntegrityEvaluationAttempted.current === evaluationKey) return;
    employmentIntegrityEvaluationAttempted.current = evaluationKey;

    const runIntegrityEvaluation = async () => {
      const token = localStorage.getItem('workerToken');
      if (!token) return;
      try {
        await axios.post(`${API}/worker/employment-integrity/evaluate`, {}, {
          headers: { Authorization: `Bearer ${token}` },
        });
        await fetchDashboard();
      } catch (error) {
        console.warn('Employment integrity evaluation did not complete:', error);
        // The backend persists a fail-closed retryable error state. Refresh so
        // the applicant sees that state instead of an indefinite spinner.
        await fetchDashboard();
      }
    };
    runIntegrityEvaluation();
  }, [
    dashboard?.employment_integrity?.state,
    dashboard?.employment_integrity?.needs_evaluation,
    dashboard?.employment_integrity?.input_fingerprint,
    dashboard?.employment_integrity?.checking_started_at,
    fetchDashboard,
  ]);

  useEffect(() => {
    if (!dashboard || trainingEvidenceRecoveryAttempted.current) return;
    const hasMissingMandatoryTraining = (dashboard.all_mandatory_trainings || []).some(
      (item) => String(item?.status || '').toLowerCase() === 'missing'
    );
    if (!hasMissingMandatoryTraining) return;

    trainingEvidenceRecoveryAttempted.current = true;
    const recoverTrainingEvidence = async () => {
      const token = localStorage.getItem('workerToken');
      if (!token) return;
      try {
        const recovery = await axios.post(`${API}/worker/training-evidence/recover`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const summary = recovery.data?.summary || {};
        if ((summary.documents_scanned || 0) > 0
            || (summary.proposals_created || 0) > 0
            || (summary.documents_processed || 0) > 0) {
          await fetchDashboard();
        }
      } catch (recoveryError) {
        console.warn('Training evidence recovery skipped:', recoveryError);
      }
    };
    recoverTrainingEvidence();
  }, [dashboard, fetchDashboard]);

  useEffect(() => {
    // The server's forced action is the recommended default, NOT an enforced
    // navigation loop. An applicant may open any *currently eligible* step
    // while a manager reviews their declarations or interview.
    const wizard = dashboard?.onboarding_wizard;
    if (!wizard?.enabled) return;
    const isReachable = (stepId) => (wizard.steps || []).some(
      (step) => step.id === stepId && step.status !== 'locked'
    );
    const stepFromUrl = searchParams.get('step');
    if (stepFromUrl && isReachable(stepFromUrl)) {
      setWizardStepOverride(stepFromUrl);
      return;
    }

    // A one-tap secure link without a valid query can still focus the correct
    // available task. A locked Care Certificate/contract never becomes open.
    const hashTask = typeof window !== 'undefined'
      ? WIZARD_ROUTE_TO_STEP[window.location.hash] : null;
    if (hashTask && isReachable(hashTask)) {
      setOpenedWizardTask(hashTask);
      setWizardStepOverride(hashTask);
      return;
    }
    const fallback = wizard.forced_step_id || wizard.current_step_id;
    if (fallback && isReachable(fallback)) {
      setWizardStepOverride(fallback);
      if (stepFromUrl !== fallback) {
        const nextParams = new URLSearchParams(searchParams);
        nextParams.set('step', fallback);
        setSearchParams(nextParams, { replace: true });
      }
    }
  }, [searchParams, dashboard?.onboarding_wizard, setSearchParams]);

  useEffect(() => {
    if (!dashboard || initialApplicantDeepLinkOpened.current) return undefined;
    const hash = typeof window !== 'undefined' ? window.location.hash : '';
    const step = WIZARD_ROUTE_TO_STEP[hash];
    if (!step) return undefined;
    initialApplicantDeepLinkOpened.current = true;
    setApplicantView('tasks');
    setOpenedWizardTask(step);
    setWizardStepOverride(step);
    // The profile step renders asynchronously. Scroll after its content is
    // mounted so the user reaches the confirmation checkbox, not an overview.
    const scroll = window.setTimeout(() => {
      document.getElementById(hash.slice(1))?.scrollIntoView({
        behavior: 'smooth', block: 'center',
      });
    }, 350);
    return () => window.clearTimeout(scroll);
  }, [dashboard]);

  // Check if profile completion is needed (for offline PDF imports)
  const checkProfileCompletion = useCallback(async () => {
    try {
      const token = localStorage.getItem('workerToken');
      const response = await axios.get(`${API}/worker/profile-completion-status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProfileCompletionStatus(response.data);
      
      // Auto-show wizard if profile needs completion and not already shown
      if (response.data.needs_wizard && !showProfileWizard) {
        setShowProfileWizard(true);
      }
    } catch (error) {
      console.error('Failed to check profile completion:', error);
    }
  }, [showProfileWizard]);

  useEffect(() => {
    if (dashboard) {
      checkProfileCompletion();
    }
  }, [dashboard, checkProfileCompletion]);

  // Fetch CV extraction status
  const fetchCvStatus = useCallback(async () => {
    setCvStatusLoading(true);
    try {
      const token = localStorage.getItem('workerToken');
      const response = await axios.get(`${API}/worker/cv-extraction-status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCvStatus(response.data);
    } catch (error) {
      console.error('Failed to fetch CV status:', error);
    } finally {
      setCvStatusLoading(false);
    }
  }, []);

  useEffect(() => {
    if (dashboard) {
      const dashboardEmployee = dashboard?.employee || {};
      const activeCapability = dashboard?.capabilities?.active_workforce;
      const workerIsActive =
        typeof activeCapability === 'boolean'
          ? activeCapability
          : (
              typeof dashboard?.is_active_employee === 'boolean'
                ? dashboard.is_active_employee
                : (
                    dashboardEmployee?.is_active_employee ||
                    isActiveLifecycleStatus(dashboardEmployee?.employee_status) ||
                    isActiveLifecycleStatus(dashboardEmployee?.status)
                  )
            );
      fetchCvStatus();
      fetchReferenceMismatches();
      if (workerIsActive) {
        fetchWorkerShifts();
        fetchAvailableShifts();
        fetchWorkerIncidents();
        fetchMyRaisedConcerns();
        fetchWorkerEarnings();
      } else {
        setWorkerShifts([]);
        setAvailableShifts([]);
        setWorkerIncidents([]);
        setRaisedConcerns([]);
        setEarningsSummary(null);
      }
    }
  }, [dashboard, fetchCvStatus]);

  const fetchWorkerShifts = async () => {
    setWorkerShiftsLoading(true);
    try {
      const token = localStorage.getItem('workerToken');
      const response = await axios.get(`${API}/worker/shifts`, {
        params: { include_completed: true },
        headers: { Authorization: `Bearer ${token}` }
      });
      const rows = response.data?.shifts || [];
      setWorkerShifts(rows);
    } catch (error) {
      console.error('Failed to fetch worker shifts:', error);
    } finally {
      setWorkerShiftsLoading(false);
    }
  };

  const fetchWorkerEarnings = async () => {
    setEarningsLoading(true);
    try {
      const token = localStorage.getItem('workerToken');
      const response = await axios.get(`${API}/worker/earnings-summary`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setEarningsSummary(response.data);
    } catch (error) {
      console.error('Failed to fetch worker earnings summary:', error);
      setEarningsSummary(null);
    } finally {
      setEarningsLoading(false);
    }
  };

  const fetchAvailableShifts = async () => {
    const token = localStorage.getItem('workerToken');
    const read = availableShiftsRead.current.begin(token);
    setAvailableShiftsError('');
    setAvailableShiftsLoading(true);
    try {
      const response = await axios.get(`${API}/worker/shifts/available`, {
        headers: { Authorization: `Bearer ${token}` }, signal: read.signal, timeout: 20000,
      });
      if (!read.isCurrent(localStorage.getItem('workerToken'))) return;
      if (!Array.isArray(response.data?.shifts)) throw new Error('Invalid shifts');
      setAvailableShifts(response.data.shifts);
    } catch (error) {
      if (read.isCurrent(localStorage.getItem('workerToken'))) setAvailableShiftsError('We couldn’t load available shifts. Try again.');
    } finally {
      if (read.isCurrent(localStorage.getItem('workerToken'))) setAvailableShiftsLoading(false);
    }
  };

  const handleApplyForAvailableShift = async (shiftId) => {
    if (!shiftId) return;
    setAvailableShiftApplyingId(shiftId);
    try {
      const token = localStorage.getItem('workerToken');
      await axios.post(
        `${API}/worker/shifts/${shiftId}/apply`,
        { note: null },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success('Interest registered — admin will review your application');
      await fetchAvailableShifts();
    } catch (error) {
      const message = typeof error.response?.data?.detail === 'string'
        ? error.response.data.detail
        : 'Could not register interest for this shift';
      toast.error(message);
    } finally {
      setAvailableShiftApplyingId(null);
    }
  };

  const handleOpenWorkerShift = async (shiftId) => {
    try {
      const token = localStorage.getItem('workerToken');
      const response = await axios.get(`${API}/worker/shifts/${shiftId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedWorkerShift(response.data || null);
      if (response.data?.current_daily_note) {
        const note = response.data.current_daily_note;
        setWorkerDailyNoteText(note.effective_note_text || note.note_text || '');
        setWorkerDailyNoteTags(
          Array.isArray(note.effective_tags)
            ? note.effective_tags
            : Array.isArray(note.tags) ? note.tags : []
        );
      } else {
        setWorkerDailyNoteText('');
        setWorkerDailyNoteTags([]);
      }
      setWorkerDailyNoteCorrectionOpen(false);
      setWorkerDailyNoteCorrectionReason('');
      setWorkerShiftDetailOpen(true);
    } catch (error) {
      const message = typeof error.response?.data?.detail === 'string'
        ? error.response.data.detail
        : 'Could not load shift detail';
      toast.error(message);
    }
  };

  const openWorkerShiftResponseModal = (item, mode) => {
    setWorkerShiftResponseItem(item || null);
    setWorkerShiftResponseMode(mode);
    setWorkerShiftResponseNote('');
    setWorkerShiftResponseOpen(true);
  };

  const handleWorkerShiftResponse = async () => {
    const shiftId = workerShiftResponseItem?.shift?.id;
    if (!shiftId) {
      toast.error('Shift not found');
      return;
    }
    setWorkerShiftResponding(true);
    try {
      const token = localStorage.getItem('workerToken');
      const endpoint = workerShiftResponseMode === 'reject'
        ? `${API}/worker/shifts/${shiftId}/reject`
        : `${API}/worker/shifts/${shiftId}/accept`;
      await axios.post(
        endpoint,
        { note: workerShiftResponseNote || null },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(workerShiftResponseMode === 'reject' ? 'Shift rejected' : 'Shift accepted');
      setWorkerShiftResponseOpen(false);
      setWorkerShiftResponseItem(null);
      setWorkerShiftResponseNote('');
      fetchWorkerShifts();
      fetchDashboard();
    } catch (error) {
      const message = typeof error.response?.data?.detail === 'string'
        ? error.response.data.detail
        : 'Could not update shift response';
      toast.error(message);
    } finally {
      setWorkerShiftResponding(false);
    }
  };

  const getWorkerShiftAttendanceStatus = (item) => getWorkerShiftAttendanceStatusFromItem(item);

  const workerShiftGroups = useMemo(() => {
    const pending = [];
    const active = [];
    const history = [];
    for (const item of workerShifts) {
      const category = getWorkerShiftListCategory(item);
      if (category === 'pending_offer') pending.push(item);
      else if (category === 'history') history.push(item);
      else active.push(item);
    }
    const byStartAsc = (a, b) => String(a.shift?.start_at || '').localeCompare(String(b.shift?.start_at || ''));
    const byEndDesc = (a, b) => String(b.shift?.end_at || '').localeCompare(String(a.shift?.end_at || ''));
    pending.sort(byStartAsc);
    active.sort(byStartAsc);
    history.sort(byEndDesc);
    return { pending, active, history };
  }, [workerShifts]);

  const selectedWorkerShiftCategory = selectedWorkerShift
    ? getWorkerShiftListCategory(selectedWorkerShift)
    : 'active';
  const isSelectedHistoricalShift = selectedWorkerShiftCategory === 'history';

  // Geolocation capture — resolves even if permission denied or feature unavailable
  const getGeolocation = () =>
    new Promise((resolve) => {
      if (!navigator.geolocation) {
        resolve({ latitude: null, longitude: null, accuracy_metres: null, geo_flag: 'location_unavailable' });
        return;
      }
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          resolve({
            latitude: pos.coords.latitude,
            longitude: pos.coords.longitude,
            accuracy_metres: pos.coords.accuracy ?? null,
            geo_flag: null,
          });
        },
        () => {
          resolve({ latitude: null, longitude: null, accuracy_metres: null, geo_flag: 'location_denied' });
        },
        { timeout: 8000, enableHighAccuracy: false },
      );
    });

  const canWorkerClockIn = (item) => {
    const assignmentStatus = item?.assignment_status || item?.assignment?.status;
    const workerResponseStatus = item?.worker_response_status || item?.assignment?.worker_response_status || 'pending';
    const attendanceStatus = getWorkerShiftAttendanceStatus(item);
    return assignmentStatus === 'active'
      && workerResponseStatus === 'accepted'
      && !['open', 'submitted', 'approved', 'rejected'].includes(attendanceStatus || '');
  };

  const canWorkerClockOut = (item) => (
    getWorkerShiftAttendanceStatus(item) === 'open' && isWorkerAssignmentActiveAccepted(item)
  );

  const canWorkerReClock = (item) => {
    const assignmentStatus = item?.assignment_status || item?.assignment?.status;
    const workerResponseStatus = item?.worker_response_status || item?.assignment?.worker_response_status || 'pending';
    const attendance = item?.current_attendance;
    return assignmentStatus === 'active'
      && workerResponseStatus === 'accepted'
      && workerMayResubmitRejectedAttendance(attendance);
  };

  const handleWorkerShiftClock = async (item, mode, siteToken = null, siteTokenMethod = null) => {
    const shiftId = item?.shift?.id;
    if (!shiftId) {
      toast.error('Shift not found');
      return;
    }
    setWorkerShiftClocking(true);
    try {
      const token = localStorage.getItem('workerToken');
      const endpoint = mode === 'in'
        ? `${API}/worker/shifts/${shiftId}/clock-in`
        : `${API}/worker/shifts/${shiftId}/clock-out`;
      const geoData = await getGeolocation();
      await axios.post(
        endpoint,
        {
          note: null,
          latitude: geoData.latitude,
          longitude: geoData.longitude,
          accuracy_metres: geoData.accuracy_metres,
          geo_flag: geoData.geo_flag,
          site_token: siteToken,
          site_token_method: siteTokenMethod,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success(mode === 'in' ? 'Clocked in successfully' : 'Clocked out successfully');
      await fetchWorkerShifts();
      if (selectedWorkerShift?.shift?.id === shiftId) {
        await handleOpenWorkerShift(shiftId);
      }
    } catch (error) {
      toast.error(readableErrorDetail(
        error,
        mode === 'in' ? 'Could not clock in' : 'Could not clock out',
      ));
    } finally {
      setWorkerShiftClocking(false);
    }
  };

  const handleWorkerReClock = async (item, siteToken = null, siteTokenMethod = null) => {
    const shiftId = item?.shift?.id;
    if (!shiftId) {
      toast.error('Shift not found');
      return;
    }
    setWorkerShiftClocking(true);
    try {
      const token = localStorage.getItem('workerToken');
      const geoData = await getGeolocation();
      await axios.post(
        `${API}/worker/shifts/${shiftId}/reclock`,
        {
          note: null,
          latitude: geoData.latitude,
          longitude: geoData.longitude,
          accuracy_metres: geoData.accuracy_metres,
          geo_flag: geoData.geo_flag,
          site_token: siteToken,
          site_token_method: siteTokenMethod,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success('Attendance re-submitted. Please clock out when your shift ends.');
      await fetchWorkerShifts();
      if (selectedWorkerShift?.shift?.id === shiftId) {
        await handleOpenWorkerShift(shiftId);
      }
    } catch (error) {
      const message = typeof error.response?.data?.detail === 'string'
        ? error.response.data.detail
        : 'Could not re-submit attendance';
      toast.error(message);
    } finally {
      setWorkerShiftClocking(false);
    }
  };

  // ── Site QR verification helpers ────────────────────────────────────────────
  const stopQrScanner = async () => {
    if (qrScannerRef.current) {
      try {
        if (qrScannerRef.current.isScanning) {
          await qrScannerRef.current.stop();
        }
      } catch (_) {}
      qrScannerRef.current = null;
    }
    setSiteScanActive(false);
  };

  const startQrScanner = async () => {
    if (siteScanActive) return;
    try {
      const { Html5Qrcode } = await import('html5-qrcode');
      const scanner = new Html5Qrcode('site-qr-reader');
      qrScannerRef.current = scanner;
      setSiteScanActive(true);
      await scanner.start(
        { facingMode: 'environment' },
        { fps: 10, qrbox: { width: 220, height: 220 } },
        (text) => { setSiteScanResult(text); stopQrScanner(); },
        () => {},
      );
    } catch {
      setSiteScanActive(false);
      toast.error('Camera not available. Please enter the code manually.');
    }
  };

  const openSiteVerify = (item, mode) => {
    setSiteManualInput('');
    setSiteScanResult(null);
    setSiteVerifyPending({ item, mode });
    setSiteVerifyOpen(true);
  };

  const confirmSiteVerify = async () => {
    const { item, mode } = siteVerifyPending;
    const scannedToken = siteScanResult || null;
    const manualToken = siteManualInput.trim() || null;
    const resolvedToken = scannedToken || manualToken;
    const resolvedMethod = scannedToken ? 'qr_scan' : manualToken ? 'manual_code' : null;
    setSiteVerifyOpen(false);
    await stopQrScanner();
    setSiteManualInput('');
    setSiteScanResult(null);
    if (mode === 'reclock') {
      await handleWorkerReClock(item, resolvedToken, resolvedMethod);
    } else {
      await handleWorkerShiftClock(item, mode, resolvedToken, resolvedMethod);
    }
  };

  const skipSiteVerify = async () => {
    const { item, mode } = siteVerifyPending;
    setSiteVerifyOpen(false);
    await stopQrScanner();
    setSiteManualInput('');
    setSiteScanResult(null);
    if (mode === 'reclock') {
      await handleWorkerReClock(item, null, null);
    } else {
      await handleWorkerShiftClock(item, mode, null, null);
    }
  };
  // ────────────────────────────────────────────────────────────────────────────

  const toggleWorkerDailyNoteTag = (tag) => {
    setWorkerDailyNoteTags((prev) => (
      prev.includes(tag) ? prev.filter((item) => item !== tag) : [...prev, tag]
    ));
  };

  const handleSubmitWorkerDailyNote = async () => {
    const shiftId = selectedWorkerShift?.shift?.id;
    if (!shiftId) {
      toast.error('Shift not found');
      return;
    }
    const text = (workerDailyNoteText || '').trim();
    if (text.length < 2) {
      toast.error('Daily note text is required');
      return;
    }
    setWorkerDailyNoteSubmitting(true);
    try {
      const token = localStorage.getItem('workerToken');
      await axios.post(
        `${API}/worker/shifts/${shiftId}/daily-notes`,
        { note_text: text, tags: workerDailyNoteTags },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Daily note saved');
      await fetchWorkerShifts();
      await handleOpenWorkerShift(shiftId);
    } catch (error) {
      toast.error(readableErrorDetail(error, 'Could not save daily note'));
    } finally {
      setWorkerDailyNoteSubmitting(false);
    }
  };

  const handleAmendWorkerDailyNote = async () => {
    const shiftId = selectedWorkerShift?.shift?.id;
    const noteId = selectedWorkerShift?.current_daily_note?.id;
    if (!shiftId || !noteId) {
      toast.error('Daily note not found');
      return;
    }
    const text = (workerDailyNoteText || '').trim();
    const reason = (workerDailyNoteCorrectionReason || '').trim();
    if (text.length < 2) {
      toast.error('Corrected daily note text is required');
      return;
    }
    if (reason.length < 4) {
      toast.error('Please explain why you are correcting the note');
      return;
    }
    setWorkerDailyNoteSubmitting(true);
    try {
      const token = localStorage.getItem('workerToken');
      await axios.post(
        `${API}/worker/shifts/${shiftId}/daily-notes/${noteId}/amend`,
        { note_text: text, tags: workerDailyNoteTags, reason },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success('Daily note correction saved. The original record is preserved.');
      setWorkerDailyNoteCorrectionOpen(false);
      setWorkerDailyNoteCorrectionReason('');
      await fetchWorkerShifts();
      await handleOpenWorkerShift(shiftId);
    } catch (error) {
      toast.error(readableErrorDetail(error, 'Could not save daily note correction'));
    } finally {
      setWorkerDailyNoteSubmitting(false);
    }
  };

  const fetchWorkerIncidents = async () => {
    setWorkerIncidentsLoading(true);
    try {
      const token = localStorage.getItem('workerToken');
      const response = await axios.get(`${API}/worker/incidents`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setWorkerIncidents(response.data?.incidents || []);
    } catch (error) {
      console.error('Failed to fetch worker incidents:', error);
    } finally {
      setWorkerIncidentsLoading(false);
    }
  };

  const handleReportIncidentForShift = () => {
    const shift = selectedWorkerShift?.shift;
    setIncidentReportFromPastShift(
      selectedWorkerShift ? getWorkerShiftListCategory(selectedWorkerShift) === 'history' : false,
    );
    if (!shift?.id) {
      setIncidentModalOpen(true);
      return;
    }
    const now = new Date();
    const localIso = new Date(now.getTime() - now.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
    setIncidentForm((prev) => ({
      ...prev,
      occurred_at: prev.occurred_at || localIso,
      location_text: prev.location_text || shift.location_text || '',
      related_shift_id: shift.id,
      service_user_id: shift.service_user_id || '',
    }));
    setIncidentModalOpen(true);
  };

  const handleSubmitIncident = async () => {
    if (!incidentForm.occurred_at || !incidentForm.location_text || !incidentForm.description) {
      toast.error('Date/time, location, and description are required');
      return;
    }
    setIncidentSubmitting(true);
    try {
      const token = localStorage.getItem('workerToken');
      await axios.post(
        `${API}/worker/incidents`,
        {
          incident_type: incidentForm.incident_type,
          occurred_at: new Date(incidentForm.occurred_at).toISOString(),
          location_text: incidentForm.location_text,
          description: incidentForm.description,
          people_involved: incidentForm.people_involved || null,
          witnesses: incidentForm.witnesses || null,
          immediate_actions_taken: incidentForm.immediate_actions_taken || null,
          injury_or_harm: incidentForm.injury_or_harm || null,
          safeguarding_concern: !!incidentForm.safeguarding_concern,
          escalation_required: !!incidentForm.escalation_required,
          escalation_details: incidentForm.escalation_required ? (incidentForm.escalation_details || null) : null,
          learning_outcome: incidentForm.learning_outcome || null,
          prevention_actions: incidentForm.prevention_actions || null,
          related_shift_id: incidentForm.related_shift_id || null,
          service_user_id: incidentForm.service_user_id || null,
          note: incidentForm.note || null,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Incident submitted');
      setIncidentModalOpen(false);
      setIncidentForm({
        incident_type: 'incident',
        occurred_at: '',
        location_text: '',
        description: '',
        people_involved: '',
        witnesses: '',
        immediate_actions_taken: '',
        injury_or_harm: '',
        safeguarding_concern: false,
        escalation_required: false,
        escalation_details: '',
        learning_outcome: '',
        prevention_actions: '',
        related_shift_id: '',
        service_user_id: '',
        note: '',
      });
      fetchWorkerIncidents();
    } catch (error) {
      const message = typeof error.response?.data?.detail === 'string'
        ? error.response.data.detail
        : 'Failed to submit incident';
      toast.error(message);
    } finally {
      setIncidentSubmitting(false);
    }
  };

  // === Protected Raised Concerns (Speak Up / PIDA channel) - separate from regular incidents ===
  const RAISED_CONCERN_CATEGORIES = [
    'Malpractice or illegal activity',
    'Danger to health & safety (service users, staff or public)',
    'Breach of legal or CQC obligation',
    'Bullying, harassment or victimisation by management or colleagues',
    'Fraud or financial irregularity',
    'Cover-up of any of the above',
    'Other (please describe)',
  ];

  const fetchMyRaisedConcerns = async () => {
    setRaisedConcernsLoading(true);
    try {
      const token = localStorage.getItem('workerToken');
      const response = await axios.get(`${API}/worker/raised-concerns`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setRaisedConcerns(response.data?.concerns || []);
    } catch (error) {
      console.error('Failed to fetch raised concerns:', error);
    } finally {
      setRaisedConcernsLoading(false);
    }
  };

  const handleOpenRaiseProtectedConcern = () => {
    const now = new Date();
    const localIso = new Date(now.getTime() - now.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
    setRaisedConcernForm({
      categories: [],
      description: '',
      occurred_at: localIso,
      location_text: '',
      is_anonymous: false,
      contact_preference: 'full',
      pida_ack: false,
    });
    setRaisedConcernModalOpen(true);
  };

  const handleSubmitRaisedConcern = async () => {
    if (!raisedConcernForm.description.trim()) {
      toast.error('Please describe the concern');
      return;
    }
    if (raisedConcernForm.categories.length === 0) {
      toast.error('Please select at least one category');
      return;
    }
    if (!raisedConcernForm.pida_ack) {
      toast.error('Please confirm you understand this is a protected disclosure');
      return;
    }

    setRaisedConcernSubmitting(true);
    try {
      const token = localStorage.getItem('workerToken');
      const payload = {
        categories: raisedConcernForm.categories,
        description: raisedConcernForm.description.trim(),
        occurred_at: raisedConcernForm.occurred_at ? new Date(raisedConcernForm.occurred_at).toISOString() : null,
        location_text: raisedConcernForm.location_text || null,
        is_anonymous: !!raisedConcernForm.is_anonymous,
        contact_preference: raisedConcernForm.contact_preference,
      };

      const res = await axios.post(
        `${API}/worker/raised-concerns`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const ref = res.data?.reference;
      toast.success(ref 
        ? `Protected concern received. Reference: ${ref}` 
        : 'Protected concern received. You will not suffer any detriment.');

      setRaisedConcernModalOpen(false);
      setRaisedConcernForm({
        categories: [],
        description: '',
        occurred_at: '',
        location_text: '',
        is_anonymous: false,
        contact_preference: 'full',
        pida_ack: false,
      });

      await fetchMyRaisedConcerns();
    } catch (error) {
      const message = typeof error.response?.data?.detail === 'string'
        ? error.response.data.detail
        : 'Could not submit protected concern';
      toast.error(message);
    } finally {
      setRaisedConcernSubmitting(false);
    }
  };

  const toggleRaisedConcernCategory = (cat) => {
    setRaisedConcernForm((prev) => {
      const has = prev.categories.includes(cat);
      return {
        ...prev,
        categories: has
          ? prev.categories.filter((c) => c !== cat)
          : [...prev.categories, cat],
      };
    });
  };

  const openIncidentDetail = (incident) => {
    setSelectedIncident(incident);
    setIncidentDetailOpen(true);
  };

  const handleReportBodyMapForShift = () => {
    const shift = selectedWorkerShift?.shift;
    setBodyMapForm({
      related_shift_id: shift?.id || '',
      service_user_id: shift?.service_user_id || '',
      gender: '',
      injury_date: '',
      reported_to: '',
      additional_information: '',
      marks: [],
    });
    setBodyMapMarkDraft({ region: '', description: '', view: null, x_pct: null, y_pct: null });
    setBodyMapModalOpen(true);
  };

  const handleAddBodyMapMark = () => {
    if (!bodyMapMarkDraft.region || !bodyMapMarkDraft.description.trim()) {
      toast.error('Select a body region and enter a description');
      return;
    }
    if (bodyMapMarkDraft.x_pct == null || bodyMapMarkDraft.y_pct == null) {
      toast.error('Tap the exact location on the body map first');
      return;
    }
    setBodyMapForm(prev => ({ ...prev, marks: [...prev.marks, { ...bodyMapMarkDraft }] }));
    setBodyMapMarkDraft({ region: '', description: '', view: null, x_pct: null, y_pct: null });
  };

  const handleRemoveBodyMapMark = (idx) => {
    setBodyMapForm(prev => ({ ...prev, marks: prev.marks.filter((_, i) => i !== idx) }));
  };

  const handleSubmitBodyMap = async () => {
    if (bodyMapForm.marks.length === 0) {
      toast.error('Add at least one marked location before submitting');
      return;
    }
    if (!bodyMapForm.gender) {
      toast.error('Please select the gender of the service user');
      return;
    }
    if (!bodyMapForm.reported_to) {
      toast.error('Please select who this should be reported to');
      return;
    }
    setBodyMapSubmitting(true);
    try {
      const token = localStorage.getItem('workerToken');
      await axios.post(`${API}/worker/body-maps`, {
        related_shift_id: bodyMapForm.related_shift_id || null,
        service_user_id: bodyMapForm.service_user_id || null,
        gender: bodyMapForm.gender || 'unknown',
        injury_date: bodyMapForm.injury_date || null,
        reported_to: bodyMapForm.reported_to || '',
        additional_information: bodyMapForm.additional_information || '',
        marks: bodyMapForm.marks,
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Body map submitted');
      setBodyMapModalOpen(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit body map');
    } finally {
      setBodyMapSubmitting(false);
    }
  };

  // Fetch reference-employment mismatches
  const fetchReferenceMismatches = async () => {
    try {
      const token = localStorage.getItem('workerToken');
      const response = await axios.get(`${API}/worker/reference-mismatches`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReferenceMismatches(response.data);
    } catch (error) {
      console.error('Failed to fetch reference mismatches:', error);
    }
  };

  // Open mismatch explanation modal
  const openMismatchExplanationModal = (mismatch) => {
    setSelectedMismatch(mismatch);
    const defaultType =
      mismatch?.mismatch_type === 'identity_email' || mismatch?.comparison?.email_mismatch
        ? 'referee_work_email'
        : mismatch?.mismatch_type === 'earlier_employer'
          ? 'earlier_employment'
          : '';
    setMismatchExplanationType(defaultType);
    setMismatchExplanationText(mismatch?.existing_explanation?.text || '');
    setShowMismatchExplanationModal(true);
  };

  // Submit mismatch explanation
    // Worker submits replacement referee after rejection
    const handleProvideNewSubmit = async (refNum) => {
      const form = provideNewFormsBySlot[refNum] || BLANK_REFEREE_FORM;
      // CS-R2 double-submit guard: ignore clicks while this slot is already
      // submitting or has been accepted. Data integrity is ALSO protected by the
      // backend (idempotent upsert + declared-slot gate) — this is UX defence.
      const phase = provideNewPhaseBySlot[refNum];
      if (phase === 'submitting' || phase === 'saved') return;

      if (!form.name || !form.email || !form.organisation?.trim()) {
        toast.error('Name, email, and organisation are required');
        return;
      }
      // Note: change_reason ("short note") is optional and must NOT be demanded
      // speculatively. A genuine most-recent-employer mismatch is handled by the
      // backend via slot_reason_type/note and surfaced below with a clear message.
      if (
        form.slot_reason_type === 'other'
        && (form.slot_reason_note || '').trim().length < 10
      ) {
        toast.error('Please add a brief note explaining the selected reason');
        return;
      }
      setProvideNewPhaseBySlot((prev) => ({ ...prev, [refNum]: 'submitting' }));
      try {
        const token = localStorage.getItem('workerToken');
        const res = await axios.post(
          `${API}/worker/references/${refNum}/provide-new`,
          form,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        // Save-through: a genuine employment/referee mismatch does NOT discard the
        // typed details — the backend stored them as a pending draft and returned a
        // calm correct-or-explain prompt. Keep THIS slot's form open with values.
        if (res.data?.status === 'pending_mismatch') {
          setProvideNewMismatchBySlot((prev) => ({
            ...prev,
            [refNum]: { refNum, ...(res.data.mismatch || {}) },
          }));
          restoredDraftSlotsRef.current.add(refNum);
          setProvideNewPhaseBySlot((prev) => ({ ...prev, [refNum]: 'editing' }));
          toast.warning(
            (res.data.mismatch && res.data.mismatch.message)
            || 'We need a quick clarification before sending this reference.'
          );
          fetchDashboard();
          return;
        }
        // Application gate: details are saved as a draft; return to editing.
        if (res.data?.status === 'application_required' || res.data?.requires_application) {
          setProvideNewPhaseBySlot((prev) => ({ ...prev, [refNum]: 'editing' }));
          toast.warning(res.data?.message || 'Please complete your application first.');
          fetchDashboard();
          return;
        }

        const warnings = res.data?.declaration_warnings || [];
        // Employment-history review is a separate recruitment task, not a
        // failed referee submission. Do not alarm the applicant after saving.
        const applicantWarnings = warnings.filter((w) => w?.code !== 'employment_history_pending');
        if (applicantWarnings.length > 0) {
          toast.warning(applicantWarnings.map((w) => w.message || w).join(' '));
        }
        // Server ACCEPTED and PERSISTED the slot -> mark saved (locks the button),
        // clear this slot's mismatch prompt, and confirm to the applicant.
        setProvideNewPhaseBySlot((prev) => ({ ...prev, [refNum]: 'saved' }));
        setProvideNewMismatchBySlot((prev) => {
          const next = { ...prev };
          delete next[refNum];
          return next;
        });
        restoredDraftSlotsRef.current.delete(refNum);
        toast.success(`Referee ${refNum} saved`);

        // Refresh canonical dashboard state in the BACKGROUND (never block the UI
        // on it). Because outreach is now decoupled server-side, this returns
        // quickly and the wizard advances to the canonical next task on its own.
        fetchDashboard();

        // Prompt transition (PHASE 5): derive the next referee slot needing input
        // from the CURRENT dashboard so we do not wait for a refetch. If there is
        // one, open it (blank / its own draft only). Otherwise close and let the
        // canonical next-action logic move the applicant forward (e.g. Right to Work).
        const nextSlot = ((dashboard && dashboard.references) || []).find(
          (r) => r && r.reference_number !== refNum && r.can_provide_new
        );
        window.setTimeout(() => {
          // Drop the just-saved slot's transient form + phase so nothing lingers.
          setProvideNewFormsBySlot((prev) => {
            const next = { ...prev };
            delete next[refNum];
            return next;
          });
          setProvideNewPhaseBySlot((prev) => {
            const next = { ...prev };
            delete next[refNum];
            return next;
          });
          if (nextSlot) {
            openReferenceSlot(nextSlot);
            try {
              const anchor = document.querySelector('[data-testid="references-section"]');
              if (anchor && typeof anchor.scrollIntoView === 'function') {
                anchor.scrollIntoView({ behavior: 'smooth', block: 'start' });
              }
            } catch (_scrollErr) { /* non-fatal */ }
          } else {
            setProvideNewRefNum(null);
          }
        }, 550);
      } catch (err) {
        // Preserve all entered form values (we do NOT clear this slot's form here)
        // and surface a specific, human-readable message even when the backend
        // returns a structured 422 (detail as an array of field errors).
        setProvideNewPhaseBySlot((prev) => ({ ...prev, [refNum]: 'editing' }));
        toast.error(readableErrorDetail(err, 'Failed to submit referee details'));
      }
    };

    // Submit mismatch explanation
  const handleSubmitMismatchExplanation = async () => {
    if (!mismatchExplanationType) {
      toast.error('Please choose a reason for the difference');
      return;
    }
    if (!mismatchExplanationText || mismatchExplanationText.length < 20) {
      toast.error('Please provide a detailed explanation (at least 20 characters)');
      return;
    }
    
    setSubmittingMismatchExplanation(true);
    try {
      const token = localStorage.getItem('workerToken');
      await axios.post(
        `${API}/worker/reference-mismatches/${selectedMismatch.reference_number}/explain`,
        {
          reference_number: selectedMismatch.reference_number,
          explanation_type: mismatchExplanationType,
          explanation_text: mismatchExplanationText
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Explanation submitted! Admin will review.');
      setShowMismatchExplanationModal(false);
      fetchReferenceMismatches();
      fetchDashboard();
    } catch (error) {
      const message = typeof error.response?.data?.detail === 'string' ? error.response.data.detail : 'Failed to submit explanation';
      toast.error(message);
    } finally {
      setSubmittingMismatchExplanation(false);
    }
  };

  // Fetch CV preview for verification
  // CV extraction review/verify is admin-only. No worker-side handlers needed.

  // Handle CV file upload
  const handleCvUpload = async (file) => {
    if (!file) return;

    const readiness = validateCvUploadFile(file);
    if (!readiness.valid) {
      toast.error(readiness.message);
      return;
    }
    
    setUploading('cv');
    const token = localStorage.getItem('workerToken');
    
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      await axios.post(
        `${API}/worker/upload-document/cv`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          }
        }
      );
      
      toast.success('CV uploaded. We will use it as evidence and guide you through any missing dates.');
      await fetchCvStatus();
      await fetchDashboard();
    } catch (error) {
      const message = getApiErrorMessage(error, 'Failed to upload CV');
      toast.error(message);
    } finally {
      setUploading(null);
    }
  };

  // Trigger CV file input
  const triggerCvFileInput = () => {
    const replacementAllowed = workerMayUploadCv(cvStatus);

    // Double-check replacement rules client-side to avoid opening a flow that backend will reject.
    if (cvStatus?.has_cv && !replacementAllowed) {
      toast.error('Your CV is already on file. Wait for admin to request a replacement before uploading a new CV.');
      return;
    }
    
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.pdf,.doc,.docx';
    input.onchange = (e) => {
      const file = e.target.files?.[0];
      if (file) handleCvUpload(file);
    };
    input.click();
  };
  
  // Clean up blob URL when viewer closes or document changes
  const getWorkerBlobRequestConfig = (url, workerToken) => {
    const config = { responseType: 'blob' };
    if (!url) return config;

    try {
      const resolvedUrl = new URL(url, window.location.origin);
      const isSameOriginApi = resolvedUrl.origin === window.location.origin && resolvedUrl.pathname.startsWith('/api/');
      if (isSameOriginApi && workerToken) {
        config.headers = { Authorization: `Bearer ${workerToken}` };
      }
    } catch {
      // If URL parsing fails, fall back to no auth headers.
    }

    return config;
  };

  const fetchWorkerBlob = (url, workerToken) => {
    return axios.get(url, getWorkerBlobRequestConfig(url, workerToken));
  };

  const openDocumentViewer = (doc) => {
    const fetchUrl = resolveDocumentFetchUrl(doc, { apiBase: API });
    if (!fetchUrl || !canViewDocument(doc)) {
      toast.error('Document is not available to view yet');
      return;
    }
    setViewerDocument(doc);
    setViewerFetchUrl(fetchUrl);
    setViewerOpen(true);
  };

  const closeDocumentViewer = () => {
    setViewerFetchUrl(null);
    setViewerDocument(null);
    setViewerOpen(false);
  };

  const downloadAgreement = async (agreement) => {
    const url = agreement?.download_url || agreement?.file_url;
    if (!url) {
      toast.error('Agreement PDF is not available yet');
      return;
    }
    try {
      const token = localStorage.getItem('workerToken');
      const response = await fetchWorkerBlob(url, token);
      const blobUrl = URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = `${agreement.name || 'agreement'}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
    } catch {
      toast.error('Failed to download agreement');
    }
  };

  const handleAcknowledgeAgreement = async (agreement) => {
    try {
      const token = localStorage.getItem('workerToken');
      const response = await axios.post(
        `${API}/worker/agreements/${agreement.id}/acknowledge`,
        {
          signer_name: employee?.name,
          ...(agreement?.source_record_id ? { source_record_id: agreement.source_record_id } : {}),
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const idempotent = Boolean(response?.data?.idempotent);
      toast.success(idempotent ? `${agreement.name} already acknowledged` : `${agreement.name} acknowledged`);
      fetchDashboard();
      return true;
    } catch (error) {
      const payload = error?.response?.data || {};
      const detail = payload?.detail;
      const message =
        (typeof detail === 'string' && detail) ||
        (detail && typeof detail.message === 'string' && detail.message) ||
        (typeof payload?.message === 'string' && payload.message) ||
        'Failed to acknowledge agreement';
      const code =
        (typeof payload?.code === 'string' && payload.code) ||
        (detail && typeof detail === 'object' ? detail.code : null);
      if (error?.response?.status === 409 && (code === 'not_actionable' || code === 'already_acknowledged' || code === 'already_signed')) {
        toast.info(message);
      } else {
        toast.error(message);
      }
      fetchDashboard();
      return false;
    }
  };

  const openHandbookAckModal = (agreement) => {
    if (!agreement?.file_url && !agreement?.download_url) {
      toast.error('Handbook PDF is not available yet. Please try again shortly.');
      return;
    }
    setHandbookAckAgreement(agreement);
    setHandbookPdfViewed(false);
    setHandbookAckConfirmed(false);
    setShowHandbookAckModal(true);
  };

  const closeHandbookAckModal = () => {
    if (submittingHandbookAck) return;
    setShowHandbookAckModal(false);
    setHandbookAckAgreement(null);
    setHandbookPdfViewed(false);
    setHandbookAckConfirmed(false);
  };

  const viewHandbookPdfFromModal = () => {
    if (!handbookAckAgreement) return;
    openDocumentViewer({ ...handbookAckAgreement, name: handbookAckAgreement.name });
    setHandbookPdfViewed(true);
  };

  const submitHandbookAck = async () => {
    if (!handbookAckAgreement) return;
    if (!handbookAckAgreement.file_url && !handbookAckAgreement.download_url) {
      toast.error('Handbook PDF is not available yet. Please try again shortly.');
      return;
    }
    if (!handbookPdfViewed || !handbookAckConfirmed) return;
    setSubmittingHandbookAck(true);
    try {
      const ok = await handleAcknowledgeAgreement(handbookAckAgreement);
      if (ok) {
        setShowHandbookAckModal(false);
        setHandbookAckAgreement(null);
        setHandbookPdfViewed(false);
        setHandbookAckConfirmed(false);
      }
    } finally {
      setSubmittingHandbookAck(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('workerToken');
    localStorage.removeItem('workerEmployee');
    toast.success('Logged out successfully');
    navigate(workerLoginPath());
  };

  const handleSetPassword = async () => {
    if (passwordForm.new_password !== passwordForm.confirm_password) {
      toast.error('Passwords do not match');
      return;
    }
    if (passwordForm.new_password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    if (!/[A-Z]/.test(passwordForm.new_password)) {
      toast.error('Password must contain at least one uppercase letter');
      return;
    }
    if (!/[0-9]/.test(passwordForm.new_password)) {
      toast.error('Password must contain at least one number');
      return;
    }

    setSettingPassword(true);
    const token = localStorage.getItem('workerToken');
    
    try {
      await axios.post(
        `${API}/worker/set-password`,
        {
          new_password: passwordForm.new_password,
          confirm_password: passwordForm.confirm_password,
          current_password: accountStatus.has_password ? passwordForm.current_password : null
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Password set successfully! You can now login with your email and password.');
      setShowSetPasswordModal(false);
      setPasswordForm({ new_password: '', confirm_password: '', current_password: '' });
      setAccountStatus({ ...accountStatus, has_password: true });
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Failed to set password');
    } finally {
      setSettingPassword(false);
    }
  };

  const markNotificationRead = async (notificationId) => {
    const token = localStorage.getItem('workerToken');
    try {
      await axios.post(
        `${API}/worker/notifications/${notificationId}/read`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setNotifications(prev => prev.map(n => 
        n.id === notificationId ? { ...n, read: true } : n
      ));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (err) {
      console.warn('Could not mark notification read:', err);
    }
  };

  const getApiErrorMessage = (error, fallbackMessage) => {
    const detail = error?.response?.data?.detail;

    if (typeof detail === 'string' && detail.trim()) {
      return detail;
    }

    if (Array.isArray(detail) && detail.length > 0) {
      const first = detail[0];
      if (typeof first === 'string' && first.trim()) {
        return first;
      }
      if (first && typeof first === 'object') {
        if (typeof first.msg === 'string' && first.msg.trim()) {
          return first.msg;
        }
        if (typeof first.message === 'string' && first.message.trim()) {
          return first.message;
        }
      }
    }

    if (detail && typeof detail === 'object') {
      if (typeof detail.message === 'string' && detail.message.trim()) {
        return detail.message;
      }
      if (typeof detail.error === 'string' && detail.error.trim()) {
        return detail.error;
      }
    }

    const fallback = error?.response?.data?.message || error?.message;
    return typeof fallback === 'string' && fallback.trim() ? fallback : fallbackMessage;
  };

  const handleFileUpload = async (requirementId, file, slotKey = requirementId) => {
    if (!file) return;
    const isTrainingUpload = requirementId.startsWith('training_');
    if (isTrainingUpload) {
      const extension = file.name?.split('.').pop()?.toLowerCase();
      if (!['pdf', 'jpg', 'jpeg', 'png', 'webp'].includes(extension) || file.size > 10 * 1024 * 1024) {
        const message = 'Choose a PDF, JPG, PNG or WebP certificate under 10 MB. For iPhone HEIC photos, export a JPEG or PDF first.';
        setTrainingUploadFeedback({ requirementId, status: 'error', message });
        toast.error(message);
        return false;
      }
      setTrainingUploadFeedback({ requirementId, status: 'uploading', message: `Uploading ${file.name}… Please keep this page open until you see a receipt.` });
    }
    setUploading(requirementId);
    const token = localStorage.getItem('workerToken');
    
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      await axios.post(
          `${API}/worker/upload-document/${requirementId}`,
          formData,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            }
          }
        );
      
      toast.success('Document submitted. Waiting for admin review.');
      if (isTrainingUpload) {
        setTrainingUploadFeedback({
          requirementId, status: 'success',
          message: 'Certificate received by CareOps. Awaiting Osabea review — it is not verified yet.',
        });
      }
      // Optimistically lock this requirement immediately (server accepted it).
      if (isTrainingUpload) {
        setTrainingUploadReceipts(previous => ({
          ...previous,
          [requirementId]: Date.now(),
        }));
      } else {
        setJustSubmittedDocs((prev) => ({ ...prev, [slotKey]: Date.now() }));
      }
      // Keep the upload state locked until canonical dashboard truth has caught up.
      // This prevents the applicant racing to another step while CareOps is still
      // deciding whether a second document slot is required.
      try {
        await fetchDashboard();
      } catch (_refreshError) {
        // The upload is already saved; a dashboard refresh failure must not
        // produce a false "upload failed" message or invite a duplicate.
      }
      return true;
    } catch (error) {
      const message = isTrainingUpload
        ? formatWorkerTrainingUploadError(error)
        : getApiErrorMessage(error, 'Failed to upload document');
      if (isTrainingUpload) {
        setTrainingUploadFeedback({ requirementId, status: 'error', message });
      }
      toast.error(message);
      return false;
    } finally {
      setUploading(null);
    }
  };

  // Handle multiple file uploads for documents that need both sides (e.g., ID, driving licence)
  const handleMultiFileUpload = async (requirementId, files, slotKey = requirementId) => {
    if (!files || files.length === 0) return;
    
    setUploading(requirementId);
    const token = localStorage.getItem('workerToken');
    
    try {
      // Upload each file with a suffix indicator
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const formData = new FormData();
        formData.append('file', file);
        // Add suffix to indicate which part (front/back)
        const suffix = files.length > 1 ? (i === 0 ? '_front' : '_back') : '';
        
        await axios.post(
            `${API}/worker/upload-document/${requirementId}${suffix}`,
            formData,
            {
              headers: {
                Authorization: `Bearer ${token}`,
              }
            }
          );
      }
      
      toast.success(`${files.length} file${files.length > 1 ? 's' : ''} submitted. Waiting for admin review.`);
      setJustSubmittedDocs((prev) => ({ ...prev, [slotKey]: Date.now() }));
      // Do not release navigation/upload controls until the canonical dashboard
      // has reflected every accepted file and exposed any remaining slot.
      await fetchDashboard();
    } catch (error) {
      const message = getApiErrorMessage(error, 'Failed to upload documents');
      toast.error(message);
    } finally {
      setUploading(null);
    }
  };

  // Document types that require/allow multiple files (front + back)
  const MULTI_FILE_DOC_TYPES = ['identity', 'proof_of_address'];

  const handleSubmitNmcRegistration = async () => {
    const pin = nmcPin.trim();
    if (!pin) {
      toast.error('Please enter your NMC PIN');
      return;
    }

    setSubmittingNmc(true);
    const token = localStorage.getItem('workerToken');
    try {
      await axios.post(
        `${API}/worker/professional-registration`,
        {
          registration_number: pin,
          registration_expiry_date: nmcExpiry || null,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`NMC PIN submitted. ${providerName} will verify your registration.`);
      setShowNmcDialog(false);
      setNmcPin('');
      setNmcExpiry('');
      fetchDashboard();
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to submit NMC registration'));
    } finally {
      setSubmittingNmc(false);
    }
  };

  const triggerFileInput = (requirementId) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.pdf,.jpg,.jpeg,.png,.webp';
    
    // Allow multiple files for ID documents
    if (MULTI_FILE_DOC_TYPES.includes(requirementId)) {
      input.multiple = true;
    }
    
    input.onchange = (e) => {
      const files = e.target.files;
      if (files && files.length > 0) {
        if (files.length > 1) {
          handleMultiFileUpload(requirementId, Array.from(files));
        } else {
          handleFileUpload(requirementId, files[0]);
        }
      }
    };
    input.click();
  };

  if (loading) {
    const loadingContent = getWorkerDashboardLoadingContent(location.state);
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#F7FBFB] via-white to-[#EEF7F6] flex items-center justify-center px-5">
        <div className="w-full max-w-sm rounded-3xl border border-[#D7E7E5] bg-white/90 p-6 text-center shadow-xl shadow-teal-900/5">
          <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-[#E7F4F2] text-[#0F5C5E]">
            <Loader2 className="h-7 w-7 animate-spin" />
          </div>
          <p className="text-xs font-semibold uppercase tracking-[0.16em] text-[#0F5C5E]">{loadingContent.eyebrow}</p>
          <h1 className="mt-2 text-xl font-semibold text-slate-950">{loadingContent.title}</h1>
          <p className="mt-2 text-sm leading-6 text-slate-600">{loadingContent.description}</p>
        </div>
      </div>
    );
  }

  if (!dashboard) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center space-y-4">
          <AlertCircle className="h-10 w-10 text-red-400 mx-auto" />
          <p className="text-slate-600" role="alert">{dashboardError || 'We couldn’t open your dashboard. Please try again.'}</p>
          <Button onClick={fetchDashboard} className="gap-2">
            <RefreshCw className="h-4 w-4" />
            Try Again
          </Button>
        </div>
      </div>
    );
  }

  const {
    employee = {},
    progress = { percentage: 0, completed: 0, required: 0 },
    forms: _forms,
    interview_form: _interview_form,
    interview_appointment: _interview_appointment,
    interview_state: _interview_state,
    interview_booking_slots: _interview_booking_slots,
    missing_documents: _missing_documents,
    missing_trainings: _missing_trainings,
    completed_documents: _completed_documents,
    completed_trainings: _completed_trainings,
    expired_trainings: _expired_trainings,
    all_mandatory_trainings: _all_mandatory_trainings,
    recommended_trainings: _recommended_trainings,
    alerts: _alerts,
    contract_signed = false,
    unified_blockers: _unified_blockers,
    legal_blockers: _legal_blockers,
    internal_blockers: _internal_blockers,
    employment_readiness,
    employment_readiness_label,
    employment_readiness_blockers: _employment_readiness_blockers,
    professional_registration,
    references: _references,
    induction,
    competency_assessments,
    spot_checks,
    supervisions: _supervisions,
    appraisals: _appraisals,
    recurring_compliance_summary: _recurring_compliance_summary,
    agreements: _agreements,
    assigned_policies: _assigned_policies,
    worker_tasks: _worker_tasks,
    next_action: _next_action,
    onboarding_wizard: _onboarding_wizard,
    employment_gap_evaluation: _employment_gap_evaluation,
    employment_integrity: _employment_integrity,
    applicant_declaration: _applicant_declaration,
    cv_assessment: _cv_assessment,
  } = dashboard;

  // Safe array defaults — prevent .length / .map / .filter on undefined
  const forms = _forms || [];
  const interview_form = _interview_form || null;
  const interview_appointment = _interview_appointment || null;
  const interview_state = _interview_state || null;
  const interview_booking_slots = _interview_booking_slots || [];
  const missing_documents = _missing_documents || [];
  const visibleMissingDocuments = missing_documents.filter((doc) => !isCvDocumentRequirement(doc));
  const missing_trainings = _missing_trainings || [];
  const completed_documents = _completed_documents || [];
  const completed_trainings = _completed_trainings || [];
  const expired_trainings = _expired_trainings || [];
  const all_mandatory_trainings = _all_mandatory_trainings || [];
  const recommended_trainings = _recommended_trainings || [];
  const alerts = _alerts || [];
  const complianceDueAlerts = buildComplianceDueAlerts(alerts);
  const references = _references || [];
  const supervisions = _supervisions || [];
  const appraisals = _appraisals || [];
  const agreements = Array.isArray(_agreements) ? _agreements : [];
  const assignedPolicies = Array.isArray(_assigned_policies) ? _assigned_policies : [];
  const operationalAgreements = agreements
    .filter((agreement) => agreement?.latest_active !== false)
    .filter((agreement, idx, arr) => arr.findIndex((x) => x?.id === agreement?.id) === idx);
  const worker_tasks = _worker_tasks || [];
  const employmentIntegrity = _employment_integrity || null;
  const canonicalNextAction = _next_action || null;
  const recurring_compliance_summary = _recurring_compliance_summary || { total: 0, overdue: 0, due: 0, upcoming: 0, scheduled: 0, preview: [] };
  const recurringItems = recurring_compliance_summary.items || recurring_compliance_summary.preview || [];
  const recurringItemsByStatus = {
    overdue: recurringItems.filter((item) => item?.computed_status === 'overdue'),
    due: recurringItems.filter((item) => item?.computed_status === 'due'),
    upcoming: recurringItems.filter((item) => item?.computed_status === 'upcoming'),
    scheduled: recurringItems.filter((item) => item?.computed_status === 'scheduled'),
  };
  const unifiedBlockers = _unified_blockers || [];
  const legalBlockers = _legal_blockers || [];
  const internalBlockers = _internal_blockers || [];
  const canonicalBlockers = [...unifiedBlockers, ...legalBlockers, ...internalBlockers];
  const competencyRecurringItems = recurringItems.filter((item) => item?.item_type === 'competency_assessment');
  const competencyRecurringCounts = {
    overdue: competencyRecurringItems.filter((item) => item?.computed_status === 'overdue').length,
    due: competencyRecurringItems.filter((item) => item?.computed_status === 'due').length,
    upcoming: competencyRecurringItems.filter((item) => item?.computed_status === 'upcoming').length,
  };
  const employment_readiness_blockers = _employment_readiness_blockers || [];
  const hasCanonicalBlockerMatch = (keys, category) => {
    const canonicalMatch = canonicalBlockers.some((blocker) => {
      const blockerId = normalizeBlockerValue(blocker?.id);
      const blockerGate = normalizeBlockerValue(blocker?.gate);
      const blockerCategory = normalizeBlockerValue(blocker?.category);
      const blockerClass = normalizeBlockerValue(blocker?.blocker_class);
      return (
        keys.has(blockerId) ||
        keys.has(blockerGate) ||
        blockerCategory === normalizeBlockerValue(category) ||
        blockerClass === normalizeBlockerValue(category)
      );
    });
    if (canonicalMatch) return true;
    return employment_readiness_blockers.some((blocker) => keys.has(normalizeBlockerValue(blocker?.type)));
  };

  const referencesNeedActionFromBlockers = hasCanonicalBlockerMatch(BLOCKER_KEYS_REFERENCES, 'references');
  const latestContractAgreement = getLatestActiveContract(operationalAgreements, { contractEligibility });
  const contractAgreement = latestContractAgreement || operationalAgreements.find((agreement) => agreement.id === 'contract_acceptance');
  
  const activeWorkforceCapability = dashboard?.capabilities?.active_workforce;
  const isActiveEmployee =
    typeof activeWorkforceCapability === 'boolean'
      ? activeWorkforceCapability
      : (
          typeof dashboard?.is_active_employee === 'boolean'
            ? dashboard.is_active_employee
            : (
                employee?.is_active_employee ||
                isActiveLifecycleStatus(employee?.employee_status) ||
                isActiveLifecycleStatus(employee?.status)
              )
        );
  const canonicalStage = getCanonicalPersonStage(employee);
  const isPreEmploymentEmployee =
    !isActiveEmployee && (
      canonicalStage === 'employee' ||
      employee?.is_approved ||
      employee?.recruitment_approved ||
      normalizeLifecycleStatus(employee?.employee_status) === 'onboarding' ||
      normalizeLifecycleStatus(employee?.status) === 'onboarding' ||
      employee?.status === 'READY' ||
      contract_signed
    );
  const lifecycleStage = isActiveEmployee
    ? 'active'
    : isPreEmploymentEmployee
      ? 'pre_employment'
      : 'recruitment';
  // Active-employee dashboard split — Tier 4. Olumide-style "Ready for
  // Work" workers have a 30-section onboarding archive that should be
  // tucked away. The "Today" tab keeps live items (shifts, obligations,
  // alerts, incidents); the "Staff File" tab holds the historic record
  // (forms, documents, employment history, training, agreements, etc.).
  // Applicant Home never mounts the historic ledger or the 30-section staff
  // file. The existing detailed step journey remains accessible under My tasks.
  const showFileSections = !isActiveEmployee ? applicantView === 'tasks' : workerMainView === 'file';
  const showTodaySections = !isActiveEmployee ? applicantView === 'tasks' : workerMainView === 'today';
  const onboarding_wizard = _onboarding_wizard || null;
  const onboarding_sync = dashboard?.onboarding_sync || null;

  // Sprint 1A: surface the applicant declaration repair task on the applicant's
  // OWN dashboard using the shared next-action logic (single source of truth).
  // Reuses deriveApplicantNextAction ordering + the backend declaration state;
  // does not duplicate readiness rules or create a second source of truth.
  const applicantDeclarationState = _applicant_declaration || null;
  const applicantNextActionResolved = presentApplicantNextAction(
    deriveApplicantNextAction({
      applicationOnFile: dashboard?.application_on_file?.on_file !== false,
      cvFileExists: !!_cv_assessment?.on_file,
      cvApproved: !!_cv_assessment?.admin_approved,
      cvLinkedForReview: !!(_cv_assessment?.recruitment_locked || _cv_assessment?.admin_approved),
      cvReviewReady: !!_cv_assessment?.on_file,
      declarationsOnFile: !!applicantDeclarationState?.on_file,
      declarationsAdequatelyReviewed: !!applicantDeclarationState?.reviewed,
      employmentIntegrity: _employment_integrity,
      employmentGapEvaluation: _employment_gap_evaluation,
      employmentSignedOff: false,
    }),
    'worker',
  );
  const declarationRepairRoute = applicantDeclarationState?.action_route || '/worker/forms/applicant_declarations';
  // Blocking prerequisite: show only when declarations are genuinely missing (or
  // an admin-requested correction is pending) and it is the current applicant
  // action — so it outranks later onboarding tasks. Never for active employees.
  const showDeclarationRepairTask =
    !isActiveEmployee &&
    !!applicantDeclarationState &&
    !applicantDeclarationState.on_file &&
    (applicantNextActionResolved?.key === 'declarations_missing' || applicantDeclarationState.amendment_pending);
  const pendingWorkerActions = onboarding_wizard?.pending_worker_actions || dashboard?.pending_worker_actions || [];
  const gapsNeedAction = deriveEmploymentGapsNeedAction({
    gapEvaluation: _employment_gap_evaluation,
    employmentReadinessBlockers: employment_readiness_blockers,
    pendingWorkerActions,
  });
  const wizardEnabled = Boolean(onboarding_wizard?.enabled) && !isActiveEmployee;
  // A manual choice of an eligible server-owned step must stay selected.
  // Forced tasks remain visible separately, and backend compliance gates still
  // determine what can be submitted, approved or unlocked.
  const selectedWizardStep = wizardEnabled && (onboarding_wizard?.steps || []).some(
    step => step.id === wizardStepOverride && step.status !== 'locked'
  ) ? wizardStepOverride : null;
  const activeWizardStepId = selectedWizardStep
    || onboarding_wizard?.forced_step_id
    || onboarding_wizard?.current_step_id
    || 'profile_checks';
  const onboardingWizardForDisplay = wizardEnabled
    ? {
        ...onboarding_wizard,
        current_step_id: activeWizardStepId,
        steps: (onboarding_wizard?.steps || []).map((step) => {
          const status = String(step.status || '').toLowerCase();
          const isChecking = status === 'paused' || status === 'awaiting_review';
          return {
            ...step,
            is_current: step.id === activeWizardStepId,
            // Replace any backend "wait for admin / sit tight" pause copy with a
            // reassuring, non-blocking message so the guided panel never reads
            // like a hard stop.
            ...(isChecking
              ? { pause_message: sanitizeApplicantWaitCopy(step.pause_message, APPLICANT_CHECKING_PANEL_MESSAGE) }
              : {}),
          };
        }),
      }
    : onboarding_wizard;
  const applicantJourneyModel = wizardEnabled
    ? buildApplicantJourneyModel(onboarding_wizard)
    : null;
  const forcedWizardAction = onboarding_wizard?.forced_action || null;
  const employmentHistoryNeedsAction = Boolean(
    isEmploymentHistoryAction(canonicalNextAction)
    || isEmploymentHistoryAction(forcedWizardAction)
    || pendingWorkerActions.some(isEmploymentHistoryAction)
    || employment_readiness_blockers.some(isEmploymentHistoryAction)
  );
  const wizardShowsStep = (stepId) => !wizardEnabled || activeWizardStepId === stepId;
  const trainingTaskClosed = wizardEnabled && activeWizardStepId === 'mandatory_training'
    && openedWizardTask !== 'mandatory_training';
  // CS-R2 A-3R: a booked interview must ALWAYS be visible to the applicant,
  // independent of the guided wizard step. After self-booking, the interview
  // wizard step flips to 'paused' (current_step -> profile_checks), so keying
  // appointment visibility off wizardShowsStep('interview') could hide the
  // just-created appointment. Derive it purely from the appointment state.
  const hasScheduledInterview = Boolean(
    interview_appointment
    && ['scheduled', 'confirmed'].includes(String(interview_appointment.status || '').toLowerCase())
    // C3F: a past active appointment must NOT render as an Upcoming interview.
    && interview_appointment.is_past !== true
  );

  // C3F canonical projection bug fix: the applicant interview surface is driven
  // by the canonical `interview_state` (via the tested resolver), NOT solely by
  // whether the onboarding wizard's active step happens to be 'interview'. The
  // resolver guarantees exactly ONE surface renders: 'appointment' (upcoming
  // scheduled interview) XOR 'journey' (all other applicant-visible states).
  const interviewSurface = resolveInterviewSurface({
    isActiveEmployee,
    interviewState: interview_state,
    hasScheduledInterview,
    wizardShowsInterview: wizardShowsStep('interview'),
  });
  const rejectedIdentityDocuments = visibleMissingDocuments.filter((doc) => doc.rejection);
  const isPoaDoc = (doc) => (
    String(doc?.type || '').startsWith('proof_of_address') || Boolean(doc?.poa_upload_needed)
  );
  const isIdentityDoc = (doc) => String(doc?.type || '') === 'identity';
  const poaMissingSlots = visibleMissingDocuments.filter(isPoaDoc);
  const nonPoaMissingSlots = visibleMissingDocuments.filter((doc) => !isPoaDoc(doc));
  // Which identity document the guided step should show first. A task the
  // applicant tapped (?document=dbs) wins; otherwise the server's own forced
  // action. All of these documents are already uploadable in parallel, so this
  // only changes which one is on screen — never which gates apply.
  const preferredIdentityDocType = resolvePreferredIdentityDocType({
    documentParam: searchParams?.get('document'),
    forcedAction: forcedWizardAction,
  });
  const guidedIdentityDocuments = wizardEnabled
    ? selectGuidedIdentityDocuments({
      rejectedDocuments: rejectedIdentityDocuments,
      nonPoaMissingSlots,
      poaMissingSlots,
      preferredType: preferredIdentityDocType,
    })
    : visibleMissingDocuments;
  const identityDocumentsForDisplay = wizardEnabled
    ? guidedIdentityDocuments
    : visibleMissingDocuments;
  const nmcNeedsWorkerAction = Boolean(
    professional_registration
    && professional_registration.status !== 'verified'
    && (
      !professional_registration.number
      || !professional_registration.certificate_uploaded
    ),
  );
  const showNmcInWizard = wizardEnabled
    && activeWizardStepId === 'identity_documents'
    && guidedIdentityDocuments.length === 0
    && nmcNeedsWorkerAction;
  const showOnboardingContractSection = false;
  const handbookAgreement = getLatestActiveAgreementById(operationalAgreements, 'handbook_acknowledgement')
    || getLatestActiveAgreementById(operationalAgreements, 'employee_handbook_acknowledgement')
    || operationalAgreements.find((agreement) => agreement.id === 'handbook_acknowledgement')
    || operationalAgreements.find((agreement) => agreement.id === 'employee_handbook_acknowledgement');
  const effectiveContractEligibility = contractEligibility || {
    can_sign: Boolean(contractAgreement?.can_sign),
    blockers: Array.isArray(contractAgreement?.signing_gate_blockers) ? contractAgreement.signing_gate_blockers : [],
    reason: contractAgreement?.signing_gate_reason || null,
  };
  const contractDisplay = getAgreementDisplay(contractAgreement, { contractEligibility: effectiveContractEligibility });
  const latestContractState = resolveLatestContractState(contractAgreement, { contractEligibility: effectiveContractEligibility });
  const contractLifecycleStatus = latestContractState.status;
  const effectiveContractCanSign = latestContractState.canSign;
  const hasPendingSignableContract = latestContractState.hasPendingSignableContract;
  const contractNeedsReissueMessage = !hasPendingSignableContract && (
    ['rejected', 'superseded', 'action_required'].includes(contractLifecycleStatus) ||
    contractLifecycleStatus !== 'pending_signature'
  );
  const truncateAgreementCardText = (value, max = 160) => {
    const text = String(value || '').trim();
    if (!text) return '';
    if (text.length <= max) return text;
    return `${text.slice(0, max - 1).trimEnd()}…`;
  };
  const handbookDisplay = getAgreementDisplay(handbookAgreement, { contractEligibility });
  const cvDisplay = getCvDisplay(cvStatus);
  const unresolvedCvNotifications = getUnresolvedCvNotifications(notifications);
  const cvNeedsWorkerAction = Boolean(
    applicantCvNeedsAction(cvStatus, { unresolvedNotifications: unresolvedCvNotifications })
    || (forcedWizardAction && isCvActionKey(forcedWizardAction.key)),
  );
  const showApplicantCvSection = Boolean(
    cvStatus && applicantCvNeedsAction(cvStatus, { unresolvedNotifications: unresolvedCvNotifications }),
  );
  const trainingDisplay = getTrainingDisplay({
    missingTrainings: missing_trainings,
    expiredTrainings: expired_trainings,
    allMandatoryTrainings: all_mandatory_trainings,
  });
  // Only actionable certificates belong in the guided training step. Fully
  // verified ones remain available in My Records instead of creating a ledger.
  const actionableTrainings = all_mandatory_trainings.filter((training) => (
    (TRAINING_STATUS_CONFIG[training.status] || TRAINING_STATUS_CONFIG.missing).showUpload
  ));
  const completedReferencesCount = references.filter((reference) => isReferenceComplete(reference?.status)).length;
  const referencesProgressSummary = getReferencesProgressSummary(references);
  const onboardingWizardComplete = Boolean(onboarding_wizard?.onboarding_complete);
  const referencesNeedAction = referencesNeedActionFromBlockers || references.some((reference) => !isReferenceComplete(reference?.status));
  const mismatchByRefNum = new Map(
    ((referenceMismatches?.mismatches || []).map((m) => [Number(m?.reference_number), m]))
  );
  const referencesRequiringWorkerInput = references.filter(
    (ref) => referenceNeedsWorkerAction(ref, mismatchByRefNum),
  );
  const profileChecksTask = wizardEnabled && activeWizardStepId === 'profile_checks'
    ? resolveProfileChecksTask({
        applicationOnFile: dashboard?.application_on_file?.on_file !== false,
        applicationStatus: dashboard?.application_on_file?.status,
        cvNeedsWorkerAction,
        cvDisplay,
        forms,
        references,
        mismatchByRefNum,
        gapsNeedAction,
        employmentHistoryNeedsAction,
        isFormWorkerActionRequired,
      })
    : null;
  const profileChecksFocus = profileChecksTask?.focus ?? null;
  // ER-S1R: the application is the FIRST recruitment gate. Whenever it is not on
  // file, it must be the top next-action REGARDLESS of the wizard's current step
  // (otherwise CV/document steps can hide a recoverable/review application). For
  // possible_historical this resolves to a calm, button-less waiting card.
  const applicationGateState = dashboard?.application_on_file || {};
  const applicationGateActive = applicationGateState.on_file === false;
  const applicationGateAction = applicationGateActive
    ? (() => {
        const t = resolveProfileChecksTask({
          applicationOnFile: false,
          applicationStatus: applicationGateState.status,
          cvNeedsWorkerAction,
          cvDisplay,
          forms,
          references,
          mismatchByRefNum,
          gapsNeedAction,
          employmentHistoryNeedsAction,
          isFormWorkerActionRequired,
        });
        if (!t || t.focus !== 'application') return null;
        return {
          key: t.actionKey,
          title: t.title,
          description: t.description,
          subtitle: t.subtitle,
          primaryLabel: t.primaryLabel,
          route: t.route,
          owner: t.owner,
          level: 'critical',
        };
      })()
    : null;
  const applicantHomePrimaryAction = wizardEnabled
    ? resolveApplicantHomePrimaryAction({ applicationGateAction, forcedAction: forcedWizardAction })
    : null;
  const profileChecksRepairTask = wizardEnabled
    ? resolveProfileChecksRepairTask({
        cvNeedsWorkerAction,
        cvDisplay,
        gapsNeedAction,
        employmentHistoryNeedsAction,
      })
    : null;
  const showProfileChecksSectionContent = showFileSections && wizardShowsStep('profile_checks');
  const employmentIntegrityState = String(employmentIntegrity?.state || '').toLowerCase();
  const showEmploymentIntegritySection = Boolean(
    showProfileChecksSectionContent
    && employmentIntegrity
    && !['', 'not_ready'].includes(employmentIntegrityState)
  );
  const showEmploymentGapsRepairSection = Boolean(
    showProfileChecksSectionContent
    && (!wizardEnabled || profileChecksFocus === 'gaps' || profileChecksFocus === 'employment_history' || profileChecksFocus === 'cv' || gapsNeedAction || employmentHistoryNeedsAction)
  );
  const showApplicantCvRepairSection = Boolean(
    showProfileChecksSectionContent
    && showApplicantCvSection
    && (!wizardEnabled || profileChecksFocus === 'cv' || cvNeedsWorkerAction)
  );
  const activeReferenceForWizard = profileChecksTask?.referenceNumber != null
    ? referencesRequiringWorkerInput.find(
      (ref) => Number(ref.reference_number) === Number(profileChecksTask.referenceNumber),
    ) || referencesRequiringWorkerInput[0]
    : referencesRequiringWorkerInput[0];
  const normalizedAgreements = [
    ...operationalAgreements.filter((agreement) => agreement.id !== 'contract_acceptance' && agreement.id !== 'handbook_acknowledgement' && agreement.id !== 'employee_handbook_acknowledgement'),
    ...(handbookAgreement ? [handbookAgreement] : []),
    ...(contractAgreement ? [contractAgreement] : []),
  ].filter((agreement, idx, arr) => arr.findIndex((x) => x?.id === agreement?.id) === idx);
  const agreementDisplays = normalizedAgreements.map((agreement) => ({
    agreement,
    display: getAgreementDisplay(agreement, { contractEligibility: effectiveContractEligibility }),
  }));
  const agreementsActionRequiredCount = agreementDisplays.filter(({ display }) => display.tone === 'critical').length;
  const agreementsCompletedCount = agreementDisplays.filter(({ display }) => display.tone === 'success').length;
  const agreementsHasInProgressState = agreementDisplays.some(({ display }) => display.tone === 'info');
  const activeTrainingExpiringSoon = alerts.filter((entry) => entry?.type === 'training').length;
  const completedCompetencyCount = (competency_assessments || []).filter(
    (item) => item?.outcome === 'pass' || item?.status === 'completed'
  ).length;

  const completedSupervisionCount = (supervisions || []).filter(
    (item) => (item?.status || '').toLowerCase() === 'completed'
  ).length;

  const openWorkerAppraisalRecord = async (cycleId) => {
    if (!cycleId) return;
    setAppraisalPdfLoading(cycleId);
    try {
      const token = localStorage.getItem('workerToken');
      const response = await axios.get(
        `${API}/worker/appraisals/${cycleId}/completed-record/download-pdf`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob',
        },
      );
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank', 'noopener,noreferrer');
      setTimeout(() => window.URL.revokeObjectURL(url), 60_000);
    } catch (error) {
      toast.error('Your appraisal record is not available yet');
    } finally {
      setAppraisalPdfLoading(null);
    }
  };

  const pendingWorkerTasks = worker_tasks.filter((task) => String(task?.status || '').toLowerCase() === 'pending');
  const hasNonContractCriticalBlockers = (Array.isArray(legalBlockers) ? legalBlockers : []).some((b) => {
    const gate = String(b?.gate || b?.id || '').toLowerCase();
    const severity = String(b?.severity || '').toLowerCase();
    const isContract = gate.includes('contract');
    return !isContract && severity === 'critical';
  });
  const hasReferenceOrGapBlockers = (Array.isArray(legalBlockers) ? legalBlockers : []).some((b) => {
    const gate = String(b?.gate || b?.id || '').toLowerCase();
    return gate.includes('reference') || gate.includes('employment_gap') || gate.includes('employment history');
  });
  const mapWorkerTaskToNextAction = (task) => {
    if (!task) return null;
    const taskKey = String(task?.key || task?.type || '').toLowerCase();
    const taskTitle = String(task?.title || '').toLowerCase();
    const looksLikeContractTask =
      taskKey.includes('contract') ||
      taskTitle.includes('contract') ||
      taskTitle.includes('sign new contract');
    if (looksLikeContractTask && (!effectiveContractCanSign || hasNonContractCriticalBlockers || hasReferenceOrGapBlockers)) {
      return null;
    }
    return {
      key: task.key || task.type || 'task',
      title: task.title || 'Action required',
      description: task.description || 'Open this task to continue your onboarding.',
      primaryLabel: task.primary_label || task.primaryLabel || 'Open task',
      route: task.action_route || '#training',
      level: task.blocking_readiness ? 'critical' : 'high',
    };
  };
  // Synthesize a fallback NextAction from pending agreements (Tier 4 fix).
  // Without this the NextActionCard rendered "You're all caught up" for
  // applicants who still had to sign a contract — contradicting the
  // Readiness Checklist directly above it. Trust the agreement payload so
  // the next-action surface NEVER tells a lie.
  const synthesizeAgreementNextAction = () => {
    const allAgreements = Array.isArray(operationalAgreements) ? operationalAgreements : [];
    const agreementKey = (a) => String(a?.id || a?.agreement_type || a?.key || '').toLowerCase();
    // Contract: actionable when the worker can sign right now.
    const pendingContract = allAgreements.find((a) => {
      if (agreementKey(a) !== 'contract_acceptance') return false;
      const status = String(a?.status || '').toLowerCase();
      // Tier 4 sync fix: same logic as handbook — worker has no further
      // action if the contract is signed/awaiting review/verified.
      const verified = a?.verified === true
        || status === 'verified'
        || status === 'completed'
        || status === 'fully_executed'
        || status === 'signed'
        || status === 'acknowledged'
        || status === 'submitted'
        || status === 'awaiting_review';
      const canSignNow = (a?.can_sign === true) || (effectiveContractEligibility?.can_sign === true);
      return !verified && !hasNonContractCriticalBlockers && (canSignNow || a?.lifecycle_status === 'awaiting_worker_signature');
    });
    if (pendingContract) {
      return {
        key: 'sign_contract',
        title: 'Sign your employment contract',
        description: 'Review the contract PDF, then add your signature to complete onboarding.',
        primaryLabel: 'Review & sign contract',
        route: '#agreements-section',
        level: 'critical',
      };
    }
    // Contract locked: show pre-sign progress so the worker knows what's
    // still outstanding (vs being told "all caught up").
    const lockedContract = allAgreements.find((a) => {
      if (agreementKey(a) !== 'contract_acceptance') return false;
      const status = String(a?.status || a?.lifecycle_status || '').toLowerCase();
      const isTerminal = ['verified', 'completed', 'fully_executed', 'signed', 'acknowledged', 'submitted', 'awaiting_review'].includes(status);
      if (isTerminal) return false;
      if (a?.contract_signing_unlocked === false) return true;
      // Safety net: if eligibility explicitly says cannot sign and contract is still pending,
      // never allow "all caught up" to render.
      return effectiveContractEligibility?.can_sign === false && (status === 'pending_signature' || status === 'awaiting_worker_signature');
    });
    if (lockedContract) {
      const blockerCount = Array.isArray(lockedContract.contract_signing_blockers)
        ? lockedContract.contract_signing_blockers.length
        : 0;
      const hasReferenceOrGapBlockers = (Array.isArray(legalBlockers) ? legalBlockers : []).some((b) => {
        const gate = String(b?.gate || b?.id || '').toLowerCase();
        return gate.includes('reference') || gate.includes('employment_gap');
      });
      return {
        key: 'contract_locked',
        title: hasReferenceOrGapBlockers ? 'Resolve references/history first' : 'Contract awaiting earlier steps',
        description: hasReferenceOrGapBlockers
          ? 'Please resolve your reference and employment-history actions first. Your contract will unlock automatically once those are reviewed.'
          : (blockerCount > 0
            ? `${blockerCount} item${blockerCount === 1 ? '' : 's'} still needed before your contract can be signed.`
            : 'Your contract will unlock once the remaining onboarding checks are complete.'),
        primaryLabel: hasReferenceOrGapBlockers ? 'Review blockers' : 'See what\'s outstanding',
        route: hasReferenceOrGapBlockers ? '#checks-card' : '#agreements-section',
        level: 'high',
      };
    }
    // Handbook: actionable when worker hasn't yet acknowledged it.
    const pendingHandbook = allAgreements.find((a) => {
      const k = agreementKey(a);
      if (k !== 'handbook_acknowledgement' && k !== 'employee_handbook_acknowledgement') return false;
      const status = String(a?.status || '').toLowerCase();
      // Tier 4 sync fix: treat signed/acknowledged/submitted as "done for
      // the worker" even when admin hasn't yet verified — the worker has
      // no further action at that point, admin does.
      const verified = a?.verified === true
        || status === 'verified'
        || status === 'completed'
        || status === 'acknowledged'
        || status === 'signed'
        || status === 'submitted'
        || status === 'awaiting_review';
      return !verified;
    });
    if (pendingHandbook) {
      return {
        key: 'acknowledge_handbook',
        title: 'Acknowledge the Employee Handbook',
        description: 'Review the handbook PDF, then confirm you have read and understood it.',
        primaryLabel: 'Review & acknowledge',
        route: '#agreements-section',
        level: 'high',
      };
    }
    return null;
  };
  const actionableWorkerTasks = pendingWorkerTasks
    .map((task) => ({ task, mapped: mapWorkerTaskToNextAction(task) }))
    .filter((x) => Boolean(x.mapped))
    .filter(({ mapped }) => !actionDuplicatesPrimarySurface(mapped.key, null, forcedWizardAction))
    .filter(({ mapped }) => !(wizardEnabled && cvNeedsWorkerAction && isCvActionKey(mapped.key)));
  const canonicalNextActionMapped = (() => {
    if (!(canonicalNextAction && canonicalNextAction.key)) return null;
    const taskLike = {
      key: canonicalNextAction.key,
      type: canonicalNextAction.key,
      title: canonicalNextAction.title,
      description: canonicalNextAction.description,
      action_route: canonicalNextAction.route,
      primary_label: canonicalNextAction.primary_label || canonicalNextAction.primaryLabel,
      blocking_readiness: canonicalNextAction.level === 'critical',
      status: 'pending',
    };
    return mapWorkerTaskToNextAction(taskLike);
  })();

  const synthesizeWizardNextAction = () => {
    if (!wizardEnabled) return null;
    const stepId = activeWizardStepId;
    const currentWizardStep = (onboarding_wizard?.steps || []).find((s) => s.id === stepId);

    if (forcedWizardAction) {
      const routeByStep = {
        profile_checks: '#documents-card',
        identity_documents: '#missing-documents-section',
        interview: '#interview-step',
        mandatory_training: '#training-card',
        care_certificate: '#induction',
        agreements: '#agreements-contract',
      };
      return {
        key: forcedWizardAction.key,
        title: forcedWizardAction.title,
        description: forcedWizardAction.description,
        primaryLabel: forcedWizardAction.primary_label
          || (forcedWizardAction.is_replacement ? 'Upload replacement' : 'Continue'),
        route: forcedWizardAction.route || routeByStep[forcedWizardAction.wizard_step_id] || '#refresh',
        level: 'critical',
      };
    }

    if (stepId !== 'profile_checks' && profileChecksRepairTask?.focus) {
      return {
        key: `profile_repair_${profileChecksRepairTask.actionKey}`,
        title: profileChecksRepairTask.title,
        description: profileChecksRepairTask.description,
        primaryLabel: profileChecksRepairTask.primaryLabel,
        route: profileChecksRepairTask.route,
        level: 'critical',
      };
    }

    if (stepId === 'profile_checks') {
      if (profileChecksTask?.focus) {
        return {
          key: profileChecksTask.actionKey,
          title: profileChecksTask.title,
          description: profileChecksTask.description,
          primaryLabel: profileChecksTask.primaryLabel,
          route: profileChecksTask.route,
          formId: profileChecksTask.formId,
          level: 'critical',
        };
      }
      if (currentWizardStep?.is_paused) {
        return {
          key: 'profile_waiting',
          title: `Waiting for ${providerName} review`,
          description: 'You have completed everything for this step. We will email you when you can continue.',
          primaryLabel: 'Refresh status',
          route: '#refresh',
          level: 'medium',
        };
      }
    }

    if (stepId === 'interview') {
      // C3F.1: once the interview has concluded from the applicant's side
      // (awaiting_outcome/under_review/successful/unsuccessful), NO interview
      // next action may be projected. Defer to the genuine current recruitment
      // step so we never contradict the canonical "Interview successful/…"
      // surface (rules 2 & 4).
      if (isInterviewStepConcluded(interview_state)) {
        return null;
      }
      if (interview_form && isFormWorkerActionRequired(interview_form.status)) {
        const inProgress = String(interview_form.status || '').toLowerCase() === 'in_progress';
        return {
          key: 'form_pre_interview_questionnaire',
          title: inProgress ? 'Continue Interview Questionnaire' : 'Start Interview Questionnaire',
          description: 'Answer the role-specific questions before your interview.',
          primaryLabel: inProgress ? 'Continue questionnaire' : 'Start questionnaire',
          route: '#interview-step',
          formId: interview_form.id,
          level: 'critical',
        };
      }
      if (!interview_appointment && interview_booking_slots.length > 0 && interview_form) {
        return {
          key: 'book_interview_slot',
          title: 'Book your video interview',
          description: 'Choose an available slot below. You will verify original documents on the call.',
          primaryLabel: 'View available slots',
          route: '#interview-step',
          level: 'critical',
        };
      }
      // C3F.1: only project a join action for a genuinely upcoming (active,
      // not-past) appointment whose canonical state is scheduled/join_today —
      // never merely because an appointment record still carries a video link
      // (rules 1 & 3).
      if (canProjectJoinInterviewAction({ interviewState: interview_state, appointment: interview_appointment })) {
        return {
          key: 'join_interview_video',
          title: 'Join your video interview',
          description: `Scheduled for ${interview_appointment.scheduled_at ? new Date(interview_appointment.scheduled_at).toLocaleString('en-GB') : 'soon'}.`,
          primaryLabel: 'Open video link',
          route: interview_appointment.video_meeting_link,
          level: 'critical',
        };
      }
      if (currentWizardStep?.is_paused) {
        return {
          key: 'interview_waiting',
          title: 'Waiting for interview',
          description: interview_appointment
            ? 'Your interview is scheduled — see details on this step.'
            : 'Our recruitment team is reviewing your questionnaire or scheduling your interview.',
          primaryLabel: 'Refresh status',
          route: '#refresh',
          level: 'medium',
        };
      }
    }

    if (stepId === 'identity_documents') {
      const nextDoc = guidedIdentityDocuments[0];
      if (nextDoc) {
        return {
          key: `doc_${nextDoc.type}`,
          title: `Upload ${nextDoc.name}`,
          description: 'Take a photo or upload a clear scan of this document.',
          primaryLabel: nextDoc.rejection ? 'Re-upload' : 'Add document',
          route: '#missing-documents-section',
          level: 'critical',
        };
      }
      if (showNmcInWizard) {
        return {
          key: 'nmc_registration',
          title: professional_registration.number
            ? 'Upload NMC certificate'
            : `Submit ${professional_registration.type} PIN`,
          description: 'Nurses must provide a valid NMC registration before onboarding can continue.',
          primaryLabel: professional_registration.number ? 'Upload certificate' : 'Submit PIN',
          route: '#professional-registration-section',
          level: 'critical',
        };
      }
      if (currentWizardStep?.is_paused) {
        return {
          key: 'identity_waiting',
          title: 'Documents under review',
          description: `${providerName} is verifying your uploads. You will be notified when you can continue.`,
          primaryLabel: 'Refresh status',
          route: '#refresh',
          level: 'medium',
        };
      }
    }

    if (stepId === 'mandatory_training') {
      const nextTraining = all_mandatory_trainings.find(
        (t) => ['missing', 'expired', 'rejected', 'needs_information'].includes(String(t.status || '').toLowerCase()),
      );
      if (nextTraining) {
        return {
          key: `training_${nextTraining.id}`,
          title: `Upload ${nextTraining.name}`,
          description: 'Upload the certificate you already hold for this training.',
          primaryLabel: 'Upload certificate',
          route: '#training-card',
          level: 'critical',
        };
      }
    }

    if (stepId === 'care_certificate') {
      return {
        key: 'care_certificate',
        title: 'Continue Care Certificate',
        description: `Complete the next ${providerName} standard - one at a time.`,
        primaryLabel: 'Open Care Certificate',
        route: '#induction',
        level: 'critical',
      };
    }

    if (stepId === 'agreements') {
      const agreementAction = synthesizeAgreementNextAction();
      if (agreementAction && agreementAction.key !== 'contract_locked') {
        return agreementAction;
      }
    }

    return null;
  };

  const displayedNextAction = applicationGateAction || (wizardEnabled
    ? (
      synthesizeWizardNextAction()
      || canonicalNextActionMapped
      || (actionableWorkerTasks[0] && actionableWorkerTasks[0].mapped)
    )
    : (
      canonicalNextActionMapped
      || (actionableWorkerTasks[0] && actionableWorkerTasks[0].mapped)
      || synthesizeAgreementNextAction()
    ));

  const forcedActionShownInNextCard = Boolean(
    wizardEnabled
    && forcedWizardAction?.key
    && displayedNextAction?.key
    && normalizeActionKey(displayedNextAction.key) === normalizeActionKey(forcedWizardAction.key),
  );
  const cvUploadHandledAbove = Boolean(
    cvNeedsWorkerAction
    && displayedNextAction?.key
    && isCvActionKey(displayedNextAction.key),
  );
  const showCvNotificationAlert = Boolean(
    wizardShowsStep('profile_checks')
    && unresolvedCvNotifications.length > 0
    && !cvUploadHandledAbove,
  );
  const secondaryActionableWorkerTasks = actionableWorkerTasks.filter(
    ({ mapped }) => !actionDuplicatesPrimarySurface(mapped.key, displayedNextAction, forcedWizardAction),
  );

  const wizardStepContext = (() => {
    if (!wizardEnabled) return null;
    const currentWizardStep = (onboarding_wizard?.steps || []).find((s) => s.id === activeWizardStepId);

    if (activeWizardStepId === 'profile_checks') {
      if (currentWizardStep?.is_paused) {
        return {
          subtitle: `With ${providerName} for review`,
          title: 'Nothing more to do right now',
          description:
            `You have finished everything on your side for this step. ${providerName} is reviewing your forms and references - we will email you when identity documents unlock.`,
        };
      }
      if (profileChecksTask?.focus) {
        return {
          subtitle: profileChecksTask.subtitle,
          title: profileChecksTask.title,
          description: profileChecksTask.description,
        };
      }
    }

    if (activeWizardStepId === 'identity_documents') {
      if (currentWizardStep?.is_paused) {
        return {
          subtitle: `With ${providerName} for review`,
          title: 'Documents under review',
          description:
            `You have uploaded everything we asked for. ${providerName} is verifying your documents - we will email you when the interview step unlocks.`,
        };
      }
      const nextDoc = guidedIdentityDocuments[0];
      if (nextDoc) {
        return {
          subtitle: 'One document at a time',
          title: nextDoc.name,
          description: getDocumentGuidance(nextDoc.type, providerName),
        };
      }
      if (showNmcInWizard) {
        return {
          subtitle: 'Professional registration',
          title: `${professional_registration.type} registration`,
          description: professional_registration.number
            ? 'Upload your NMC certificate so we can verify your registration.'
            : 'Submit your NMC PIN and upload your registration certificate.',
        };
      }
    }

    if (activeWizardStepId === 'interview') {
      if (interview_form && isFormWorkerActionRequired(interview_form.status)) {
        const inProgress = String(interview_form.status || '').toLowerCase() === 'in_progress';
        return {
          subtitle: inProgress ? 'Continue where you left off' : 'Before your interview',
          title: interview_form.name,
          description:
            'Complete this questionnaire so our team can review your answers before your video interview.',
        };
      }
      if (currentWizardStep?.is_paused) {
        return {
          subtitle: 'With recruitment',
          title: 'Interview being arranged',
          description:
            'You have finished everything on your side. We will email you when your interview is scheduled.',
        };
      }
    }

    return null;
  })();

  const scrollToSection = (selector) => {
    const node = document.querySelector(selector);
    if (node) {
      node.scrollIntoView({ behavior: 'smooth', block: 'start' });
      return true;
    }
    return false;
  };

  const scrollToFileSection = (...selectors) => {
    const tryScroll = () => selectors.some((selector) => scrollToSection(selector));
    if (isActiveEmployee) {
      setWorkerMainView('file');
      window.setTimeout(tryScroll, 80);
      return true;
    }
    return tryScroll();
  };

  const handleNextAction = (action) => {
    if (uploading) {
      toast.info('Finishing your document upload. Please wait a moment.');
      return;
    }
    // Deep links and notifications must reveal the actionable task view first.
    if (!isActiveEmployee) setApplicantView('tasks');
    const route = action?.route || '';
    if (route.startsWith('/worker/dashboard#')) {
      const hashRoute = `#${route.split('#')[1] || ''}`;
      return handleNextAction({ ...action, route: hashRoute });
    }
    if (route.startsWith('http://') || route.startsWith('https://')) {
      window.open(route, '_blank', 'noopener,noreferrer');
      return;
    }
    if (route.startsWith('/')) {
      navigate(route);
      return;
    }
    if (action?.formId) {
      navigate(`/worker/forms/${action.formId}`);
      return;
    }
    if (route === '#refresh') {
      fetchDashboard();
      return;
    }
    let wizardStepChanged = false;
    if (wizardEnabled) {
      const wizardStep = WIZARD_ROUTE_TO_STEP[route];
      if (wizardStep) {
        setOpenedWizardTask(wizardStep);
        setWizardStepOverride(wizardStep);
        wizardStepChanged = true;
        const nextParams = new URLSearchParams(searchParams);
        nextParams.set('step', wizardStep);
        setSearchParams(nextParams, { replace: true });
      }
    }
    const scrollAfterWizardStepChange = (fn) => {
      if (!wizardStepChanged) {
        fn();
        return;
      }
      window.setTimeout(fn, 80);
    };
    switch (action?.route) {
      case '#documents-cv':
        triggerCvFileInput();
        return;
      case '#documents-card':
        scrollAfterWizardStepChange(() => scrollToFileSection('[data-testid="documents-card"]'));
        return;
      case '#agreements-contract':
        if (contractDisplay.workerActionable && effectiveContractCanSign) {
          setShowSignaturePad(true);
          return;
        }
        scrollToFileSection('[data-testid="agreements-section"]');
        return;
      case '#forms':
        scrollAfterWizardStepChange(() => scrollToFileSection('[data-testid="forms-section"]', '[data-testid="employment-readiness-checklist"]'));
        return;
      case '#references-section':
        scrollAfterWizardStepChange(() => scrollToFileSection('[data-testid="references-section"]'));
        return;
      case '#employment-history':
        scrollAfterWizardStepChange(() => scrollToFileSection('[data-testid="employment-history-section"]'));
        return;
      case '#employment-gaps-section':
        scrollAfterWizardStepChange(() => scrollToFileSection('[data-testid="employment-gaps-section"]'));
        return;
      case '#missing-documents-section':
        scrollToFileSection('[data-testid="missing-documents-section"]');
        return;
      case '#professional-registration-section':
        scrollToFileSection('[data-testid="professional-registration-section"]');
        return;
      case '#agreements-handbook':
        if (handbookAgreement && handbookDisplay.workerActionable && (handbookAgreement.file_url || handbookAgreement.download_url)) {
          openHandbookAckModal(handbookAgreement);
          return;
        }
        scrollToFileSection('[data-testid="agreements-section"]');
        return;
      case '#documents':
        scrollToFileSection('[data-testid="documents-card"]', '[data-testid="missing-documents-section"]');
        return;
      case '#training':
      case '#training-card':
        // React must first mount the task workspace, then scroll to its
        // immediately visible upload controls. The old collapsed <details>
        // required a third click that applicants understandably missed.
        scrollAfterWizardStepChange(() => scrollToFileSection('[data-testid="training-card"]'));
        return;
      case '#induction':
        scrollToFileSection('[data-testid="induction-section"]', '[data-testid="employment-readiness-checklist"]');
        return;
      case '#checks':
        scrollToFileSection('[data-testid="checks-card"]', '[data-testid="references-section"]', '[data-testid="employment-gaps-section"]');
        return;
      default:
        scrollToFileSection('[data-testid="employment-readiness-checklist"]');
    }
  };

  const readinessState = (employment_readiness || '').toLowerCase();
  const isReadinessReady = readinessState === 'ready_for_work';
  const readinessTone =
    readinessState === 'system_issue_preventing_completion'
      ? 'red'
      : isReadinessReady
        ? 'green'
        : 'amber';
  const readinessHeadline = employment_readiness_label || (isReadinessReady ? 'Ready for Work' : 'Not ready for work');
  const nextReadinessBlocker = employment_readiness_blockers[0];
  const readinessChecklist = (
    <OnboardingPremiumCard
      tone={readinessTone === 'green' ? 'success' : readinessTone === 'red' ? 'critical' : readinessTone === 'amber' ? 'warning' : 'info'}
      data-testid="employment-readiness-checklist"
      header={(
        <div className="mb-4 flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="font-heading text-lg font-bold text-[#17324D]">Readiness checklist</h2>
              <LifecycleStagePill
                status={
                  isActiveEmployee
                    ? 'active'
                    : (normalizeLifecycleStatus(employee?.status) || (isPreEmploymentEmployee ? 'onboarding' : 'new'))
                }
                stage={isActiveEmployee || isPreEmploymentEmployee ? 'employee' : 'applicant'}
              />
            </div>
            <p className="mt-1.5 text-sm text-slate-600">
              {progress.completed} of {progress.required} required items complete
            </p>
          </div>
          <Badge className={readinessTone === 'green' ? 'bg-green-100 text-green-700' : readinessTone === 'red' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}>
            {readinessHeadline}
          </Badge>
        </div>
      )}
    >
        <Progress value={progress.percentage} className="h-2.5 rounded-full" />
        {!isReadinessReady && nextReadinessBlocker ? (
          <p className="mt-3 text-sm text-slate-700">
            <span className="font-medium">Next action:</span>{' '}
            {nextReadinessBlocker.label}
          </p>
        ) : null}
        {!isReadinessReady && employment_readiness_blockers.length > 0 ? (
          <ul className="mt-3 space-y-2">
            {employment_readiness_blockers.map((blocker, index) => (
              <li key={`${blocker.label}-${index}`} className="flex items-start gap-2 text-sm text-slate-700">
                <AlertTriangle className="mt-0.5 h-4 w-4 text-amber-500" />
                <span>{blocker.label}</span>
              </li>
            ))}
          </ul>
        ) : (
          <p className={`mt-3 text-sm ${isReadinessReady ? 'text-green-700' : 'text-slate-700'}`}>
            {isReadinessReady ? 'Everything required from you is complete.' : 'Your readiness has not been confirmed yet. Ask the team to review your staff file.'}
          </p>
        )}
    </OnboardingPremiumCard>
  );

  return (
    <WorkerDashboardPage
      supportRequest={supportRequest}
      header={(
        <DashboardHeader
          orgName={tenantDisplayName}
          logoUrl={orgSettings.logo_url}
          workerName={employee?.full_name || employee?.name || 'Worker'}
          employeeCode={employee?.employee_code || employee?.applicant_reference || employee?.display_code}
          mode={isActiveEmployee ? 'employee' : 'applicant'}
          portalLabel={isActiveEmployee || isPreEmploymentEmployee ? 'Workforce Portal' : 'Applicant Portal'}
          unreadCount={unreadCount}
          hasPassword={false}
          onHelp={() => setSupportRequest((value) => value + 1)}
          notificationsOpen={showNotificationsPanel}
          onRefresh={fetchDashboard}
          onPassword={null}
          onToggleNotifications={() => {
            setShowNotificationsPanel((open) => {
              if (open) setShowAllNotifications(false);
              return !open;
            });
          }}
          onLogout={handleLogout}
        />
      )}
      nextAction={showDeclarationRepairTask ? (
        <NextActionCard
          action={{
            level: 'high',
            title: 'Complete your declarations',
            description: 'We need a few final confirmations before your application can continue.',
            primaryLabel: 'Complete your declarations',
            route: declarationRepairRoute,
          }}
          onPrimaryAction={handleNextAction}
        />
      ) : (
        <>
          {displayedNextAction && !wizardEnabled && !(
            !isActiveEmployee && applicantView === 'tasks'
            && /#training(?:-card)?$/.test(String(displayedNextAction.route || ''))
          ) ? (
            <NextActionCard action={displayedNextAction} onPrimaryAction={handleNextAction} />
          ) : null}
        </>
      )}
      readinessChecklist={isActiveEmployee && !isReadinessReady && workerMainView === 'today' ? (
        <WorkerReadinessNotice blockers={employment_readiness_blockers}
          onReview={() => setWorkerMainView('file')}
          onHelp={() => setSupportRequest((value) => value + 1)} />
      ) : ((!isActiveEmployee && applicantView === 'tasks') || (isActiveEmployee && workerMainView === 'file')) && !wizardEnabled ? readinessChecklist : null}
    >
      {/* Active staff use the bottom navigation; history is in More. */}
      {isActiveEmployee && workerMainView === 'more' ? <WorkerInstallPrompt appName={appName} /> : null}


      {/* Show only an already-prepared pre-terms KID; no extra wizard step or
          invented compliance completion. Existing Active staff are unaffected. */}
      {!isActiveEmployee && applicantView === 'tasks' && <WorkerKidCard />}

      {!isActiveEmployee && (
        <nav className="grid grid-cols-3 gap-1 rounded-2xl border border-slate-200 bg-white p-1 shadow-sm" aria-label="Applicant sections" data-testid="applicant-nav">
          {[
            { id: 'home', label: 'Home', Icon: Home },
            { id: 'tasks', label: 'My tasks', Icon: ClipboardCheck },
            { id: 'records', label: 'My records', Icon: FolderOpen },
          ].map(({ id, label, Icon }) => (
            <button
              type="button"
              key={id}
              className={`flex min-h-12 items-center justify-center gap-1.5 rounded-xl px-2 text-xs font-semibold transition sm:text-sm ${applicantView === id ? 'bg-[#0F5C5E] text-white' : 'text-slate-600 hover:bg-slate-50'}`}
              aria-current={applicantView === id ? 'page' : undefined}
              onClick={() => setApplicantView(id)}
              data-testid={`applicant-nav-${id}`}
            >
              <Icon className="h-4 w-4 shrink-0" /> {label}
            </button>
          ))}
        </nav>
      )}

      {!isActiveEmployee && applicantView === 'home' && (
        <ApplicantHome
          actions={pendingWorkerActions}
          primaryAction={applicantHomePrimaryAction}
          trainings={all_mandatory_trainings}
          expiredTrainings={expired_trainings}
          uploading={Boolean(uploading)}
          uploadFeedback={trainingUploadFeedback}
          onOpenTasks={() => setApplicantView('tasks')}
          onOpenInduction={() => setShowApplicantInduction(true)}
          onOpenTask={(action) => handleNextAction(buildApplicantHomeTaskTarget(action))}
          onRenewCertificate={(training) => {
            const trainingId = training.id || training.code;
            if (trainingId) {
              triggerFileInput(`training_${trainingId}`);
            } else {
              setApplicantView('tasks');
              setWizardStepOverride('mandatory_training');
            }
          }}
        />
      )}

      {/* Induction is always reachable from Home, regardless of which document
          or admin review step the onboarding wizard is currently displaying. */}
      {!isActiveEmployee && showApplicantInduction && (
        <div className="fixed inset-0 z-[10040] flex items-end justify-center bg-black/50 p-0 sm:items-center sm:p-4"
          onMouseDown={(event) => { if (event.target === event.currentTarget) setShowApplicantInduction(false); }}
          data-testid="applicant-induction-dialog-backdrop">
          <div role="dialog" aria-modal="true" aria-label="Care Certificate induction"
            className="flex max-h-[95vh] w-full max-w-3xl flex-col overflow-hidden rounded-t-2xl bg-white shadow-2xl sm:rounded-2xl">
            <div className="flex shrink-0 items-center justify-between border-b border-slate-200 px-4 py-3">
              <div>
                <h2 className="font-semibold text-slate-900">Your Care Certificate</h2>
                <p className="text-xs text-slate-600">Learn and complete your forms while your documents are checked.</p>
              </div>
              <button type="button" className="min-h-11 rounded-lg border border-slate-200 px-4 text-sm font-semibold"
                onClick={() => {
                  setShowApplicantInduction(false);
                  if (searchParams?.get('induction') === '1') {
                    const nextParams = new URLSearchParams(searchParams);
                    nextParams.delete('induction');
                    setSearchParams(nextParams, { replace: true });
                  }
                }} data-testid="applicant-close-induction">Close</button>
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto p-3 sm:p-5">
              <CareCertificateInductionPanel />
            </div>
          </div>
        </div>
      )}

      {!isActiveEmployee && applicantView === 'records' && (
        <section className="space-y-3" aria-label="My records" data-testid="applicant-records-page">
          <div className="flex items-center justify-between gap-3">
            <div>
              <h2 className="text-lg font-semibold text-slate-900">My records</h2>
              <p className="text-sm text-slate-600">Your submitted and verified files are here, not on your home dashboard.</p>
            </div>
            <Button size="sm" variant="outline" onClick={() => setApplicantView('home')}>Home</Button>
          </div>
          {(completed_documents?.length > 0 || completed_trainings?.length > 0) ? (
            <SubmittedDocumentsVault
              documents={completed_documents}
              trainings={completed_trainings}
              token={localStorage.getItem('workerToken')}
              apiBase={API}
              formatDate={formatDate}
              getDocumentDisplayStatus={getCompletedDocumentDisplayStatus}
              getTrainingBadgeMeta={getCompletedTrainingBadgeMeta}
              documentWorkflowUi={DOCUMENT_WORKFLOW_UI}
            />
          ) : (
            <div className="rounded-xl border border-slate-200 bg-white p-5 text-sm text-slate-600">
              No documents to show yet. Files you submit will appear here after they have been saved.
            </div>
          )}
        </section>
      )}

      {/* Duplicate quick-action cards grid fully removed (was duplicating bottom nav). Bottom nav now drives the views. */}
      {(isActiveEmployee || applicantView === 'tasks') && (
      <OnboardingWizardShell
        wizard={wizardEnabled ? onboardingWizardForDisplay : null}
        hidePostInterviewSteps={!employee?.recruitment_approved}
        stepContext={wizardStepContext}
        hideStepIntro={wizardEnabled && activeWizardStepId === 'mandatory_training'}
        hideStepContent={trainingTaskClosed}
        onStepSelect={(stepId) => {
          if (uploading) {
            toast.info('Finishing your document upload. Please wait a moment.');
            return;
          }
          setOpenedWizardTask(stepId);
          setWizardStepOverride(stepId);
          const nextParams = new URLSearchParams(searchParams);
          nextParams.set('step', stepId);
          setSearchParams(nextParams, { replace: true });
        }}
        nextActionSlot={(
          <>
            {showDeclarationRepairTask ? (
              <NextActionCard
                action={{
                  level: 'high',
                  title: 'Complete your declarations',
                  description: 'We need a few final confirmations before your application can continue.',
                  primaryLabel: 'Complete your declarations',
                  route: declarationRepairRoute,
                }}
                onPrimaryAction={handleNextAction}
              />
            ) : null}
            {wizardEnabled && applicantJourneyModel ? (
              <ApplicantJourneyOverview
                model={applicantJourneyModel}
                providerName={providerName}
                primaryTaskOverride={
                  (applicationGateAction?.route && applicationGateAction.owner !== 'admin')
                    ? { ...applicationGateAction, focus: 'application' }
                    : (profileChecksTask?.owner === 'admin' ? null : profileChecksTask)
                }
                inlineWorkHistoryVisible={Boolean(
                  (profileChecksFocus === 'employment_history' || profileChecksFocus === 'gaps')
                  && (showEmploymentGapsRepairSection || showEmploymentIntegritySection)
                )}
                onAction={(step) => handleNextAction(resolveApplicantJourneyStepAction(step, {
                  applicationGateAction,
                  profileChecksTask,
                }))}
              />
            ) : null}
          </>
        )}
      >
      {wizardEnabled && forcedWizardAction && !forcedActionShownInNextCard ? (
        <OnboardingPremiumCard
          tone="critical"
          data-testid="admin-forced-action-banner"
          title={getApplicantForcedActionBannerTitle(applicantHomePrimaryAction, forcedWizardAction)}
          description={toApplicantPlatformCopy(forcedWizardAction.description)}
          icon={AlertTriangle}
          action={<Badge className="bg-red-100 text-red-700">From {providerName}</Badge>}
        >
          <p className="text-sm text-slate-700">
            Complete <span className="font-medium">{forcedWizardAction.title}</span> on this step before later onboarding stages unlock.
          </p>
        </OnboardingPremiumCard>
      ) : null}
      {wizardEnabled && onboardingWizardComplete ? (
        <OnboardingPremiumCard
          tone="success"
          data-testid="onboarding-complete-card"
          title="Onboarding complete on your side"
          description={`${providerName} is finishing final checks. We will email you when you are activated and can apply for shifts.`}
          icon={CheckCircle}
        >
          <p className="text-sm text-slate-700">
            You have finished every guided step. Activation is handled by our team — you do not need to repeat completed items unless we ask.
          </p>
          <div className="mt-3 rounded-xl border border-purple-200 bg-purple-50/80 px-4 py-3">
            <p className="text-sm font-medium text-purple-900">Speak Up — protected concerns</p>
            <p className="mt-1 text-xs text-purple-800">
              If anything feels unsafe or wrong during onboarding or later shifts, use Speak Up. It is separate from day-to-day incidents and protected under whistleblowing policy.
            </p>
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="mt-2 border-purple-300 text-purple-800"
              onClick={() => setRaisedConcernModalOpen(true)}
            >
              <Shield className="mr-1 h-3.5 w-3.5" />
              Open Speak Up
            </Button>
          </div>
        </OnboardingPremiumCard>
      ) : null}
      {wizardEnabled && !onboardingWizardComplete && wizardShowsStep('agreements') ? (
        <OnboardingPremiumCard
          tone="purple"
          data-testid="onboarding-speakup-intro"
          title="Know how to raise a protected concern"
          description="Before you finish onboarding, note that Speak Up exists if you ever need a confidential, protected channel."
          icon={Shield}
        >
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="border-purple-300 text-purple-800"
            onClick={() => setRaisedConcernModalOpen(true)}
          >
            Learn about Speak Up
          </Button>
        </OnboardingPremiumCard>
      ) : null}
      {!isActiveEmployee && secondaryActionableWorkerTasks.length > 1 && !wizardEnabled ? (
        <OnboardingPremiumCard tone="critical" data-testid="worker-action-required-section" title="Action Required" icon={AlertTriangle}>
            <div className="space-y-2">
              {secondaryActionableWorkerTasks.map(({ task, mapped }) => (
                <div key={task.key || task.title} className="flex flex-col gap-2 rounded-xl border border-red-100 bg-white p-3 shadow-sm sm:flex-row sm:items-center sm:justify-between">
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-slate-900">{task.title}</p>
                    <p className="text-xs text-slate-600">{task.required ? 'Required before readiness clearance' : 'Action available'}</p>
                  </div>
                  <Button
                    size="sm"
                    className="w-full sm:w-auto"
                    onClick={() => handleNextAction(mapped)}
                  >
                    {mapped.primaryLabel || 'Open task'}
                  </Button>
                </div>
              ))}
            </div>
        </OnboardingPremiumCard>
      ) : null}
      {showNotificationsPanel && (
        <div className="fixed inset-x-4 top-20 z-50 mx-auto max-w-sm rounded-lg border border-slate-200 bg-white shadow-lg">
                  <div className="p-3 border-b border-slate-200 flex items-center justify-between">
                    <h3 className="font-semibold text-slate-800">Notifications</h3>
                    {unreadCount > 0 && (
                      <Badge className="bg-red-100 text-red-700">{unreadCount} unread</Badge>
                    )}
                  </div>
                  <div className="max-h-80 overflow-y-auto">
                    {notifications.length === 0 ? (
                      <div className="p-4 text-center text-slate-500 text-sm">
                        No notifications
                      </div>
                    ) : (
                      (showAllNotifications ? notifications : notifications.slice(0, 5)).map((notif) => (
                        <div 
                          key={notif.id} 
                          className={`p-3 border-b border-slate-100 hover:bg-slate-50 cursor-pointer ${!notif.read ? 'bg-blue-50' : ''}`}
                          onClick={() => {
                            if (!notif.read) markNotificationRead(notif.id);
                            const trainingUrl = notif.data?.primary_url || notif.data?.programme_url;
                            if (notif.type === 'care_certificate_training' && trainingUrl) {
                              window.open(trainingUrl, '_blank', 'noopener,noreferrer');
                              return;
                            }
                            if (
                              isReferenceMismatchNotificationType(notif.type)
                              || isReferenceRejectionNotificationType(notif.type)
                              || isEmploymentGapNotificationType(notif.type)
                            ) {
                              setShowNotificationsPanel(false);
                              navigateFromNotification(notif, { navigate, handleNextAction });
                            }
                          }}
                        >
                          <div className="flex items-start gap-2">
                            {notif.type === 'cv_rejected' ? (
                              <AlertTriangle className="h-4 w-4 text-red-500 flex-shrink-0 mt-0.5" />
                            ) : notif.type === 'reference_rejected' || notif.type === 'reference_mismatch_rejected' ? (
                              <AlertCircle className="h-4 w-4 text-red-500 flex-shrink-0 mt-0.5" />
                            ) : notif.type === 'reference_mismatch_needs_clarification' ? (
                              <MessageSquare className="h-4 w-4 text-amber-500 flex-shrink-0 mt-0.5" />
                            ) : notif.type === 'employment_gap_rejected' || notif.type === 'gap_info_requested' ? (
                              <Briefcase className="h-4 w-4 text-amber-500 flex-shrink-0 mt-0.5" />
                            ) : notif.type === 'care_certificate_training' ? (
                              <GraduationCap className="h-4 w-4 text-[#0F5C5E] flex-shrink-0 mt-0.5" />
                            ) : (
                              <Bell className="h-4 w-4 text-blue-500 flex-shrink-0 mt-0.5" />
                            )}
                            <div className="flex-1 min-w-0">
                              <p className={`text-sm ${!notif.read ? 'font-semibold text-slate-800' : 'text-slate-700'}`}>
                                {notif.title}
                              </p>
                              <p className="text-xs text-slate-500 mt-1 line-clamp-2">
                                {notif.message}
                              </p>
                              <p className="text-xs text-slate-400 mt-1">
                                {formatDate(notif.created_at)}
                              </p>
                            </div>
                            {!notif.read && (
                              <div className="w-2 h-2 bg-blue-500 rounded-full flex-shrink-0" />
                            )}
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                  {notifications.length > 5 && !showAllNotifications && (
                    <div className="p-2 text-center border-t border-slate-200">
                      <button
                        type="button"
                        className="text-sm text-purple-600 hover:underline"
                        onClick={() => setShowAllNotifications(true)}
                        data-testid="view-all-notifications"
                      >
                        View all notifications ({notifications.length})
                      </button>
                    </div>
                  )}
          </div>
      )}

      {/* NMC / Professional Registration Modal */}
      <Dialog open={showNmcDialog} onOpenChange={setShowNmcDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5 text-blue-600" />
              Submit {professional_registration?.type || 'Professional'} Registration
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <p className="text-sm text-slate-600">
              Enter your registration PIN. Upload your registration certificate separately if you have not already done so.
            </p>
            <div className="space-y-2">
              <Label htmlFor="nmc-pin">Registration PIN *</Label>
              <Input
                id="nmc-pin"
                value={nmcPin}
                onChange={(e) => setNmcPin(e.target.value)}
                placeholder="e.g. 12A3456E"
                data-testid="nmc-pin-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="nmc-expiry">Revalidation / expiry date (optional)</Label>
              <Input
                id="nmc-expiry"
                type="date"
                value={nmcExpiry}
                onChange={(e) => setNmcExpiry(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNmcDialog(false)}>Cancel</Button>
            <Button onClick={handleSubmitNmcRegistration} disabled={submittingNmc}>
              {submittingNmc ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Submit PIN
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Earnings Details Modal (app-style detail view) */}
      <Dialog open={showEarningsModal} onOpenChange={setShowEarningsModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>My Earnings</DialogTitle>
            <p className="text-sm text-slate-600">Approved hours and timesheet status. No gross pay shown here.</p>
          </DialogHeader>
          <div className="space-y-4">
            <div className="rounded-lg border p-4">
              <div className="text-sm font-medium">This month (approved, awaiting timesheet)</div>
              <div className="mt-1 text-2xl font-semibold">
                {earningsSummary?.approved_awaiting_timesheet?.count ?? 0} shifts · {(earningsSummary?.approved_awaiting_timesheet?.hours ?? 0).toFixed(2)}h
              </div>
              <Badge variant="outline" className="mt-2 bg-amber-100 text-amber-800 border-amber-200">awaiting timesheet</Badge>
            </div>

            {earningsSummary?.latest_timesheet && (
              <div className="rounded-lg border p-4 text-sm">
                <div className="font-medium mb-1">Latest timesheet</div>
                <div>{earningsSummary.latest_timesheet.reference}</div>
                <div className="text-xs text-slate-600">
                  {formatDate(earningsSummary.latest_timesheet.pay_period_start)} – {formatDate(earningsSummary.latest_timesheet.pay_period_end)}
                </div>
                <div className="mt-1">
                  {(earningsSummary.latest_timesheet.total_hours ?? 0).toFixed(2)}h · <Badge variant="outline">{earningsSummary.latest_timesheet.status_label || 'Awaiting processing'}</Badge>
                </div>
              </div>
            )}

            <p className="text-xs text-slate-500">Timesheets feed payroll and client invoices. Fully closed records appear as "Financially Closed" in admin views. For pay queries contact your payroll team.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEarningsModal(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Full Shifts Modal (app detail view) */}
      <Dialog open={showShiftsModal} onOpenChange={setShowShiftsModal}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>My Shifts</DialogTitle>
          </DialogHeader>
          <div className="py-2">
            <WorkerMyShiftsCard
              loading={workerShiftsLoading}
              shiftGroups={workerShiftGroups}
              showPastShifts={showPastWorkerShifts}
              onTogglePastShifts={() => setShowPastWorkerShifts((prev) => !prev)}
              formatDate={formatDate}
              formatDateTime={formatDateTime}
              canWorkerClockIn={canWorkerClockIn}
              canWorkerClockOut={canWorkerClockOut}
              canWorkerReClock={canWorkerReClock}
              workerShiftClocking={workerShiftClocking}
              onAccept={(item) => { setShowShiftsModal(false); openWorkerShiftResponseModal(item, 'accept'); }}
              onReject={(item) => { setShowShiftsModal(false); openWorkerShiftResponseModal(item, 'reject'); }}
              onClockIn={(item) => { setShowShiftsModal(false); openSiteVerify(item, 'in'); }}
              onClockOut={(item) => { setShowShiftsModal(false); openSiteVerify(item, 'out'); }}
              onReClock={(item) => { setShowShiftsModal(false); openSiteVerify(item, 'reclock'); }}
              onView={(item) => { setShowShiftsModal(false); handleOpenWorkerShift(item); }}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowShiftsModal(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Compliance Modal */}
      <Dialog open={showComplianceModal} onOpenChange={setShowComplianceModal}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Compliance &amp; Due Items</DialogTitle>
          </DialogHeader>
          <WorkerComplianceDueCard
            alerts={complianceDueAlerts}
            formatDate={formatDate}
            onUpload={triggerFileInput}
            uploading={uploading}
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowComplianceModal(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Policies Modal */}
      <Dialog open={showPoliciesModal} onOpenChange={setShowPoliciesModal}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Policies &amp; Acknowledgements</DialogTitle>
          </DialogHeader>
          <WorkerAssignedPoliciesCard
            policies={assignedPolicies}
            token={localStorage.getItem('workerToken')}
            onRefresh={fetchDashboard}
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPoliciesModal(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Incidents Modal */}
      <Dialog open={showIncidentsModal} onOpenChange={setShowIncidentsModal}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Incidents &amp; Reports</DialogTitle>
          </DialogHeader>
          <WorkerIncidentsCard
            loading={workerIncidentsLoading}
            incidents={workerIncidents}
            formatDateTime={formatDateTime}
            getStatusMeta={getWorkerIncidentStatusMeta}
            onReport={() => { setShowIncidentsModal(false); setIncidentModalOpen(true); }}
            onView={(incident) => { setShowIncidentsModal(false); openIncidentDetail(incident); }}
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowIncidentsModal(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Documents Modal (basic hub - links to file tab for full view) */}
      <Dialog open={showDocumentsModal} onOpenChange={setShowDocumentsModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Documents &amp; Evidence</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2 text-sm">
            <p>Your CV, ID, DBS, training certs, and other required documents live in your Staff File.</p>
            <div className="rounded-lg border p-3 bg-slate-50">
              <p className="font-medium">Quick access:</p>
              <ul className="mt-2 list-disc pl-5 text-xs space-y-1 text-slate-600">
                <li>CV / Resume status</li>
                <li>Identity &amp; right to work docs</li>
                <li>DBS / Background checks</li>
                <li>Training certificates &amp; renewals</li>
              </ul>
            </div>
            <p className="text-xs text-slate-500">Switch to the "My Staff File" tab for the full list and upload options, or use the compliance alerts for anything due.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDocumentsModal(false)}>Close</Button>
            <Button onClick={() => { setShowDocumentsModal(false); }}>Go to My Staff File</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* More Menu Modal (app drawer style) */}
      <Dialog open={showMoreModal} onOpenChange={setShowMoreModal}>
        <DialogContent className="max-w-xs">
          <DialogHeader>
            <DialogTitle>More</DialogTitle>
          </DialogHeader>
          <div className="grid gap-2 py-2">
            <Button variant="ghost" className="justify-start" onClick={() => { setShowMoreModal(false); }}>My Staff File (full archive)</Button>
            <Button variant="ghost" className="justify-start" onClick={() => { setShowMoreModal(false); setShowEarningsModal(true); }}>Earnings Details</Button>
            <Button variant="ghost" className="justify-start" onClick={() => { setShowMoreModal(false); setShowPoliciesModal(true); }}>All Policies</Button>
            <Button variant="ghost" className="justify-start" onClick={() => { setShowMoreModal(false); setShowIncidentsModal(true); }}>All Incidents &amp; Reports</Button>
            <Button variant="ghost" className="justify-start" onClick={() => { setShowMoreModal(false); setShowDocumentsModal(true); }}>Documents Hub</Button>
            <Button variant="ghost" className="justify-start" onClick={() => { setShowMoreModal(false); /* notifications toggle if wanted */ }}>Notifications</Button>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowMoreModal(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Set Password Modal */}
      {false && <Dialog open={showSetPasswordModal} onOpenChange={setShowSetPasswordModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Lock className="h-5 w-5 text-purple-600" />
              {accountStatus.has_password ? 'Change Password' : 'Set Password'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-sm text-slate-600">
              {accountStatus.has_password 
                ? 'Update your password for faster login.'
                : 'Set a password for faster login. You can still use magic links if you prefer.'}
            </p>
            
            {accountStatus.has_password && (
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700">Current Password</label>
                <input
                  type="password"
                  value={passwordForm.current_password}
                  onChange={(e) => setPasswordForm({ ...passwordForm, current_password: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                  placeholder="Enter current password"
                />
              </div>
            )}
            
            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-700">New Password</label>
              <input
                type="password"
                value={passwordForm.new_password}
                onChange={(e) => setPasswordForm({ ...passwordForm, new_password: e.target.value })}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                placeholder="Enter new password"
              />
              <p className="text-xs text-slate-500">
                Min 8 characters, 1 uppercase, 1 number
              </p>
            </div>
            
            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-700">Confirm Password</label>
              <input
                type="password"
                value={passwordForm.confirm_password}
                onChange={(e) => setPasswordForm({ ...passwordForm, confirm_password: e.target.value })}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                placeholder="Confirm new password"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSetPasswordModal(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleSetPassword} 
              disabled={settingPassword || !passwordForm.new_password || !passwordForm.confirm_password}
              className="bg-purple-600 hover:bg-purple-700"
            >
              {settingPassword ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              {accountStatus.has_password ? 'Update Password' : 'Set Password'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>}

      {/* Profile Completion Banner - for offline PDF imports */}
        {profileCompletionStatus?.needs_wizard && !isActiveEmployee && wizardShowsStep('profile_checks') && !profileChecksFocus && (
          <OnboardingPremiumCard tone="purple" data-testid="profile-completion-banner" title="Complete Your Profile" description={`${profileCompletionStatus.completed_sections}/${profileCompletionStatus.total_sections} sections complete — please fill in the remaining information`} icon={User} action={(
                <Button
                  onClick={() => setShowProfileWizard(true)}
                  className="rounded-xl bg-purple-600 shadow-sm hover:bg-purple-700"
                  data-testid="complete-profile-btn"
                >
                  <Edit3 className="h-4 w-4 mr-2" />
                  Complete Profile
                </Button>
          )} />
        )}

        {showFileSections && wizardShowsStep('profile_checks') && !wizardEnabled && (
        <>
          {/* Monzo-style Priority Grouping: Directed by importance/necessity.
              Priority 1: These gate contract and early onboarding. Resolve first.
              Only show BLOCKING label if checks still actually need attention. */}
          {(referencesNeedAction || gapsNeedAction || forms.some(f => isFormWorkerActionRequired(f.status))) ? (
            <div className="mb-2 flex items-center gap-2">
              <OnboardingPriorityPill variant="blocking">PRIORITY 1 — BLOCKING (Resolve first to unlock contract)</OnboardingPriorityPill>
            </div>
          ) : (
            <div className="mb-2 flex items-center gap-2">
              <OnboardingPriorityPill variant="complete">CHECKS — ALL COMPLETE</OnboardingPriorityPill>
            </div>
          )}
        <OnboardingPremiumCard data-testid="checks-card" title="Checks" description="References, employment history, and onboarding forms." icon={ShieldCheck}>
            <div className="space-y-2 text-sm text-slate-700">
              <div className="flex items-center justify-between rounded-xl bg-slate-50 px-3 py-2">
                <span>Forms</span>
                <Badge className="bg-slate-100 text-slate-700">{forms.filter((form) => isFormAdminComplete(form.status)).length}/{forms.length || 0} complete</Badge>
              </div>
              <div className="flex items-center justify-between rounded-xl bg-slate-50 px-3 py-2">
                <span>References</span>
                <Badge className={referencesNeedAction ? 'bg-amber-100 text-amber-700' : 'bg-green-100 text-green-700'}>
                  {referencesNeedAction ? 'Needs attention' : 'Complete'}
                </Badge>
              </div>
              <div className="flex items-center justify-between rounded-xl bg-slate-50 px-3 py-2">
                <span>Employment history</span>
                <Badge className={gapsNeedAction ? 'bg-amber-100 text-amber-700' : 'bg-green-100 text-green-700'}>
                  {gapsNeedAction ? 'Needs attention' : 'Complete'}
                </Badge>
              </div>
            </div>
        </OnboardingPremiumCard>
        </>
        )}

        {/* Only show Available Shifts on the Today home view (bottom nav "page" switching hides this when on Shifts etc.) */}
        {/* Medication task card — shown when an active shift has a service user assigned */}
        {workerMainView === 'today' && showTodaySections && isActiveEmployee && workerShiftGroups.active.some(s => s.shift?.service_user_id) && (
          <div
            role="button"
            tabIndex={0}
            onClick={() => {
              const shift = workerShiftGroups.active.find(s => s.shift?.service_user_id);
              setSelectedMedShift(shift);
              setWorkerMainView('meds');
            }}
            onKeyDown={e => e.key === 'Enter' && (() => {
              const shift = workerShiftGroups.active.find(s => s.shift?.service_user_id);
              setSelectedMedShift(shift);
              setWorkerMainView('meds');
            })()}
            className="flex items-center gap-4 p-4 rounded-2xl border-2 border-[#0F5C5E]/20 bg-gradient-to-r from-[#D9F1F4]/60 to-[#D8E6D5]/40 shadow-sm cursor-pointer hover:border-[#0F5C5E]/40 hover:shadow-md transition-all select-none"
          >
            <div className="w-12 h-12 rounded-2xl bg-[#0F5C5E] flex items-center justify-center flex-shrink-0 shadow-sm">
              <svg className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
              </svg>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-bold text-[#0F5C5E]">Medication Administration</p>
              <p className="text-xs text-[#17324D]/70">
                {workerShiftGroups.active.filter(s => s.shift?.service_user_id).length === 1
                  ? 'Tap to view and record medications for your shift'
                  : `${workerShiftGroups.active.filter(s => s.shift?.service_user_id).length} shifts with medication schedules`
                }
              </p>
            </div>
            <svg className="h-5 w-5 text-[#0F5C5E]/50 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
            </svg>
          </div>
        )}



        {/* Only today's operational actions are surfaced by default. */}
        {isActiveEmployee && showTodaySections && workerMainView === 'today' && (
          <WorkerTodayHome
            shiftGroups={workerShiftGroups}
            availableShifts={availableShifts}
            alerts={complianceDueAlerts.filter(alertSupportsWorkerUpload)}
            expiredTrainings={expired_trainings}
            formatDateTime={formatDateTime}
            onOpenShift={handleOpenWorkerShift}
            onShifts={() => setWorkerMainView('shifts')}
            onAlerts={() => setWorkerMainView('compliance')}
            onRenew={(training) => triggerFileInput(`training_${training.id || training.code}`)}
            uploading={uploading}
            renewalFeedback={trainingUploadFeedback}
          />
        )}

        {/* === Bottom-nav driven "page" views (proper navigation, not modals) === */}
        {workerMainView === 'shifts' && (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold">My Shifts</h2>
              <Button size="sm" variant="ghost" onClick={() => setWorkerMainView('today')}>Back to Today</Button>
            </div>
            <WorkerMyShiftsCard
              loading={workerShiftsLoading}
              shiftGroups={workerShiftGroups}
              showPastShifts={showPastWorkerShifts}
              onTogglePastShifts={() => setShowPastWorkerShifts((prev) => !prev)}
              formatDate={formatDate}
              formatDateTime={formatDateTime}
              canWorkerClockIn={canWorkerClockIn}
              canWorkerClockOut={canWorkerClockOut}
              canWorkerReClock={canWorkerReClock}
              workerShiftClocking={workerShiftClocking}
              onAccept={(item) => openWorkerShiftResponseModal(item, 'accept')}
              onReject={(item) => openWorkerShiftResponseModal(item, 'reject')}
              onClockIn={(item) => openSiteVerify(item, 'in')}
              onClockOut={(item) => openSiteVerify(item, 'out')}
              onReClock={(item) => openSiteVerify(item, 'reclock')}
              onView={handleOpenWorkerShift}
            />
            <WorkerAvailableShiftsCard
              loading={availableShiftsLoading}
              error={availableShiftsError}
              onRetry={fetchAvailableShifts}
              shifts={availableShifts}
              applyingShiftId={availableShiftApplyingId}
              formatDate={formatDate}
              formatDateTime={formatDateTime}
              onApply={handleApplyForAvailableShift}
            />
          </div>
        )}

        {workerMainView === 'compliance' && (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold">Compliance &amp; Alerts</h2>
              <Button size="sm" variant="ghost" onClick={() => setWorkerMainView('today')}>Back to Today</Button>
            </div>
            <WorkerComplianceDueCard
              alerts={complianceDueAlerts}
              formatDate={formatDate}
              onUpload={triggerFileInput}
              uploading={uploading}
            />
          </div>
        )}

        {workerMainView === 'speakup' && (
          <div className="space-y-3 pb-20"> {/* extra padding for fixed bottom nav on the Speak Up "page" view */}
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-purple-900">Speak Up – Protected Concerns</h2>
              <Button size="sm" variant="ghost" onClick={() => setWorkerMainView('today')}>Back to Today</Button>
            </div>
            {/* Full Speak Up content as a "page" */}
            <Card className="border-2 border-purple-200 bg-purple-50/30 shadow-sm">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg text-purple-900 flex items-center gap-2">
                  <Shield className="h-5 w-5 text-purple-700" />
                  Raise a Protected Concern
                </CardTitle>
                <p className="text-sm text-purple-800">
                  Protected channel under PIDA. You will not suffer detriment for raising in good faith.
                </p>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2 mb-4">
                  <Button onClick={handleOpenRaiseProtectedConcern} className="bg-purple-700 hover:bg-purple-800">
                    <Shield className="h-4 w-4 mr-2" /> Open Raise Form
                  </Button>
                  <Button variant="outline" onClick={() => setShowMyRaisedConcerns((v) => !v)}>
                    {showMyRaisedConcerns ? 'Hide' : 'Show'} My Concerns
                  </Button>
                </div>
                {showMyRaisedConcerns && (
                  <div className="rounded-lg border border-purple-200 bg-white p-3">
                    {/* the my concerns list content - simplified inline */}
                    <p className="text-sm font-medium text-purple-900 mb-2">My Protected Concerns</p>
                    {raisedConcerns.length === 0 ? (
                      <p className="text-xs text-purple-600">No submissions yet.</p>
                    ) : (
                      raisedConcerns.slice(0,5).map((c, idx) => (
                        <div key={idx} className="text-sm border-t py-1">{c.reference} — {c.status}</div>
                      ))
                    )}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Note: the actual long form is in the scrollable modal below (opened by the button).
                This keeps the tab view lightweight while the form itself is now properly scrollable on small screens. */}
          </div>
        )}

        {workerMainView === 'meds' && selectedMedShift && (
          <ShiftMedicationTask
            shift={selectedMedShift}
            onBack={() => { setWorkerMainView('today'); setSelectedMedShift(null); }}
          />
        )}

        {workerMainView === 'meds' && !selectedMedShift && (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold">Medication</h2>
              <Button size="sm" variant="ghost" onClick={() => setWorkerMainView('today')}>Back to Today</Button>
            </div>
            {workerShiftGroups.active.filter(s => s.shift?.service_user_id).length === 0 ? (
              <p className="text-sm text-slate-500 text-center py-8">No shifts with medication schedules today.</p>
            ) : (
              <div className="space-y-2">
                {workerShiftGroups.active.filter(s => s.shift?.service_user_id).map(item => (
                  <button key={item.assignment_id || item.shift.id}
                    onClick={() => setSelectedMedShift(item)}
                    className="w-full flex items-center gap-3 p-4 rounded-xl border border-slate-200 bg-white hover:border-[#0F5C5E]/30 hover:bg-[#D9F1F4]/10 transition-all text-left shadow-sm">
                    <div className="w-9 h-9 rounded-xl bg-[#D9F1F4]/60 flex items-center justify-center">
                      <svg className="h-5 w-5 text-[#0F5C5E]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /></svg>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-slate-900">{item.shift.location_text || 'Shift'}</p>
                      <p className="text-xs text-slate-500">Service user assigned · tap to view medications</p>
                    </div>
                    <svg className="h-4 w-4 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" /></svg>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {workerMainView === 'more' && (
          <div className="space-y-3" data-testid="worker-more-page">
            <h2 className="text-lg font-semibold">More</h2>
            <p className="text-sm text-slate-600">Only open these when you need them.</p>
            <div className="grid gap-2">
              <Button variant="outline" onClick={() => setWorkerMainView('id_card')}>My ID Card</Button>
              <Button variant="outline" onClick={() => setWorkerMainView('file')}>Documents &amp; Staff File</Button>
              <Button variant="outline" onClick={() => setShowEarningsModal(true)}>Earnings</Button>
              <Button variant="outline" onClick={() => setShowPoliciesModal(true)}>Policies</Button>
              <Button variant="outline" onClick={() => setWorkerMainView('speakup')}>Speak Up — protected concerns</Button>
              <Button variant="outline" onClick={() => setShowSetPasswordModal(true)}>Account Settings</Button>
            </div>
            <Button variant="ghost" onClick={() => setWorkerMainView('today')}>Back to Today</Button>
          </div>
        )}

        {workerMainView === 'id_card' && (
          <div className="space-y-3" data-testid="worker-id-card-page">
            <div className="flex justify-between items-center">
              <h2 className="text-lg font-semibold">My ID Card</h2>
              <Button size="sm" variant="ghost" onClick={() => setWorkerMainView('more')}>Back to More</Button>
            </div>
            <WorkerStaffIdCard />
          </div>
        )}

        {/* Forms Section */}
        {isActiveEmployee && workerMainView === 'file' && (
          <div className="flex items-center justify-between rounded-xl border border-slate-200 bg-white p-3" data-testid="worker-staff-file-header">
            <div>
              <h2 className="font-semibold text-slate-900">My staff file</h2>
              <p className="text-xs text-slate-600">Documents and employment records, when you need them.</p>
            </div>
            <Button size="sm" variant="outline" onClick={() => setWorkerMainView('more')}>Back</Button>
          </div>
        )}
        {showFileSections && wizardShowsStep('profile_checks') && (!wizardEnabled || profileChecksFocus === 'form') && (
          <FormsSection guidedMode={wizardEnabled} dashboardForms={forms} />
        )}

        {/* One authoritative work history workspace: CV hints, official timeline,
            gaps and final confirmation share one page and one write source. */}
        {(showEmploymentGapsRepairSection || showEmploymentIntegritySection) && (!isActiveEmployee || showFileSections) && (
          <EmploymentGapsSection
            providerName={providerName}
            onUpdated={fetchDashboard}
            forceWorkHistoryRepair={employmentHistoryNeedsAction || profileChecksFocus === 'employment_history'}
            integrity={showEmploymentIntegritySection ? employmentIntegrity : null}
          />
        )}

        {/* CV rejection/replacement alerts are surfaced once via NextActionCard + Documents status. */}
        {showCvNotificationAlert && !wizardEnabled && (!isActiveEmployee || showFileSections) ? (
          <OnboardingPremiumCard
            tone="critical"
            data-testid="cv-action-alert"
            title={unresolvedCvNotifications.some((entry) => entry.type === 'cv_replacement_requested') ? 'Updated CV requested' : 'CV requires attention'}
            description={unresolvedCvNotifications[0]?.message || 'Admin has reviewed your CV and needs you to take action'}
            icon={AlertTriangle}
            action={<Badge className="bg-red-100 text-red-700">Action Required</Badge>}
          >
            <p className="text-sm text-slate-700">
              {unresolvedCvNotifications[0]?.type === 'cv_rejected'
                ? 'Please explain any employment gaps or upload an updated CV from the Documents section below.'
                : 'Upload an updated CV covering the requested information. Your previous CV stays safely on file.'}
            </p>
          </OnboardingPremiumCard>
        ) : null}

        {showApplicantCvRepairSection && (!cvUploadHandledAbove || wizardEnabled) && (
          <OnboardingPremiumCard
            data-testid="documents-card"
            tone={wizardEnabled ? 'critical' : undefined}
            title={wizardEnabled ? (cvDisplay.title || 'Upload your CV') : 'Documents'}
            description={wizardEnabled
              ? cvDisplay.description
              : 'Upload or replace your CV when requested.'}
            icon={FileText}
            action={(
              <Badge className={
                cvDisplay.tone === 'success' ? 'bg-green-100 text-green-700' :
                cvDisplay.tone === 'critical' ? 'bg-red-100 text-red-700' :
                'bg-blue-100 text-blue-700'
              }>
                {cvDisplay.badge}
              </Badge>
            )}
          >
              <div className="rounded-xl border border-slate-200 bg-white p-4">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <p className="font-medium text-slate-900">CV / Resume</p>
                    <p className="mt-1 text-sm text-slate-600">{cvDisplay.description}</p>
                  </div>
                  <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row">
                    {workerMayUploadCv(cvStatus) && (!cvUploadHandledAbove || wizardEnabled) ? (
                      <Button className="w-full sm:w-auto" onClick={triggerCvFileInput} disabled={uploading === 'cv'}>
                        {uploading === 'cv' ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Upload className="mr-2 h-4 w-4" />}
                        {cvDisplay.primaryLabel || (cvDisplay.hasCv ? 'Upload replacement CV' : 'Upload CV')}
                      </Button>
                    ) : cvUploadHandledAbove && !wizardEnabled ? (
                      <p className="text-sm text-slate-500">Use the button above to upload your CV.</p>
                    ) : null}
                  </div>
                </div>
                {workerMayUploadCv(cvStatus) && (!cvUploadHandledAbove || wizardEnabled) ? (
                  <div className="mt-4 rounded-xl border border-emerald-100 bg-emerald-50/80 px-4 py-3">
                    <p className="text-xs font-semibold uppercase tracking-[0.18em] text-emerald-800">Before you upload</p>
                    <ul className="mt-2 space-y-1 text-sm text-emerald-900">
                      {CV_UPLOAD_GUIDANCE.map((item) => (
                        <li key={item} className="flex gap-2">
                          <CheckCircle className="mt-0.5 h-4 w-4 flex-shrink-0 text-emerald-700" />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                    <p className="mt-2 text-xs text-emerald-800">
                      If the reader misses a role, you can still complete the employment timeline manually.
                    </p>
                  </div>
                ) : null}
              </div>
          </OnboardingPremiumCard>
        )}

        {/* ========== REFERENCE-EMPLOYMENT MISMATCH ALERT ========== */}
        {/* CQC: filter out mismatches for references that admin has already
            requested replacement for. Once admin clicks "Request new
            referee" the worker no longer needs to re-explain the previous
            mismatch — they just need to provide fresh details. Cross-check
            against the references list below where can_provide_new=true
            indicates the slot is awaiting a new referee. */}
        {(() => {
          const replacementRefNums = new Set(
            (references || [])
              .filter(r => r?.can_provide_new === true)
              .map(r => r.reference_number)
          );
          const activeMismatches = (referenceMismatches?.mismatches || []).filter(
            m => !replacementRefNums.has(m.reference_number)
          );
          if (wizardEnabled || activeMismatches.length === 0) return null;
          return (
          <OnboardingPremiumCard
            tone="warning"
            data-testid="reference-mismatch-alert"
            title="Reference details need a quick check"
            description={`${activeMismatches.length} reference response${activeMismatches.length === 1 ? '' : 's'} need${activeMismatches.length === 1 ? 's' : ''} a short explanation`}
            icon={Link2}
            action={<Badge className="bg-amber-100 text-amber-700">Action Required</Badge>}
          >
              <div className="space-y-3">
                {activeMismatches.map((mismatch, idx) => (
                  <div 
                    key={idx}
                    className={`p-4 rounded-xl border ${
                      mismatch.mismatch_admin_decision === 'accepted' ? 'bg-green-50 border-green-200' :
                      mismatch.mismatch_admin_decision === 'rejected' ? 'bg-red-50 border-red-200' :
                      mismatch.mismatch_admin_decision === 'needs_clarification' ? 'bg-amber-50 border-amber-400' :
                      mismatch.explanation_status === 'submitted' ? 'bg-blue-50 border-blue-200' :
                      'bg-white border-amber-200'
                    }`}
                    data-testid={`mismatch-ref-${mismatch.reference_number}`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-3">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                          mismatch.mismatch_admin_decision === 'accepted' ? 'bg-green-100' :
                          mismatch.mismatch_admin_decision === 'rejected' ? 'bg-red-100' :
                          mismatch.mismatch_admin_decision === 'needs_clarification' ? 'bg-amber-100' :
                          mismatch.explanation_status === 'submitted' ? 'bg-blue-100' :
                          'bg-amber-100'
                        }`}>
                          {mismatch.mismatch_admin_decision === 'accepted' ? (
                            <CheckCircle className="h-5 w-5 text-green-600" />
                          ) : mismatch.mismatch_admin_decision === 'rejected' ? (
                            <AlertCircle className="h-5 w-5 text-red-600" />
                          ) : mismatch.explanation_status === 'submitted' ? (
                            <Clock className="h-5 w-5 text-blue-600" />
                          ) : (
                            <AlertTriangle className="h-5 w-5 text-amber-600" />
                          )}
                        </div>
                        <div>
                          <p className="font-medium text-slate-800">
                            Reference {mismatch.reference_number}: {mismatch.referee_name}
                          </p>
                          <p className="text-sm text-slate-600">{mismatch.referee_company}</p>
                          <p className="text-xs text-amber-700 mt-1">{getWorkerReferenceDifferenceText(mismatch.message)}</p>
                          {mismatch.comparison?.email_mismatch && (
                            <p className="text-xs text-amber-900 mt-1 font-medium">
                              Email: sent to {mismatch.comparison.request_sent_to}; reply came from {mismatch.comparison.returned?.email}
                            </p>
                          )}
                          
                          {/* Show existing explanation */}
                          {mismatch.existing_explanation && (
                            <div className="mt-2 p-2 bg-slate-100 rounded-lg">
                              <p className="text-xs text-slate-600">
                                <span className="font-medium">Your explanation:</span> {mismatch.existing_explanation.text}
                              </p>
                            </div>
                          )}
                          
                          {/* Show admin decision */}
                          {mismatch.mismatch_admin_decision && (
                            <p className={`text-xs mt-1 ${
                              mismatch.mismatch_admin_decision === 'accepted' ? 'text-green-600' :
                              mismatch.mismatch_admin_decision === 'rejected' ? 'text-red-600' :
                              'text-blue-600'
                            }`}>
                              Review update: {getWorkerReferenceDifferenceText(mismatch.mismatch_admin_decision)}
                              {mismatch.admin_notes && ` - ${mismatch.admin_notes}`}
                            </p>
                          )}
                        </div>
                      </div>
                      
                      <div className="flex flex-col items-end gap-2">
                        {mismatch.mismatch_admin_decision === 'accepted' ? (
                          <Badge className="bg-green-100 text-green-700 text-xs">
                            <CheckCircle className="h-3 w-3 mr-1" />
                            Accepted
                          </Badge>
                        ) : mismatch.mismatch_admin_decision === 'rejected' ? (
                          <>
                            <Badge className="bg-red-100 text-red-700 text-xs">
                              <AlertCircle className="h-3 w-3 mr-1" />
                              Rejected
                            </Badge>
                            <Button
                              size="sm"
                              onClick={() => openMismatchExplanationModal(mismatch)}
                              className="gap-1 bg-red-600 hover:bg-red-700"
                              data-testid={`re-explain-mismatch-${mismatch.reference_number}`}
                            >
                              <MessageSquare className="h-4 w-4" />
                              Re-explain
                            </Button>
                          </>
                        ) : mismatch.mismatch_admin_decision === 'needs_clarification' ? (
                          <>
                            <Badge className="bg-amber-100 text-amber-700 text-xs">
                              <Clock className="h-3 w-3 mr-1" />
                              More Info Needed
                            </Badge>
                            <Button
                              size="sm"
                              onClick={() => openMismatchExplanationModal(mismatch)}
                              className="gap-1 bg-amber-600 hover:bg-amber-700"
                              data-testid={`clarify-mismatch-${mismatch.reference_number}`}
                            >
                              <MessageSquare className="h-4 w-4" />
                              Respond
                            </Button>
                          </>
                        ) : mismatch.explanation_status === 'submitted' ? (
                          <Badge className="bg-blue-100 text-blue-700 text-xs">
                            <Clock className="h-3 w-3 mr-1" />
                            Under Review
                          </Badge>
                        ) : (
                          <Button
                            size="sm"
                            onClick={() => openMismatchExplanationModal(mismatch)}
                            className="gap-1 bg-amber-600 hover:bg-amber-700"
                            data-testid={`explain-mismatch-${mismatch.reference_number}`}
                          >
                            <MessageSquare className="h-4 w-4" />
                            Explain
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              
              <p className="text-xs text-amber-700 mt-4 p-3 bg-amber-100/50 rounded-lg">
                {providerName} checks references against your work history before your file can move forward.
                Please explain any differences so the team can complete your review.
              </p>
          </OnboardingPremiumCard>
          );
        })()}

        {/* Professional Registration Status - if applicable */}
        {showFileSections && wizardShowsStep('identity_documents') && professional_registration && (!wizardEnabled || showNmcInWizard) && (
          <Card className={`shadow-md border-0 ${
            professional_registration.status === 'verified' ? 'bg-green-50/30' :
            professional_registration.status === 'pending_verification' ? 'bg-amber-50/30' :
            'bg-red-50/30'
          }`} data-testid="professional-registration-section">
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2 text-lg">
                <Shield className={`h-5 w-5 ${
                  professional_registration.status === 'verified' ? 'text-green-600' :
                  professional_registration.status === 'pending_verification' ? 'text-amber-600' :
                  'text-red-600'
                }`} />
                Professional Registration ({professional_registration.type})
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="p-4 bg-white rounded-xl">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                      professional_registration.status === 'verified' ? 'bg-green-100' :
                      professional_registration.status === 'pending_verification' ? 'bg-amber-100' :
                      'bg-red-100'
                    }`}>
                      <Shield className={`h-5 w-5 ${
                        professional_registration.status === 'verified' ? 'text-green-600' :
                        professional_registration.status === 'pending_verification' ? 'text-amber-600' :
                        'text-red-600'
                      }`} />
                    </div>
                    <div>
                      <p className="font-medium text-slate-800">
                        {professional_registration.type} Registration
                      </p>
                      {professional_registration.number ? (
                        <p className="text-sm text-slate-600">
                          Reg No: {professional_registration.number}
                        </p>
                      ) : (
                        <p className="text-sm text-red-600">
                          Not submitted - Required for your role
                        </p>
                      )}
                      {professional_registration.expiry_date && (
                        <p className="text-xs text-slate-500">
                          Expires: {formatDate(professional_registration.expiry_date)}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {professional_registration.status === 'verified' ? (
                      <Badge className="bg-green-100 text-green-700">
                        <CheckCircle className="h-3 w-3 mr-1" />
                        Verified
                      </Badge>
                    ) : professional_registration.status === 'pending_verification' ? (
                      <Badge className="bg-amber-100 text-amber-700">
                        <Clock className="h-3 w-3 mr-1" />
                        Waiting for {providerName} review
                      </Badge>
                    ) : (
                      <Badge className="bg-red-100 text-red-700">
                        <AlertCircle className="h-3 w-3 mr-1" />
                        Required
                      </Badge>
                    )}
                  </div>
                </div>
                {professional_registration.status !== 'verified' && (
                  <div className="mt-4 flex flex-wrap gap-2 border-t border-slate-100 pt-4">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => triggerFileInput('nmc_registration')}
                      disabled={uploading === 'nmc_registration'}
                      data-testid="upload-nmc-certificate-btn"
                    >
                      {uploading === 'nmc_registration' ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <Upload className="h-4 w-4 mr-2" />
                      )}
                      Upload certificate
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => setShowNmcDialog(true)}
                      data-testid="submit-nmc-pin-btn"
                    >
                      Submit {professional_registration.type} PIN
                    </Button>
                  </div>
                )}
                {professional_registration.certificate_uploaded && professional_registration.status !== 'verified' && (
                  <p className="mt-2 text-xs text-green-700">Certificate uploaded — awaiting admin verification.</p>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Renewal alerts for applicants/pre-employment (active staff use Compliance due on Today tab) */}
        {showTodaySections && !isActiveEmployee && !wizardEnabled && alerts.length > 0 && (
          <Card className="border-red-200 bg-red-50/50 shadow-md border-0" data-testid="alerts-section">
            <CardHeader className="pb-2">
              <CardTitle className="text-red-800 flex items-center gap-2 text-lg">
                <AlertTriangle className="h-5 w-5" />
                Upcoming Renewals
              </CardTitle>
              <p className="text-xs text-slate-500 mt-1">
                Documents or training expiring soon. Upload renewals before they expire.
              </p>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {alerts.map((alert, idx) => {
                  const uploadId = getAlertUploadRequirementId(alert);
                  const canUpload = alertSupportsWorkerUpload(alert) && uploadId;
                  const getSeverityColors = (days) => {
                    if (days <= 30) return { bg: 'bg-red-50', border: 'border-red-200', badge: 'bg-red-100 text-red-700', dot: 'bg-red-500' };
                    if (days <= 60) return { bg: 'bg-amber-50', border: 'border-amber-200', badge: 'bg-amber-100 text-amber-700', dot: 'bg-amber-500' };
                    return { bg: 'bg-yellow-50', border: 'border-yellow-200', badge: 'bg-yellow-100 text-yellow-700', dot: 'bg-yellow-500' };
                  };
                  const severity = getSeverityColors(alert.days_left);
                  
                  return (
                    <div key={idx} className={`flex items-center justify-between p-4 ${severity.bg} border ${severity.border} rounded-xl`} data-testid={`alert-${alert.type}`}>
                      <div className="flex items-center gap-3">
                        <div className={`w-3 h-3 rounded-full ${severity.dot} animate-pulse`} />
                        <div>
                          <p className="font-medium text-slate-800">{alert.title}</p>
                          <p className="text-xs text-slate-500">Expires: {formatDate(alert.date)}</p>
                          {!canUpload && alert.action_note ? (
                            <p className="text-xs text-slate-500 mt-1">{alert.action_note}</p>
                          ) : null}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge className={severity.badge}>
                          {alert.days_left <= 0 ? 'EXPIRED' : `${alert.days_left} days`}
                        </Badge>
                        {canUpload ? (
                          <Button 
                            size="sm" 
                            variant={alert.urgent ? "default" : "outline"}
                            className={`gap-1 ${alert.urgent ? 'bg-red-600 hover:bg-red-700' : ''}`}
                            onClick={() => triggerFileInput(uploadId)}
                            disabled={uploading === uploadId}
                            data-testid={`upload-renewal-${alert.type}`}
                          >
                            {uploading === uploadId ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <RefreshCw className="h-4 w-4" />
                            )}
                            Upload Renewal
                          </Button>
                        ) : (
                          <Badge className="bg-slate-100 text-slate-700">Manager-led</Badge>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Right to Work belongs to the identity stage. Keep it off unrelated applicant steps. */}
        {showFileSections && wizardShowsStep('identity_documents') ? (
          <div className="mb-4">
            <WorkerRTWShareCode />
          </div>
        ) : null}

        {/* Priority 2: Legal Verification Documents - Must for any care work (RTW, DBS, Identity, PoA, NMC if applicable). These are non-negotiable for compliance. */}
        {showFileSections && wizardShowsStep('identity_documents') && (
          (!wizardEnabled && ((!isActiveEmployee && visibleMissingDocuments.length > 0) || visibleMissingDocuments.some(d => d.rejection) || visibleMissingDocuments.some(d => d.poa_upload_needed)))
          || (wizardEnabled && identityDocumentsForDisplay.length > 0)
        ) && (
          <>
          {!wizardEnabled && (
            <div className="mb-2 flex items-center gap-2">
              <OnboardingPriorityPill variant="legal">STEP 2 - IDENTITY DOCUMENTS</OnboardingPriorityPill>
            </div>
          )}
          <OnboardingPremiumCard
            tone={identityDocumentsForDisplay.some(d => d.rejection) ? 'critical' : 'warning'}
            data-testid="missing-documents-section"
            title={identityDocumentsForDisplay.some(d => d.rejection)
              ? 'Please re-upload this document'
              : wizardEnabled && identityDocumentsForDisplay.every(isPoaDoc)
                ? 'Proof of address (2 documents)'
                : wizardEnabled && identityDocumentsForDisplay.length === 1 && isIdentityDoc(identityDocumentsForDisplay[0])
                  ? 'Proof of identity'
                  : wizardEnabled
                    ? 'Upload this document'
                  : lifecycleStage === 'recruitment'
                    ? 'Documents to check'
                    : 'Documents needed'}
            icon={Upload}
          >
              {identityDocumentsForDisplay.some(isIdentityDoc) && (
                <p className="mb-3 text-xs text-slate-600 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
                  Upload your passport or driving licence (front and back if using a licence).
                  This is not your Right to Work check. {providerName} will confirm the original before final approval.
                </p>
              )}
              {identityDocumentsForDisplay.some(isPoaDoc) && (
                <p className="mb-3 text-xs text-slate-600 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
                  Upload two different proof-of-address documents (dated within 12 months).
                  Scanned copies are reviewed here. {providerName} will confirm originals before final approval.
                </p>
              )}
              <div className="space-y-3">
                {identityDocumentsForDisplay.map((doc, idx) => (
                  <div 
                    key={idx} 
                    className={`p-4 rounded-xl ${doc.rejection ? 'bg-red-50 border border-red-200' : 'bg-slate-50'}`}
                    data-testid={`missing-doc-${doc.type}`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${doc.rejection ? 'bg-red-100' : 'bg-slate-100'}`}>
                          {doc.rejection ? (
                            <AlertTriangle className="h-5 w-5 text-red-500" />
                          ) : (
                            <X className="h-5 w-5 text-slate-400" />
                          )}
                        </div>
                        <div>
                          <span className="font-medium text-slate-800">{doc.name}</span>
                          {doc.rejection && (
                            <p className="text-xs text-red-600 font-medium">
                              Please re-upload
                            </p>
                          )}
                        </div>
                      </div>
                      {(docSlotKey(doc) in justSubmittedDocs) ? (
                        <div
                          className="flex items-center gap-2 rounded-lg border border-purple-200 bg-purple-50 px-3 py-2 text-xs font-medium text-purple-700"
                          data-testid={`doc-submitted-${doc.type}`}
                        >
                          <Clock className="h-4 w-4" />
                          <span>
                            Document submitted
                            <span className="block font-normal text-purple-600">Waiting for admin review</span>
                          </span>
                        </div>
                      ) : (IDENTITY_CAPTURE_DOC_TYPES.has(doc.type) || isPoaDoc(doc)) && !doc.rejection ? (
                        <DocumentCaptureField
                          label={doc.name}
                          multiple={doc.type === 'proof_of_address' && !doc.type?.endsWith('_2')}
                          uploading={uploading === doc.type}
                          onFileSelected={(fileOrFiles) => {
                            if (Array.isArray(fileOrFiles)) {
                              handleMultiFileUpload(doc.type, fileOrFiles, docSlotKey(doc));
                            } else {
                              handleFileUpload(doc.type, fileOrFiles, docSlotKey(doc));
                            }
                          }}
                        />
                      ) : (
                        <Button 
                          size="sm" 
                          onClick={() => triggerFileInput(doc.type)}
                          disabled={uploading === doc.type}
                          className={`gap-1 ${doc.rejection ? 'bg-red-600 hover:bg-red-700' : 'bg-blue-600 hover:bg-blue-700'}`}
                          data-testid={`upload-${doc.type}`}
                        >
                          {uploading === doc.type ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <Upload className="h-4 w-4" />
                          )}
                          {doc.rejection ? 'Re-upload' : 'Upload'}
                        </Button>
                      )}
                    </div>
                    {/* Show rejection reason */}
                    {doc.rejection && (
                      <div className="ml-13 pl-1 mt-2 p-3 bg-red-100 rounded-lg border border-red-200">
                        <p className="text-sm text-red-700 font-medium mb-1">
                          <AlertTriangle className="h-4 w-4 inline mr-1" />
                          What needs to change:
                        </p>
                        <p className="text-sm text-red-600">
                          {doc.rejection.rejection_reason}
                        </p>
                        <p className="text-xs text-red-500 mt-1">
                          Previous file: {doc.rejection.previous_file_name} • 
                          Checked by: {doc.rejection.rejected_by_name}
                        </p>
                      </div>
                    )}
                    {!doc.rejection && (
                      <>
                        <p className="text-xs text-slate-500 ml-13 pl-1">
                          {getDocumentGuidance(doc.type, providerName)}
                        </p>
                        <p className="text-xs text-slate-400 ml-13 pl-1 mt-1">
                          {ACCEPTED_FORMATS}
                        </p>
                      </>
                    )}
                  </div>
                ))}
              </div>
          </OnboardingPremiumCard>
        </>
        )}

        {/* Expired Training */}
        {isActiveEmployee && workerMainView === 'compliance' && expired_trainings?.length > 0 && (
          <Card className="shadow-md border-0">
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2 text-lg">
                <Clock className="h-5 w-5 text-red-500" />
                Expired Training
              </CardTitle>
            </CardHeader>
            <CardContent>
              {trainingUploadFeedback?.requirementId?.startsWith('training_') && (
                <div
                  role={trainingUploadFeedback.status === 'error' ? 'alert' : 'status'}
                  aria-live="polite"
                  className={`mb-4 rounded-xl border p-3 text-sm ${trainingUploadFeedback.status === 'error'
                    ? 'border-red-200 bg-red-50 text-red-900'
                    : 'border-teal-200 bg-teal-50 text-teal-900'}`}
                >
                  {trainingUploadFeedback.message}
                </div>
              )}
              <div className="space-y-2">
                {expired_trainings.map((training, idx) => (
                  <div key={idx} className="flex items-center justify-between p-4 bg-red-50 rounded-xl">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                        <AlertTriangle className="h-5 w-5 text-red-500" />
                      </div>
                      <div>
                        <span className="font-medium text-slate-800">{training.name}</span>
                        <p className="text-xs text-red-600">Expired: {formatDate(training.expiry_date)}</p>
                      </div>
                    </div>
                    <Button 
                      size="sm" 
                      onClick={() => triggerFileInput(`training_${training.id}`)}
                      disabled={uploading === `training_${training.id}` ||
                        Boolean(trainingUploadReceipts[`training_${training.id}`]) ||
                        (trainingUploadFeedback?.status === 'success' &&
                         trainingUploadFeedback?.requirementId === `training_${training.id}`)}
                      className="gap-1"
                      data-testid={`upload-training-${training.id}`}
                    >
                      {uploading === `training_${training.id}` ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Upload className="h-4 w-4" />
                      )}
                      {uploading === `training_${training.id}` ? 'Uploading...' : 'Upload Certificate'}
                    </Button>
                    <p className="text-xs text-slate-600">Your organisation will arrange any required renewal training.</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* C3F canonical interview surface (Block A): the full journey card for
            every applicant-actionable / pre-appointment / terminal state that is
            NOT a live upcoming appointment. Driven by interview_state, not the
            wizard active step. Mutually exclusive with the upcoming-appointment
            card below via !hasScheduledInterview, so exactly ONE renders. */}
        {interviewSurface === 'journey' && wizardShowsStep('interview') && (
          <InterviewAppointmentCard
            interviewForm={interview_form}
            appointment={interview_appointment}
            bookingSlots={interview_booking_slots}
            interviewState={interview_state}
            onRefresh={fetchDashboard}
            onOpenQuestionnaire={(form) => navigate(`/worker/forms/${form.id}`)}
            onGoToTask={(task) => handleNextAction({ route: task.route })}
            isFormWorkerActionRequired={isFormWorkerActionRequired}
            getFormActionLabel={getFormActionLabel}
            getFormActionButtonClass={getFormActionButtonClass}
          />
        )}

        {/* Applicant training: one decision at a time. Course links, pricing and
            enrolment belong to the employer's admin journey, never the applicant. */}
        {!isActiveEmployee && wizardShowsStep('mandatory_training') && (
          <>
            <div className="mb-2 flex items-center gap-2">
              <OnboardingPriorityPill variant="training">STEP 3 - TRAINING CERTIFICATES</OnboardingPriorityPill>
            </div>
            <OnboardingPremiumCard
              tone="info"
              data-testid="training-card"
              title="Your training"
              description="Add only the missing or expiring certificate. We handle the rest."
              icon={GraduationCap}
            >
              <TrainingCertificateGrid
                trainings={actionableTrainings}
                allTrainings={all_mandatory_trainings}
                uploading={uploading}
                feedback={trainingUploadFeedback}
                onUpload={handleFileUpload}
              />
              <div className="mt-3 space-y-2">
                <WorkerTrainingRequestButton trainings={actionableTrainings} />
                <p className="text-xs text-slate-600">
                  Your organisation arranges training and checks certificates. You don't pay here.
                </p>
              </div>
            </OnboardingPremiumCard>
          </>
        )}

        {/* ========== RECOMMENDED TRAINING ========== */}
        {!isActiveEmployee && !wizardEnabled && recommended_trainings?.length > 0 && (
          <OnboardingPremiumCard data-testid="recommended-training-section" title="Recommended Training" description="These trainings are beneficial but not required for work readiness" icon={FileText}>
              <div className="space-y-2">
                {recommended_trainings.map((training, idx) => {
                  const cfg = TRAINING_STATUS_CONFIG[training.status] || TRAINING_STATUS_CONFIG.missing;
                  const StatusIcon = cfg.Icon;
                  return (
                  <div 
                    key={idx} 
                    className={`p-3 rounded-xl border ${cfg.cardBg}`}
                    data-testid={`recommended-training-row-${training.id}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${cfg.iconBg}`}>
                          <StatusIcon className={`h-4 w-4 ${cfg.iconCls}`} />
                        </div>
                        <div>
                          <span className="font-medium text-sm text-slate-700">{training.name}</span>
                          {training.status === 'rejected' && training.rejection_reason && (
                            <p className="text-xs text-red-600">Reason: {training.rejection_reason}</p>
                          )}
                          {training.status === 'due_soon' && training.days_until_expiry != null && (
                            <p className="text-xs text-amber-600">Expires in {training.days_until_expiry} days</p>
                          )}
                          {training.expiry_date && training.status !== 'due_soon' && (
                            <p className={`text-xs ${training.status === 'expired' ? 'text-red-600' : 'text-slate-500'}`}>
                              {training.status === 'expired' ? 'Expired: ' : 'Expires: '}{formatDate(training.expiry_date)}
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge className={`text-xs ${cfg.badgeCls}`}>
                          <StatusIcon className="h-3 w-3 mr-1" />
                          {training.status === 'missing' ? 'Optional' : cfg.badge}
                        </Badge>
                        {cfg.showUpload && (
                          <Button 
                            size="sm" 
                            variant={training.status === 'rejected' || training.status === 'expired' ? 'default' : 'outline'}
                            onClick={() => triggerFileInput(`training_${training.id}`)}
                            disabled={uploading === `training_${training.id}`}
                            className={training.status === 'rejected' || training.status === 'expired' ? 'gap-1 text-xs bg-red-600 hover:bg-red-700 text-white' : 'gap-1 text-xs'}
                            data-testid={`upload-training-${training.id}`}
                          >
                            {uploading === `training_${training.id}` ? (
                              <Loader2 className="h-3 w-3 animate-spin" />
                            ) : (
                              <Upload className="h-3 w-3" />
                            )}
                            {cfg.uploadLabel}
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                  );
                })}
              </div>
          </OnboardingPremiumCard>
        )}

        {/* ========== REFERENCES — only when applicant must act (wizard: one task at a time) ========== */}
        {!isActiveEmployee
          && wizardShowsStep('profile_checks')
          && references?.length > 0
          && (!wizardEnabled || (profileChecksFocus === 'reference' && activeReferenceForWizard)) && (
          <OnboardingPremiumCard
            tone={wizardEnabled ? 'critical' : 'purple'}
            data-testid="references-section"
            header={(
              <div className="mb-4">
                <WorkerPremiumCardHeader
                  icon={wizardEnabled ? AlertTriangle : User}
                  title={wizardEnabled
                    ? (profileChecksTask?.title || 'Reference needs your attention')
                    : 'References (2 required)'}
                  description={wizardEnabled
                    ? (profileChecksTask?.description || 'Please add or update your referee details.')
                    : 'We will contact your referees after you submit their details.'}
                  action={wizardEnabled ? (
                    <Badge className="bg-red-100 text-red-700">Action required</Badge>
                  ) : (
                    <Badge className={`${
                      referencesProgressSummary.tone === 'success'
                        ? 'bg-green-100 text-green-700'
                        : referencesProgressSummary.tone === 'info'
                          ? 'bg-blue-100 text-blue-700'
                          : 'bg-amber-100 text-amber-700'
                    }`}>
                      {referencesProgressSummary.label}
                    </Badge>
                  )}
                />
                {!wizardEnabled ? (
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="mt-2 rounded-xl"
                    onClick={() => setShowReferencesDetails((v) => !v)}
                    data-testid="toggle-references-details"
                  >
                    {showReferencesDetails ? 'Hide details' : 'Show details'}
                    {showReferencesDetails ? <ChevronUp className="ml-1 h-4 w-4" /> : <ChevronDown className="ml-1 h-4 w-4" />}
                  </Button>
                ) : null}
              </div>
            )}
          >
              {!wizardEnabled && !showReferencesDetails ? (
                <div className="space-y-2">
                  <p className="text-sm text-slate-600">
                    References collapsed to reduce noise. Expand to view referee status and required actions.
                  </p>
                  {(() => {
                    const quickExplainRef = (references || []).find((ref) => {
                      const status = String(ref?.status || '').toLowerCase();
                      const notReplacement = !ref?.replacement_requested_at && !ref?.can_provide_new;
                      return (
                        notReplacement &&
                        !isReferenceComplete(ref?.status) &&
                        (status.includes('response_received') || status.includes('pending_admin_review'))
                      );
                    });
                    if (!quickExplainRef) return null;
                    return (
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-xs border-amber-300 text-amber-700 hover:bg-amber-50"
                        onClick={() => {
                          setSelectedMismatch({
                            reference_number: quickExplainRef.reference_number,
                            referee_name: quickExplainRef.referee_name,
                            referee_company: quickExplainRef.organisation || quickExplainRef.company,
                          message: 'Please explain why this reference is different from your application.',
                          });
                          setMismatchExplanationType('');
                          setMismatchExplanationText('');
                          setShowMismatchExplanationModal(true);
                        }}
                        data-testid="quick-explain-mismatch-collapsed"
                      >
                        Explain reference difference
                      </Button>
                    );
                  })()}
                </div>
              ) : (
              <div className="space-y-2">
                {(wizardEnabled
                  ? (activeReferenceForWizard ? [activeReferenceForWizard] : [])
                  : references
                ).map((ref, idx) => (
                  <div 
                    key={idx} 
                    className={`p-4 rounded-xl border ${
                      ref.status === 'verified' ? 'bg-green-50 border-green-200' :
                      ref.status === 'rejected' || ref.status === 'needs_new_input' ? 'bg-red-50 border-red-200' :
                      ref.status === 'response_received' ? 'bg-blue-50 border-blue-200' :
                      ref.status === 'sent' ? 'bg-amber-50 border-amber-200' :
                      'bg-slate-50 border-slate-200'
                    }`}
                    data-testid={`reference-${ref.reference_number}`}
                  >
                    <div className="flex min-w-0 flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                      <div className="flex min-w-0 items-start gap-3">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                          ref.status === 'verified' ? 'bg-green-100' :
                          ref.status === 'rejected' || ref.status === 'needs_new_input' ? 'bg-red-100' :
                          ref.status === 'response_received' ? 'bg-blue-100' :
                          ref.status === 'sent' ? 'bg-amber-100' :
                          'bg-slate-100'
                        } shrink-0`}>
                          {ref.status === 'verified' ? (
                            <CheckCircle className="h-5 w-5 text-green-600" />
                          ) : ref.status === 'rejected' || ref.status === 'needs_new_input' ? (
                            <AlertCircle className="h-5 w-5 text-red-600" />
                          ) : ref.status === 'response_received' ? (
                            <Clock className="h-5 w-5 text-blue-600" />
                          ) : ref.status === 'sent' ? (
                            <Clock className="h-5 w-5 text-amber-600" />
                          ) : (
                            <AlertCircle className="h-5 w-5 text-slate-400" />
                          )}
                        </div>
                        <div className="min-w-0">
                          <span className="block break-words font-medium text-slate-800">Reference {ref.reference_number}</span>
                          {ref.referee_name && (
                            <p className="text-xs text-slate-500">{ref.referee_name}</p>
                          )}
                          {ref.verified_at && ref.verified_by_name && (
                            <p className="text-xs text-green-600">
                              Verified by {ref.verified_by_name} on {formatDate(ref.verified_at)}
                            </p>
                          )}
                          {/* Show rejection reason if reference was rejected */}
                          {ref.rejection_reason && (
                            <p className="text-xs text-red-600 mt-1">
                              Reason: {ref.rejection_reason}
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="flex w-full min-w-0 flex-col gap-2 sm:w-auto sm:min-w-[12rem] sm:shrink-0 sm:flex-row sm:flex-wrap sm:items-center sm:justify-end">
                        {/* Worker mismatch explanation action (primary legal evidence path) */}
                        {(() => {
                          const refNum = Number(ref.reference_number);
                          const mismatch = mismatchByRefNum.get(refNum);
                          const status = String(ref.status || '').toLowerCase();
                          const refHasMismatchFlag =
                            ref?.integrity?.mismatch_detected === true ||
                            ref?.mismatch_detected === true ||
                            ref?.requires_mismatch_explanation === true ||
                            ref?.awaiting_worker_explanation === true;
                          const mismatchTextHint = String(
                            ref?.status_message ||
                            ref?.readiness_note ||
                            ref?.worker_task_text ||
                            ''
                          ).toLowerCase();
                          const statusImpliesMismatchContext =
                            status.includes('mismatch') ||
                            status.includes('response_received') ||
                            status.includes('awaiting_review') ||
                            status.includes('pending_admin_review') ||
                            status.includes('reviewed') ||
                            mismatchTextHint.includes('mismatch') ||
                            mismatchTextHint.includes('awaiting worker explanation');
                          const needsExplanation = Boolean(
                            ref?.requires_mismatch_explanation ||
                            ref?.needs_explanation ||
                            (Boolean(mismatch) && mismatch.needs_explanation !== false) ||
                            (refHasMismatchFlag && statusImpliesMismatchContext)
                          );
                          const isReplacementFlow = Boolean(ref.replacement_requested_at);
                          if (isReplacementFlow) return null;
                          if (!needsExplanation) return null;
                          const fallbackMessage =
                            status.includes('pending_admin_review')
                            ? 'Your reference response is being reviewed. If anything looks different, add a short note here to avoid delays.'
                              : 'Please explain why this reference is different from your application.';
                          const hasExistingExplanation = Boolean(
                            mismatch?.existing_explanation?.text || mismatch?.existing_explanation
                          );
                          const mismatchPayload = mismatch || {
                            reference_number: ref.reference_number,
                            referee_name: ref.referee_name,
                            referee_company: ref.organisation || ref.company,
                            message: fallbackMessage,
                            existing_explanation: hasExistingExplanation
                              ? mismatch.existing_explanation
                              : undefined,
                          };
                          const mismatchType =
                            String(
                              mismatch?.mismatch_type ||
                              mismatch?.type ||
                              mismatch?.existing_explanation?.type ||
                              ''
                            ).toLowerCase();
                          const isIdentityMismatch =
                            mismatchType.includes('identity') ||
                            mismatchType.includes('email') ||
                            mismatchType.includes('name') ||
                            mismatchType.includes('organisation');
                          const isEmploymentMismatch =
                            mismatchType.includes('employment') ||
                            mismatchType.includes('recent_employer') ||
                            mismatchType.includes('earlier_employer');
                          const buttonLabel = hasExistingExplanation
                            ? 'Update explanation'
                            : isIdentityMismatch
                              ? 'Explain referee details'
                              : isEmploymentMismatch
                                ? 'Explain work history'
                                : 'Explain difference';
                          return (
                            <Button
                              size="sm"
                              variant="outline"
                              className="w-full justify-center text-xs border-amber-300 text-amber-700 hover:bg-amber-50 sm:w-auto"
                              onClick={() => {
                                setSelectedMismatch(mismatchPayload);
                                setMismatchExplanationType('');
                                setMismatchExplanationText('');
                                setShowMismatchExplanationModal(true);
                              }}
                              data-testid={`explain-mismatch-ref-${ref.reference_number}`}
                            >
                              {buttonLabel}
                            </Button>
                          );
                        })()}
                        {/* Show "Provide New" button for rejected references */}
                        {ref.can_provide_new && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="w-full justify-center text-xs border-primary text-primary hover:bg-primary hover:text-white sm:w-auto"
                            onClick={() => openReferenceSlot(ref)}
                            data-testid={`provide-new-ref-${ref.reference_number}`}
                          >
                            {provideNewRefNum === ref.reference_number ? 'Cancel' : 'Add referee details'}
                          </Button>
                        )}
                        <Badge className={`w-full justify-center whitespace-normal text-center text-xs sm:w-auto sm:whitespace-nowrap ${
                          ref.status === 'verified' ? 'bg-green-100 text-green-700' :
                          ref.status === 'rejected' || ref.status === 'needs_new_input' ? 'bg-red-100 text-red-700' :
                          ref.status === 'response_received' ? 'bg-blue-100 text-blue-700' :
                          ref.status === 'sent' ? 'bg-amber-100 text-amber-700' :
                          ref.status === 'declared' ? 'bg-slate-100 text-slate-600' :
                          'bg-slate-100 text-slate-500'
                        }`}>
                          {ref.status === 'verified' && <CheckCircle className="h-3 w-3 mr-1" />}
                          {(ref.status === 'rejected' || ref.status === 'needs_new_input') && <AlertCircle className="h-3 w-3 mr-1" />}
                          {(ref.status === 'response_received' || ref.status === 'sent') && <Clock className="h-3 w-3 mr-1" />}
                          {getApplicantReferenceStatusLabel(ref)}
                        </Badge>
                          {/* Inline replacement-referee form (strictly slot-scoped) */}
                          {provideNewRefNum === ref.reference_number && (() => {
                            const slotForm = referenceSlotForm(ref.reference_number);
                            const slotPhase = referenceSlotPhase(ref.reference_number);
                            const slotMismatch = provideNewMismatchBySlot[ref.reference_number];
                            const historyOnlyDraft = isHistoryOnlyReferenceDraft(slotMismatch);
                            const isSubmitting = slotPhase === 'submitting';
                            const isSaved = slotPhase === 'saved';
                            return (
                            <div className="mt-4 w-full min-w-0 basis-full space-y-3 border-t border-slate-200 pt-4">
                              <p className="text-sm font-medium text-slate-700">Add referee details</p>
                              <p className="text-xs text-slate-500">
                                Please use someone who can confirm your work history. We may email them after you submit.
                              </p>
                              {slotMismatch && (historyOnlyDraft ? (
                                <div
                                  className="rounded-lg border border-sky-200 bg-sky-50 p-3 text-xs text-sky-900"
                                  data-testid={`provide-new-history-pending-${ref.reference_number}`}
                                >
                                  <p className="font-medium">You can continue</p>
                                  <p className="mt-1">
                                    Your referee details are saved as a draft. Submit them below — you do not need
                                    to wait for your work history to be checked.
                                  </p>
                                  <p className="mt-2">Your work history will be reviewed separately before recruitment approval.</p>
                                </div>
                              ) : (
                                <div
                                  className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900"
                                  data-testid={`provide-new-mismatch-${ref.reference_number}`}
                                >
                                  <p className="font-medium">We just need a quick check</p>
                                  <p className="mt-1">{slotMismatch.message}</p>
                                  <p className="mt-2 font-medium">You can either:</p>
                                  <ul className="mt-1 list-disc space-y-0.5 pl-4">
                                    <li>{"Correct the referee's details above (for example the organisation), or"}</li>
                                    <li>Tell us why this referee is appropriate using the reason below.</li>
                                  </ul>
                                  <p className="mt-2 text-[11px] text-amber-800">
                                    {"Don't worry \u2014 your details are saved. We won't contact this referee until this is resolved."}
                                  </p>
                                </div>
                              ))}
                              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                                <div>
                                  <label className="text-xs text-slate-500">Full Name *</label>
                                  <input
                                    className="w-full mt-1 px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30"
                                    placeholder="Jane Smith"
                                    disabled={isSubmitting || isSaved}
                                    value={slotForm.name}
                                    onChange={e => setReferenceSlotForm(ref.reference_number, { name: e.target.value })}
                                    data-testid={`provide-new-name-${ref.reference_number}`}
                                  />
                                </div>
                                <div>
                                  <label className="text-xs text-slate-500">Email *</label>
                                  <input
                                    type="email"
                                    className="w-full mt-1 px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30"
                                    placeholder="jane@nhs.net"
                                    disabled={isSubmitting || isSaved}
                                    value={slotForm.email}
                                    onChange={e => setReferenceSlotForm(ref.reference_number, { email: e.target.value })}
                                    data-testid={`provide-new-email-${ref.reference_number}`}
                                  />
                                  <p className="text-[10px] text-slate-500 mt-1">
                                    Use the email your referee is likely to reply from, such as their work email.
                                  </p>
                                </div>
                                <div>
                                  <label className="text-xs text-slate-500">Phone</label>
                                  <input
                                    className="w-full mt-1 px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30"
                                    placeholder="07700 000000"
                                    disabled={isSubmitting || isSaved}
                                    value={slotForm.phone}
                                    onChange={e => setReferenceSlotForm(ref.reference_number, { phone: e.target.value })}
                                    data-testid={`provide-new-phone-${ref.reference_number}`}
                                  />
                                </div>
                                <div>
                                  <label className="text-xs text-slate-500">Organisation *</label>
                                  <input
                                    className="w-full mt-1 px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30"
                                    placeholder="ABC Care Ltd"
                                    disabled={isSubmitting || isSaved}
                                    value={slotForm.organisation}
                                    onChange={e => setReferenceSlotForm(ref.reference_number, { organisation: e.target.value })}
                                    data-testid={`provide-new-org-${ref.reference_number}`}
                                  />
                                </div>
                                <div>
                                  <label className="text-xs text-slate-500">Job Title / Position</label>
                                  <input
                                    className="w-full mt-1 px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30"
                                    placeholder="Line Manager"
                                    disabled={isSubmitting || isSaved}
                                    value={slotForm.position}
                                    onChange={e => setReferenceSlotForm(ref.reference_number, { position: e.target.value })}
                                    data-testid={`provide-new-position-${ref.reference_number}`}
                                  />
                                </div>
                                <div>
                                  <label className="text-xs text-slate-500">Relationship</label>
                                  <input
                                    className="w-full mt-1 px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30"
                                    placeholder="e.g. Line Manager"
                                    disabled={isSubmitting || isSaved}
                                    value={slotForm.relationship}
                                    onChange={e => setReferenceSlotForm(ref.reference_number, { relationship: e.target.value })}
                                    data-testid={`provide-new-relationship-${ref.reference_number}`}
                                  />
                                </div>
                              </div>
                              <div className="mt-3">
                                <label className="text-xs text-slate-500">
                                  If this is not your most recent employer, tell us why
                                </label>
                                <Select
                                  value={slotForm.slot_reason_type || undefined}
                                  onValueChange={(value) => setReferenceSlotForm(ref.reference_number, { slot_reason_type: value })}
                                  disabled={isSubmitting || isSaved}
                                >
                                  <SelectTrigger className="mt-1" data-testid={`provide-new-slot-reason-${ref.reference_number}`}>
                                    <SelectValue placeholder="Choose a reason if needed" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {SLOT_REASON_OPTIONS.map((option) => (
                                      <SelectItem key={option.value} value={option.value}>
                                        {option.label}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </div>
                              {slotForm.slot_reason_type && (
                                <div>
                                  <label className="text-xs text-slate-500">
                                    Brief note {slotForm.slot_reason_type === 'other' ? '*' : '(optional)'}
                                  </label>
                                  <textarea
                                    rows={2}
                                    className="w-full mt-1 px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30"
                                    placeholder="Explain why this referee is appropriate for this reference."
                                    disabled={isSubmitting || isSaved}
                                    value={slotForm.slot_reason_note}
                                    onChange={e => setReferenceSlotForm(ref.reference_number, { slot_reason_note: e.target.value })}
                                    data-testid={`provide-new-slot-note-${ref.reference_number}`}
                                  />
                                </div>
                              )}
                              <div className="mt-3">
                                <label className="text-xs text-slate-500">
                                  Short note <span className="text-slate-400">(optional)</span>
                                </label>
                                <textarea
                                  rows={3}
                                  className="w-full mt-1 px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30"
                                  placeholder="e.g. The previous referee has left the company or cannot be contacted."
                                  disabled={isSubmitting || isSaved}
                                  value={slotForm.change_reason}
                                  onChange={e => setReferenceSlotForm(ref.reference_number, { change_reason: e.target.value })}
                                  data-testid={`provide-new-reason-${ref.reference_number}`}
                                />
                                <p className="text-[10px] text-slate-500 mt-1">
                                  Add a short note only if it helps explain this referee — it is not required.
                                </p>
                              </div>
                              <Button
                                size="sm"
                                disabled={
                                  isSubmitting
                                  || isSaved
                                  || !slotForm.name
                                  || !slotForm.email
                                  || !(slotForm.organisation || '').trim()
                                  || (slotForm.slot_reason_type === 'other' && (slotForm.slot_reason_note || '').trim().length < 10)
                                }
                                onClick={() => handleProvideNewSubmit(ref.reference_number)}
                                className="mt-2"
                                data-testid={`provide-new-submit-${ref.reference_number}`}
                              >
                                {isSubmitting
                                  ? `Saving Referee ${ref.reference_number}\u2026`
                                  : isSaved
                                    ? `Referee ${ref.reference_number} saved`
                                    : 'Send referee details'}
                              </Button>
                              {(isSubmitting || isSaved) && (
                                <p
                                  className="mt-2 text-xs font-medium text-slate-600"
                                  data-testid={`provide-new-status-${ref.reference_number}`}
                                >
                                  {isSubmitting
                                    ? `Saving Referee ${ref.reference_number}\u2026 please wait, this is now locked.`
                                    : `Referee ${ref.reference_number} saved \u2014 moving to your next step\u2026`}
                                </p>
                              )}
                            </div>
                            );
                          })()}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              )}
          </OnboardingPremiumCard>
        )}

        {/* Priority 4: Care Certificate - Mandatory standards for new care staff (CQC requirement). Treat as a structured sub-journey. */}
        {showFileSections && wizardShowsStep('care_certificate') && (
          <>
          <div className="mb-2 flex items-center gap-2">
            <OnboardingPriorityPill variant="care">STEP 4 - CARE CERTIFICATE</OnboardingPriorityPill>
          </div>
          <div data-testid="induction-section">
            <CareCertificateInductionPanel />
          </div>
        </>
        )}

        {/* ========== COMPETENCY ASSESSMENTS (P1: Worker Dashboard) ========== */}
        {showFileSections && isActiveEmployee && competency_assessments && competency_assessments.length > 0 && (
          <Card className="shadow-md border-0" data-testid="competency-section">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <Award className="h-5 w-5 text-orange-500" />
                    Competency Assessments
                  </CardTitle>
                  <p className="text-xs text-slate-500 mt-1">
                    Your skills assessments and outcomes
                  </p>
                </div>
                <Badge className={`${
                  competency_assessments.filter(c => c.outcome === 'pass' || c.status === 'completed').length === competency_assessments.length
                    ? 'bg-green-100 text-green-700'
                    : 'bg-amber-100 text-amber-700'
                }`}>
                  {competency_assessments.filter(c => c.outcome === 'pass' || c.status === 'completed').length}/{competency_assessments.length} Passed
                </Badge>
              </div>
            </CardHeader>
            <CardContent>
              {isActiveEmployee && competencyRecurringItems.length > 0 && (
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-3">
                  <div className="rounded-lg border border-red-100 bg-red-50 px-3 py-2">
                    <p className="text-xs text-red-700">Overdue reviews</p>
                    <p className="text-sm font-semibold text-red-800">{competencyRecurringCounts.overdue}</p>
                  </div>
                  <div className="rounded-lg border border-amber-100 bg-amber-50 px-3 py-2">
                    <p className="text-xs text-amber-700">Due now</p>
                    <p className="text-sm font-semibold text-amber-800">{competencyRecurringCounts.due}</p>
                  </div>
                  <div className="rounded-lg border border-blue-100 bg-blue-50 px-3 py-2">
                    <p className="text-xs text-blue-700">Upcoming</p>
                    <p className="text-sm font-semibold text-blue-800">{competencyRecurringCounts.upcoming}</p>
                  </div>
                </div>
              )}

              <div className="space-y-2">
                {competency_assessments.map((comp, idx) => (
                  (() => {
                    const reviewState = comp.review_due_date ? getDueState(comp.review_due_date, 30) : null;
                    const isCompleted = comp.outcome === 'pass' || comp.status === 'completed' || comp.status === 'competent';
                    return (
                      <div
                        key={idx}
                        className={`p-3 rounded-xl border ${
                          reviewState === 'overdue' ? 'bg-red-50 border-red-200' :
                          reviewState === 'due_soon' ? 'bg-amber-50 border-amber-200' :
                          comp.outcome === 'pass' ? 'bg-green-50 border-green-200' :
                          comp.outcome === 'fail' ? 'bg-red-50 border-red-200' :
                          comp.status === 'scheduled' ? 'bg-blue-50 border-blue-200' :
                          'bg-slate-50 border-slate-200'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                              comp.outcome === 'pass' ? 'bg-green-100' :
                              comp.outcome === 'fail' ? 'bg-red-100' :
                              'bg-slate-100'
                            }`}>
                              {comp.outcome === 'pass' ? (
                                <CheckCircle className="h-5 w-5 text-green-600" />
                              ) : comp.outcome === 'fail' ? (
                                <X className="h-5 w-5 text-red-600" />
                              ) : (
                                <Clock className="h-5 w-5 text-slate-400" />
                              )}
                            </div>
                            <div>
                              <span className="font-medium text-slate-800">{comp.competency_name}</span>
                              {comp.area && <p className="text-xs text-slate-500">{comp.area}</p>}
                              {(comp.completed_date || comp.scheduled_date) && (
                                <p className="text-xs text-slate-500">
                                  {isCompleted ? 'Completed' : 'Scheduled'}: {formatDate(comp.completed_date || comp.scheduled_date)}
                                </p>
                              )}
                            </div>
                          </div>
                          <Badge className={`text-xs ${
                            comp.outcome === 'pass' ? 'bg-green-100 text-green-700' :
                            comp.outcome === 'fail' ? 'bg-red-100 text-red-700' :
                            reviewState === 'overdue' ? 'bg-red-100 text-red-700' :
                            reviewState === 'due_soon' ? 'bg-amber-100 text-amber-700' :
                            comp.status === 'scheduled' ? 'bg-blue-100 text-blue-700' :
                            'bg-slate-100 text-slate-600'
                          }`}>
                            {comp.outcome === 'pass' ? 'Current' :
                             comp.outcome === 'fail' ? 'Needs Improvement' :
                             reviewState === 'overdue' ? 'Overdue' :
                             reviewState === 'due_soon' ? 'Due soon' :
                             comp.status === 'scheduled' ? 'Scheduled' : 'Pending'}
                          </Badge>
                        </div>

                        {comp.review_due_date && (
                          <p className={`text-xs mt-2 ${
                            reviewState === 'overdue' ? 'text-red-600' :
                            reviewState === 'due_soon' ? 'text-amber-600' :
                            'text-slate-500'
                          }`}>
                            Review due: {formatDate(comp.review_due_date)}
                          </p>
                        )}

                        {comp.follow_up_required && comp.follow_up_date && (
                          <p className="text-xs text-amber-600 mt-1">
                            Follow-up scheduled: {formatDate(comp.follow_up_date)}
                          </p>
                        )}
                      </div>
                    );
                  })()
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* ========== AGREEMENTS (P0: Contract & Handbook Status) ========== */}
        {showFileSections && wizardShowsStep('agreements') && agreements && agreements.length > 0 && (
          <OnboardingPremiumCard
            tone="purple"
            data-testid="agreements-section"
            header={(
              <div className="mb-4">
                <WorkerPremiumCardHeader
                  icon={PenTool}
                  title="Agreements & Acknowledgements"
                  description="Review the exact PDF that is on file, then sign or acknowledge it from your portal."
                  action={(
                    <Badge className={`${
                      agreementsActionRequiredCount > 0 ? 'bg-red-100 text-red-700' :
                      agreements.length > 0 && agreementsCompletedCount === agreements.length ? 'bg-green-100 text-green-700' :
                      agreementsHasInProgressState || agreementsCompletedCount > 0 ? 'bg-blue-100 text-blue-700' :
                      'bg-slate-100 text-slate-600'
                    }`}>
                      {agreementsActionRequiredCount > 0
                        ? `${agreementsActionRequiredCount} action required`
                        : `${agreementsCompletedCount} of ${agreements.length} completed`}
                    </Badge>
                  )}
                />
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="mt-2 rounded-xl"
                  onClick={() => setShowAgreementsDetails((v) => !v)}
                  data-testid="toggle-agreements-details"
                >
                  {showAgreementsDetails ? 'Hide details' : 'Show details'}
                  {showAgreementsDetails ? <ChevronUp className="ml-1 h-4 w-4" /> : <ChevronDown className="ml-1 h-4 w-4" />}
                </Button>
              </div>
            )}
          >
              {!showAgreementsDetails ? (
                <p className="text-sm text-slate-600">
                  Agreements collapsed to reduce noise. Expand to review contract and handbook status/actions.
                </p>
              ) : (
              <div className="space-y-3">
                {agreementDisplays.map(({ agreement, display: agreementDisplay }) => {
                  const agreementContractState = resolveLatestContractState(agreement, { contractEligibility: effectiveContractEligibility }).status;
                  const isHistoricalRejectedContract =
                    agreement.id === 'contract_acceptance' &&
                    (
                      ['rejected', 'rejected_reopen_required', 'superseded', 'action_required'].includes(agreementContractState) ||
                      agreement.rejected
                    ) &&
                    !(agreementContractState === 'pending_signature' && effectiveContractCanSign);
                  const compactDescription = truncateAgreementCardText(agreementDisplay.description, 160);
                  const compactTitle = agreement.id === 'handbook_acknowledgement'
                    ? 'Employee Handbook'
                    : agreement.name;
                  const toneClasses =
                    agreementDisplay.tone === 'critical'
                      ? 'bg-red-50 border-red-200'
                      : agreementDisplay.tone === 'success'
                        ? 'bg-green-50 border-green-200'
                        : agreementDisplay.tone === 'info'
                          ? 'bg-blue-50 border-blue-200'
                          : 'bg-slate-50 border-slate-200';
                  const iconClasses =
                    agreementDisplay.tone === 'critical'
                      ? 'bg-red-100 text-red-600'
                      : agreementDisplay.tone === 'success'
                        ? 'bg-green-100 text-green-600'
                        : agreementDisplay.tone === 'info'
                          ? 'bg-blue-100 text-blue-600'
                          : 'bg-slate-100 text-slate-500';
                  return (
                  <div
                    key={agreement.id}
                    className={`p-4 rounded-xl border ${toneClasses}`}
                    data-testid={`agreement-${agreement.id}`}
                  >
                    {isHistoricalRejectedContract ? (
                      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                        <div className="min-w-0 flex-1 w-full whitespace-normal break-normal [overflow-wrap:normal] [word-break:normal]">
                          <p className="text-sm font-medium text-slate-700">
                            Your contract needs to be reissued by your manager.
                          </p>
                        </div>
                        <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto sm:justify-end">
                          {(agreement.file_url || agreement.download_url) && (
                            <>
                              <Button
                                size="sm"
                                variant="outline"
                                className="gap-1 whitespace-nowrap"
                                onClick={() => openDocumentViewer({ ...agreement, name: agreement.name })}
                              >
                                <Eye className="h-3.5 w-3.5" />
                                {agreement.id === 'handbook_acknowledgement' ? 'View handbook' : 'View PDF'}
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                className="gap-1 whitespace-nowrap"
                                onClick={() => downloadAgreement(agreement)}
                              >
                                <Download className="h-3.5 w-3.5" />
                                Download PDF
                              </Button>
                            </>
                          )}
                          <Badge className="shrink-0 text-xs bg-slate-100 text-slate-600">
                            Historical
                          </Badge>
                        </div>
                      </div>
                    ) : (
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                      <div className="flex items-start gap-3 min-w-0 flex-1 w-full">
                        <div className={`w-10 h-10 shrink-0 rounded-lg flex items-center justify-center ${iconClasses.split(' ')[0]}`}>
                          {agreement.verified ? (
                            <CheckCircle className={`h-5 w-5 ${iconClasses.split(' ')[1]}`} />
                          ) : agreement.rejected ? (
                            <AlertCircle className={`h-5 w-5 ${iconClasses.split(' ')[1]}`} />
                          ) : agreementDisplay.workerActionable ? (
                            <PenTool className={`h-5 w-5 ${iconClasses.split(' ')[1]}`} />
                          ) : (
                            <Clock className={`h-5 w-5 ${iconClasses.split(' ')[1]}`} />
                          )}
                        </div>
                        <div className="min-w-0 flex-1 w-full whitespace-normal break-normal [overflow-wrap:normal] [word-break:normal]">
                          <span className="font-medium text-slate-700">{compactTitle}</span>
                          <p className="text-xs text-slate-500 mt-0.5">{compactDescription}</p>
                        </div>
                        </div>
                      <div className="flex flex-wrap items-center justify-start gap-2 w-full sm:w-auto sm:justify-end sm:shrink-0">
                        {(agreement.file_url || agreement.download_url) && (
                          <>
                            <Button
                              size="sm"
                              variant="outline"
                              className="gap-1 whitespace-nowrap"
                              onClick={() => openDocumentViewer({ ...agreement, name: agreement.name })}
                            >
                              <Eye className="h-3.5 w-3.5" />
                              {agreement.id === 'handbook_acknowledgement' ? 'View handbook' : 'View PDF'}
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="gap-1 whitespace-nowrap"
                              onClick={() => downloadAgreement(agreement)}
                            >
                              <Download className="h-3.5 w-3.5" />
                              Download PDF
                            </Button>
                          </>
                        )}
                        {agreement.id === 'contract_acceptance' && agreementDisplay.workerActionable && effectiveContractCanSign && (
                          <Button
                            size="sm"
                            className="gap-1 whitespace-nowrap"
                            onClick={() => setShowSignaturePad(true)}
                            data-testid="review-sign-contract-btn"
                          >
                            <PenTool className="h-3.5 w-3.5" />
                            Review & sign contract
                          </Button>
                        )}
                        {agreement.id === 'handbook_acknowledgement' && handbookDisplay.workerActionable && agreement.id === handbookAgreement?.id && (
                          <Button
                            size="sm"
                            className="gap-1 whitespace-nowrap"
                            disabled={!agreement.file_url && !agreement.download_url}
                            title={(!agreement.file_url && !agreement.download_url) ? 'Handbook PDF is not available yet' : undefined}
                            onClick={() => openHandbookAckModal(agreement)}
                            data-testid="acknowledge-handbook-btn"
                          >
                            <CheckCircle className="h-3.5 w-3.5" />
                            Acknowledge
                          </Button>
                        )}
                        <Badge className={`shrink-0 text-xs ${
                          agreementDisplay.tone === 'critical' ? 'bg-red-100 text-red-700' :
                          agreementDisplay.tone === 'success' ? 'bg-green-100 text-green-700' :
                          agreementDisplay.tone === 'info' ? 'bg-blue-100 text-blue-700' :
                          'bg-slate-100 text-slate-600'
                        }`}>
                          {agreementDisplay.badge}
                        </Badge>
                      </div>
                    </div>
                    )}
                  </div>
                )})}
              </div>
              )}
          </OnboardingPremiumCard>
        )}

        {/* Contract Status - Only for onboarding */}
        {showOnboardingContractSection && (
          <OnboardingPremiumCard tone="critical" title="Contract Review & Signature" description={`Review and sign your contract here. ${providerName} will countersign before the final version is complete.`} icon={FileText}>
              {contractAgreement?.contract_state === 'awaiting_company_countersignature' ? (
                <div className="flex items-center justify-between p-4 bg-blue-50 rounded-xl border border-blue-200">
                  <div>
                    <span className="font-medium text-blue-800">Awaiting company countersignature</span>
                    <p className="text-xs text-blue-600">Your signed contract is on file. {providerName} still needs to countersign it before it becomes the final executed agreement.</p>
                  </div>
                  <Button
                    variant="outline"
                    className="gap-2"
                    onClick={() => openDocumentViewer({ ...contractAgreement, name: contractAgreement.name })}
                  >
                    <Eye className="h-4 w-4" />
                    View current PDF
                  </Button>
                </div>
              ) : contractAgreement?.contract_state === 'fully_executed' ? (
                <div className="flex items-center justify-between p-4 bg-green-50 rounded-xl border border-green-200">
                  <div>
                    <span className="font-medium text-green-800">Contract fully executed</span>
                    <p className="text-xs text-green-600">Your contract has been signed by both you and {providerName}. You can continue to view or download the executed PDF above.</p>
                  </div>
                  <Badge className="bg-green-100 text-green-700">Complete</Badge>
                </div>
              ) : effectiveContractEligibility?.can_sign ? (
                <div className="flex items-center justify-between p-4 bg-green-50 rounded-xl border border-green-200">
                  <div>
                    <span className="font-medium text-green-800">Ready to Sign</span>
                    <p className="text-xs text-green-600">Earlier onboarding checks are complete. You can now review and sign your contract.</p>
                  </div>
                  <Button 
                    onClick={() => setShowSignaturePad(true)}
                    className="gap-2 bg-green-600 hover:bg-green-700"
                    data-testid="sign-contract-btn"
                  >
                    <PenTool className="h-4 w-4" />
                    Review & Sign Contract
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="flex items-center justify-between p-4 bg-amber-50 rounded-xl border border-amber-200">
                    <div>
                      <span className="font-medium text-amber-800">{contractNeedsReissueMessage ? 'Contract not currently signable' : 'Contract not ready yet'}</span>
                      <p className="text-xs text-amber-600">
                        {contractNeedsReissueMessage
                          ? 'This contract is not currently signable. Please contact your manager to reissue it.'
                          : 'Contract signing becomes available once your forms, employment history, documents, references, and training are complete.'}
                      </p>
                    </div>
                    <Button 
                      disabled
                      className="gap-2 bg-gray-300 cursor-not-allowed"
                      data-testid="sign-contract-btn-locked"
                    >
                      <Lock className="h-4 w-4" />
                      Not ready
                    </Button>
                  </div>
                  
                  {effectiveContractEligibility?.blockers?.length > 0 && (
                    <div className="p-3 bg-gray-50 rounded-lg">
                      <p className="text-xs font-medium text-gray-600 mb-2">
                        Still needed before you can sign ({effectiveContractEligibility.blockers.length}):
                      </p>
                      <ul className="text-xs text-gray-500 space-y-1">
                        {effectiveContractEligibility.blockers.slice(0, 5).map((blocker, idx) => (
                          <li key={idx} className="flex items-center gap-1">
                            <AlertTriangle className="h-3 w-3 text-amber-500" />
                            {blocker}
                          </li>
                        ))}
                        {effectiveContractEligibility.blockers.length > 5 && (
                          <li className="text-gray-400">
                            + {effectiveContractEligibility.blockers.length - 5} more...
                          </li>
                        )}
                      </ul>
                    </div>
                  )}
                </div>
              )}
          </OnboardingPremiumCard>
        )}

        {showFileSections && isActiveEmployee && appraisals && appraisals.length > 0 && (
          <Card className="shadow-md border-0" data-testid="appraisals-section">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <Award className="h-5 w-5 text-teal-600" />
                    Appraisals
                  </CardTitle>
                  <p className="text-xs text-slate-500 mt-1">
                    Your annual appraisal schedule — complete the self-assessment before your meeting
                  </p>
                </div>
                <Badge className="bg-teal-100 text-teal-700">{appraisals.length} cycle(s)</Badge>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {appraisals.slice(0, 5).map((ap) => (
                  <div key={ap.cycle_id || ap.id} className="p-3 rounded-xl border bg-blue-50 border-blue-200">
                    <div className="flex items-center justify-between gap-3 flex-wrap">
                      <div>
                        <p className="font-medium text-slate-800 text-sm">Annual appraisal</p>
                        <p className="text-xs text-slate-500">
                          {ap.scheduled_at
                            ? `Meeting ${formatDate(ap.scheduled_at)}`
                            : ap.appraisal_date
                              ? `Completed ${formatDate(ap.appraisal_date)}`
                              : 'Scheduled by your manager'}
                        </p>
                        {ap.reviewer && (
                          <p className="text-xs text-slate-500 mt-1">With: {ap.reviewer}</p>
                        )}
                      </div>
                      <Badge className={
                        ap.status === 'completed'
                          ? 'bg-green-100 text-green-700'
                          : ap.status === 'self_assessment_submitted'
                            ? 'bg-[#D8E6D5] text-[#1F8F5F]'
                            : 'bg-amber-100 text-amber-700'
                      }>
                        {ap.status === 'completed'
                          ? 'Completed'
                          : ap.status === 'self_assessment_submitted'
                            ? 'Self-assessment sent'
                            : 'Action needed'}
                      </Badge>
                    </div>
                    <div className="flex flex-wrap gap-2 mt-3">
                      {ap.can_complete_self_assessment && (
                        <Button
                          size="sm"
                          onClick={() => {
                            setActiveAppraisalCycleId(ap.cycle_id || ap.id);
                            setSelfAssessmentOpen(true);
                          }}
                          data-testid={`complete-self-assessment-${ap.cycle_id || ap.id}`}
                        >
                          Complete self-assessment
                        </Button>
                      )}
                      {ap.can_view_completed_record && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => openWorkerAppraisalRecord(ap.cycle_id || ap.id)}
                          disabled={appraisalPdfLoading === (ap.cycle_id || ap.id)}
                          data-testid={`view-appraisal-record-${ap.cycle_id || ap.id}`}
                        >
                          {appraisalPdfLoading === (ap.cycle_id || ap.id) ? (
                            <Loader2 className="h-4 w-4 animate-spin mr-1" />
                          ) : (
                            <Eye className="h-4 w-4 mr-1" />
                          )}
                          View my appraisal record
                        </Button>
                      )}
                    </div>
                    {ap.can_view_completed_record && (
                      <p className="text-[11px] text-slate-500 mt-2">
                        Read-only copy of your signed annual performance review.
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {showFileSections && isActiveEmployee && supervisions && supervisions.length > 0 && (
          <Card className="shadow-md border-0" data-testid="supervisions-section">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <Calendar className="h-5 w-5 text-teal-600" />
                    Supervisions
                  </CardTitle>
                  <p className="text-xs text-slate-500 mt-1">
                    Your supervision schedule and completion history
                  </p>
                </div>
                <Badge className="bg-teal-100 text-teal-700">
                  {supervisions.length} Recorded
                </Badge>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {supervisions.slice(0, 10).map((sv, idx) => {
                  const status = (sv.status || '').toLowerCase();
                  const dueTarget = sv.next_due_at || sv.scheduled_at;
                  const dueState = getDueState(dueTarget, 14);
                  return (
                    <div
                      key={idx}
                      className={`p-3 rounded-xl border ${
                        dueState === 'overdue' ? 'bg-red-50 border-red-200' :
                        dueState === 'due_soon' ? 'bg-amber-50 border-amber-200' :
                        status === 'completed' ? 'bg-green-50 border-green-200' :
                        status === 'cancelled' ? 'bg-slate-50 border-slate-200' :
                        'bg-blue-50 border-blue-200'
                      }`}
                    >
                      <div className="flex items-center justify-between gap-3">
                        <div>
                          <p className="font-medium text-slate-800 text-sm">
                            {(sv.supervision_type || 'supervision').replace(/_/g, ' ')}
                          </p>
                          <p className="text-xs text-slate-500">
                            {sv.completed_at
                              ? `Completed ${formatDate(sv.completed_at)}`
                              : sv.scheduled_at
                                ? `Scheduled ${formatDate(sv.scheduled_at)}`
                                : 'No scheduled date'}
                          </p>
                        </div>
                        <Badge className={
                          status === 'completed'
                            ? 'bg-green-100 text-green-700'
                            : status === 'cancelled'
                              ? 'bg-slate-100 text-slate-600'
                              : status === 'overdue' || dueState === 'overdue'
                                ? 'bg-red-100 text-red-700'
                                : dueState === 'due_soon'
                                  ? 'bg-amber-100 text-amber-700'
                                  : 'bg-blue-100 text-blue-700'
                        }>
                          {status === 'completed'
                            ? 'Completed'
                            : status === 'cancelled'
                              ? 'Cancelled'
                              : status === 'overdue' || dueState === 'overdue'
                                ? 'Overdue'
                                : dueState === 'due_soon'
                                  ? 'Due soon'
                                  : 'Scheduled'}
                        </Badge>
                      </div>

                      {dueTarget && status !== 'completed' && (
                        <p className="text-xs text-slate-600 mt-2">
                          Next due: {formatDate(dueTarget)}
                        </p>
                      )}

                      {sv.notes && (
                        <p className="text-xs text-slate-600 mt-2 bg-white/50 p-2 rounded">{sv.notes}</p>
                      )}
                    </div>
                  );
                })}
              </div>
              {supervisions.length > 10 && (
                <p className="text-xs text-slate-400 mt-2 text-center">
                  Showing 10 of {supervisions.length} supervisions
                </p>
              )}
            </CardContent>
          </Card>
        )}

        {isActiveEmployee && showFileSections && !wizardEnabled && (completed_documents?.length > 0 || completed_trainings?.length > 0) && (
          <SubmittedDocumentsVault
            documents={completed_documents}
            trainings={completed_trainings}
            token={localStorage.getItem('workerToken')}
            apiBase={API}
            formatDate={formatDate}
            getDocumentDisplayStatus={getCompletedDocumentDisplayStatus}
            getTrainingBadgeMeta={getCompletedTrainingBadgeMeta}
            documentWorkflowUi={DOCUMENT_WORKFLOW_UI}
          />
        )}

        {/* Footer */}
        <div className="text-center py-6 text-xs text-slate-400">
          <p>{tenantDisplayName} Applicant &amp; Workforce Portal</p>
          <p>Application code: {employee.employee_code || employee.applicant_reference || '-'}</p>
        </div>
      </OnboardingWizardShell>
      )}

      {/* CS-R2 A-3R: a real scheduled/confirmed interview must stay visible to the
          applicant even after the interview wizard step is 'paused' post-booking.
          OnboardingWizardShell drops all children when a step is paused, so this
          appointment-only surface is rendered OUTSIDE the shell. It never unlocks
          the questionnaire (appointment visibility != assessment permission). */}
      {interviewSurface === 'appointment' && applicantView !== 'records' && (
        <div className="mt-6">
          <InterviewAppointmentCard
            appointmentOnly
            appointment={interview_appointment}
            interviewState={interview_state}
            onRefresh={fetchDashboard}
          />
        </div>
      )}
      {/* The canonical interview card is visible even while applicants are on
          their CV/documents step. Otherwise "finish uploads, then book" would
          disappear precisely when the remaining upload tasks need attention. */}
      {interviewSurface === 'journey' && applicantView === 'tasks'
        && !wizardShowsStep('interview') && !isInterviewStepConcluded(interview_state) && (
        <div className="mt-6">
          <InterviewAppointmentCard
            interviewForm={interview_form}
            appointment={interview_appointment}
            bookingSlots={interview_booking_slots}
            interviewState={interview_state}
            onRefresh={fetchDashboard}
            onOpenQuestionnaire={(form) => navigate(`/worker/forms/${form.id}`)}
            onGoToTask={(task) => handleNextAction({ route: task.route })}
            isFormWorkerActionRequired={isFormWorkerActionRequired}
            getFormActionLabel={getFormActionLabel}
            getFormActionButtonClass={getFormActionButtonClass}
          />
        </div>
      )}
      {/* NOTE: terminal / review / awaiting-outcome states are rendered by the
          canonical Block A above (it renders whenever !hasScheduledInterview),
          so there is intentionally no separate standalone status card here —
          that would duplicate the interview surface. */}

      {/* Contract Signature Dialog */}
      <Dialog open={showSignaturePad} onOpenChange={setShowSignaturePad}>
        <DialogContent className="max-w-xl p-0 max-h-[92vh] overflow-y-auto">
            <SignaturePad
              employeeId={employee.id}
              employeeName={employee.name}
              sourceRecordId={contractAgreement?.source_record_id || null}
              onSigned={() => {
                setShowSignaturePad(false);
                fetchDashboard(); // Refresh dashboard
                toast.success(`Contract signed and sent for ${providerName} countersignature.`);
              }}
            onCancel={() => setShowSignaturePad(false)}
          />
        </DialogContent>
      </Dialog>

      {/* Handbook Acknowledgement Dialog */}
      <Dialog open={showHandbookAckModal} onOpenChange={(open) => { if (!open) closeHandbookAckModal(); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-primary" />
              Acknowledge Employee Handbook
            </DialogTitle>
          </DialogHeader>
          {handbookAckAgreement && (
            <div className="space-y-4">
              {(!handbookAckAgreement.file_url && !handbookAckAgreement.download_url) ? (
                <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                  The handbook PDF for the version you are being asked to acknowledge is not available yet.
                  You cannot acknowledge until it is ready. Please refresh shortly.
                </div>
              ) : (
                <>
                  <div className="rounded-md border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700">
                    <p className="font-medium text-slate-900 mb-1">Step 1 — Read the handbook</p>
                    <p className="text-xs text-slate-600">
                      Open the full handbook PDF and read it in full before acknowledging.
                      This is the exact version you are signing.
                    </p>
                    <div className="flex flex-wrap gap-2 mt-3">
                      <Button
                        size="sm"
                        className="gap-1"
                        onClick={viewHandbookPdfFromModal}
                        data-testid="handbook-view-pdf-btn"
                      >
                        <Eye className="h-3.5 w-3.5" />
                        View Handbook PDF
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="gap-1"
                        onClick={() => downloadAgreement(handbookAckAgreement)}
                      >
                        <Download className="h-3.5 w-3.5" />
                        Download PDF
                      </Button>
                    </div>
                    {handbookAckAgreement.template_version && (
                      <p className="text-[11px] text-slate-500 mt-2">
                        Version {handbookAckAgreement.template_version}
                        {handbookAckAgreement.rendered_at && ` • prepared ${formatDate(handbookAckAgreement.rendered_at)}`}
                      </p>
                    )}
                  </div>

                  <div className="rounded-md border border-slate-200 p-3 text-sm">
                    <p className="font-medium text-slate-900 mb-1">Step 2 — Confirm</p>
                    <label className={`flex items-start gap-2 ${handbookPdfViewed ? '' : 'opacity-60'}`}>
                      <input
                        type="checkbox"
                        className="mt-1"
                        disabled={!handbookPdfViewed}
                        checked={handbookAckConfirmed}
                        onChange={(e) => setHandbookAckConfirmed(e.target.checked)}
                        data-testid="handbook-confirm-checkbox"
                      />
                      <span className="text-xs text-slate-700">
                        I confirm that I have opened and read the Employee Handbook PDF above
                        (Version {handbookAckAgreement.template_version || '—'}), that I understand its contents,
                        and that I agree to abide by the policies, procedures and expectations set out in it.
                        I am providing this acknowledgement as {employee?.name || 'the named employee'}.
                      </span>
                    </label>
                    {!handbookPdfViewed && (
                      <p className="text-[11px] text-amber-700 mt-2">
                        Open the handbook PDF first — the confirmation is locked until you have viewed it.
                      </p>
                    )}
                  </div>
                </>
              )}
            </div>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={closeHandbookAckModal}
              disabled={submittingHandbookAck}
            >
              Cancel
            </Button>
            <Button
              onClick={submitHandbookAck}
              disabled={
                submittingHandbookAck
                || !handbookAckAgreement
                || (!handbookAckAgreement.file_url && !handbookAckAgreement.download_url)
                || !handbookPdfViewed
                || !handbookAckConfirmed
              }
              className="gap-1"
              data-testid="handbook-submit-ack-btn"
            >
              {submittingHandbookAck ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <CheckCircle className="h-3.5 w-3.5" />
              )}
              Acknowledge Handbook
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      <InlineDocumentViewer
        open={viewerOpen}
        onClose={closeDocumentViewer}
        fetchUrl={viewerFetchUrl}
        title={viewerDocument?.name || viewerDocument?.file_name || 'Document'}
        token={localStorage.getItem('workerToken')}
        filename={viewerDocument?.file_name || viewerDocument?.name || 'document'}
      />

      {/* CV Extraction Verification Modal — removed. CV review is admin-only. */}
      {false && <Dialog open={false} onOpenChange={() => {}}>
        <DialogContent className="max-w-3xl max-h-[90vh] flex flex-col">
          <DialogHeader>
            <DialogTitle></DialogTitle>
          </DialogHeader>

          {cvPreviewLoading ? (
            <div className="flex flex-col items-center justify-center py-12">
              <Loader2 className="h-10 w-10 animate-spin text-blue-600 mb-4" />
              <p className="text-slate-600">Loading extracted data...</p>
            </div>
          ) : cvPreview?.has_pending_verification ? (
            <ScrollArea className="flex-1 max-h-[60vh] pr-4">
              {/* Employment History */}
              <div className="mb-6">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-semibold text-slate-800 flex items-center gap-2">
                    <Briefcase className="h-4 w-4 text-blue-600" />
                    Employment History ({cvPreview.extraction_preview?.jobs_found || 0} jobs)
                  </h3>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setCvEditMode(!cvEditMode)}
                    className="gap-1 text-blue-600"
                    data-testid="edit-cv-data-btn"
                  >
                    <Edit3 className="h-4 w-4" />
                    {cvEditMode ? 'Done Editing' : 'Edit'}
                  </Button>
                </div>

                <div className="space-y-3">
                  {editedEmploymentHistory.map((job, idx) => (
                    <div key={idx} className="p-4 bg-slate-50 rounded-xl border border-slate-200" data-testid={`cv-job-${idx}`}>
                      {cvEditMode ? (
                        <div className="space-y-3">
                          <div>
                            <label className="text-xs text-slate-500">Job Title</label>
                            <input
                              type="text"
                              value={job.job_title || ''}
                              onChange={(e) => {
                                const updated = [...editedEmploymentHistory];
                                updated[idx] = { ...updated[idx], job_title: e.target.value };
                                setEditedEmploymentHistory(updated);
                              }}
                              className="w-full mt-1 px-3 py-2 border rounded-lg text-sm"
                            />
                          </div>
                          <div>
                            <label className="text-xs text-slate-500">Employer</label>
                            <input
                              type="text"
                              value={job.employer || ''}
                              onChange={(e) => {
                                const updated = [...editedEmploymentHistory];
                                updated[idx] = { ...updated[idx], employer: e.target.value };
                                setEditedEmploymentHistory(updated);
                              }}
                              className="w-full mt-1 px-3 py-2 border rounded-lg text-sm"
                            />
                          </div>
                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <label className="text-xs text-slate-500">Start Date</label>
                              <input
                                type="date"
                                value={job.start_date || ''}
                                onChange={(e) => {
                                  const updated = [...editedEmploymentHistory];
                                  updated[idx] = { ...updated[idx], start_date: e.target.value };
                                  setEditedEmploymentHistory(updated);
                                }}
                                className="w-full mt-1 px-3 py-2 border rounded-lg text-sm"
                              />
                            </div>
                            <div>
                              <label className="text-xs text-slate-500">End Date</label>
                              <input
                                type="date"
                                value={job.end_date || ''}
                                onChange={(e) => {
                                  const updated = [...editedEmploymentHistory];
                                  updated[idx] = { ...updated[idx], end_date: e.target.value };
                                  setEditedEmploymentHistory(updated);
                                }}
                                className="w-full mt-1 px-3 py-2 border rounded-lg text-sm"
                                placeholder="Leave empty if current"
                              />
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-start justify-between">
                          <div>
                            <p className="font-medium text-slate-800">{job.job_title || 'Unknown Role'}</p>
                            <p className="text-sm text-slate-600">{job.employer || 'Unknown Employer'}</p>
                            <p className="text-xs text-slate-500 mt-1">
                              {job.start_date ? formatDate(job.start_date) : 'Unknown'} - {job.end_date ? formatDate(job.end_date) : 'Present'}
                            </p>
                          </div>
                          <Badge className="bg-slate-100 text-slate-600 text-xs">
                            {idx + 1}
                          </Badge>
                        </div>
                      )}
                    </div>
                  ))}

                  {editedEmploymentHistory.length === 0 && (
                    <div className="text-center py-6 text-slate-500">
                      <Briefcase className="h-8 w-8 mx-auto mb-2 opacity-50" />
                      <p>No employment history extracted</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Validation Issues */}
              {cvPreview.validation?.has_issues && (
                <div className="mb-6">
                  <h3 className="font-semibold text-slate-800 flex items-center gap-2 mb-3">
                    <AlertTriangle className="h-4 w-4 text-amber-600" />
                    Issues Detected
                  </h3>
                  
                  {/* Overlaps */}
                  {cvPreview.validation?.overlaps?.length > 0 && (
                    <div className="mb-3">
                      <p className="text-sm text-red-700 font-medium mb-2">Date Overlaps:</p>
                      <div className="space-y-2">
                        {cvPreview.validation.overlaps.map((overlap, idx) => (
                          <div key={idx} className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                            <p className="font-medium">{overlap.message}</p>
                            <p className="text-xs mt-1">
                              {overlap.job1_employer} overlaps with {overlap.job2_employer} ({overlap.overlap_days} days)
                            </p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Gaps */}
                  {cvPreview.validation?.gaps?.length > 0 && (
                    <div>
                      <p className="text-sm text-amber-700 font-medium mb-2">Employment Gaps (&gt;28 days):</p>
                      <div className="space-y-2">
                        {cvPreview.validation.gaps.filter(g => g.needs_explanation).map((gap, idx) => (
                          <div key={idx} className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-700">
                            <p>{gap.message}</p>
                            <p className="text-xs mt-1">
                              {formatDate(gap.start_date)} - {formatDate(gap.end_date)} ({gap.duration_days} days)
                            </p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <p className="text-xs text-slate-500 mt-3">
                    You'll be asked to explain gaps when completing the 10-Year Employment History form.
                  </p>
                </div>
              )}

              {/* Education */}
              {cvPreview.extraction_preview?.education?.length > 0 && (
                <div className="mb-6">
                  <h3 className="font-semibold text-slate-800 flex items-center gap-2 mb-3">
                    <GraduationCap className="h-4 w-4 text-purple-600" />
                    Education ({cvPreview.extraction_preview.education.length})
                  </h3>
                  <div className="space-y-2">
                    {cvPreview.extraction_preview.education.map((edu, idx) => (
                      <div key={idx} className="p-3 bg-purple-50 border border-purple-200 rounded-lg">
                        <p className="font-medium text-slate-800">{edu.qualification || edu.degree}</p>
                        <p className="text-sm text-slate-600">{edu.institution}</p>
                        {edu.year && <p className="text-xs text-slate-500">{edu.year}</p>}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Confirmation Checkbox */}
              <div className="p-4 bg-blue-50 border border-blue-200 rounded-xl mt-4">
                <div className="flex items-start gap-3">
                  <Checkbox
                    id="confirm-cv-accuracy"
                    checked={confirmCvAccuracy}
                    onCheckedChange={setConfirmCvAccuracy}
                    className="mt-1"
                    data-testid="confirm-cv-accuracy-checkbox"
                  />
                  <label htmlFor="confirm-cv-accuracy" className="text-sm text-slate-700 cursor-pointer">
                    I confirm that the information shown above is accurate and matches my actual employment history.
                    I understand this data will be used to pre-fill my 10-Year Employment History form.
                  </label>
                </div>
              </div>
            </ScrollArea>
          ) : cvPreview?.already_verified ? (
            <div className="text-center py-12">
              <CheckCircle className="h-16 w-16 text-green-600 mx-auto mb-4" />
              <p className="text-lg font-medium text-green-800">Already Verified</p>
              <p className="text-sm text-slate-600 mt-2">
                Your CV data was verified on {formatDate(cvPreview.verified_at)}
              </p>
            </div>
          ) : (
            <div className="text-center py-12">
              <AlertCircle className="h-16 w-16 text-slate-400 mx-auto mb-4" />
              <p className="text-slate-600">No items waiting for review</p>
            </div>
          )}

          <DialogFooter className="gap-2 pt-4 border-t">
            <Button variant="outline" onClick={() => setShowCvVerificationModal(false)}>
              Cancel
            </Button>
            {cvPreview?.has_pending_verification && (
              <Button
                onClick={handleVerifyCvExtraction}
                disabled={!confirmCvAccuracy || cvVerifying}
                className="gap-2 bg-green-600 hover:bg-green-700"
                data-testid="verify-cv-extraction-btn"
              >
                {cvVerifying ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <CheckCircle className="h-4 w-4" />
                )}
                Verify & Confirm
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>}

      {/* Reference Mismatch Explanation Modal */}
      <Dialog open={showMismatchExplanationModal} onOpenChange={setShowMismatchExplanationModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Link2 className="h-5 w-5 text-amber-600" />
              Explain reference details
            </DialogTitle>
            <p className="text-sm text-slate-500">
              Review the exact difference below, then tell us why it happened
            </p>
          </DialogHeader>

          {selectedMismatch && (
            <div className="space-y-4">
              {/* Mismatch Details */}
              <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl space-y-3">
                <p className="font-medium text-slate-800">
                  Reference {selectedMismatch.reference_number}: {selectedMismatch.referee_name}
                </p>
                <p className="text-sm text-slate-600">{selectedMismatch.referee_company}</p>
                <p className="text-sm text-amber-800">
                  {getWorkerReferenceDifferenceText(selectedMismatch.message || selectedMismatch.mismatch_details)}
                </p>

                {selectedMismatch.comparison && (
                  <div className="grid sm:grid-cols-2 gap-3 pt-2 border-t border-amber-200/80">
                    <div className="rounded-lg bg-white/80 border border-blue-100 p-3 text-xs space-y-1">
                      <p className="font-semibold text-blue-800 uppercase tracking-wide">What you entered</p>
                      <p><span className="text-slate-500">Name:</span> {selectedMismatch.comparison.declared?.name || '—'}</p>
                      <p><span className="text-slate-500">Email:</span> {selectedMismatch.comparison.declared?.email || '—'}</p>
                      <p><span className="text-slate-500">Organisation:</span> {selectedMismatch.comparison.declared?.organisation || '—'}</p>
                    </div>
                    <div className="rounded-lg bg-white/80 border border-violet-100 p-3 text-xs space-y-1">
                      <p className="font-semibold text-violet-800 uppercase tracking-wide">What the referee sent back</p>
                      <p><span className="text-slate-500">Name:</span> {selectedMismatch.comparison.returned?.name || '—'}</p>
                      <p><span className="text-slate-500">Email:</span> {selectedMismatch.comparison.returned?.email || '—'}</p>
                      <p><span className="text-slate-500">Organisation:</span> {selectedMismatch.comparison.returned?.organisation || '—'}</p>
                    </div>
                  </div>
                )}
                {selectedMismatch.comparison?.email_mismatch && (
                  <div className="rounded-lg bg-white border border-amber-300 px-3 py-2 text-xs text-amber-900">
                    <span className="font-medium">Email difference:</span> we contacted{' '}
                    <span className="font-mono">{selectedMismatch.comparison.request_sent_to}</span>, but the reply came from{' '}
                    <span className="font-mono">{selectedMismatch.comparison.returned?.email}</span>.
                  </div>
                )}
              </div>

              {/* Explanation Type */}
              <div>
                <label className="text-sm font-medium text-slate-700 mb-2 block">
                  {selectedMismatch.mismatch_type === 'identity_email'
                    ? 'Why did the referee respond from a different email?'
                    : 'Why is this referee different from your work history?'}
                </label>
                <Select value={mismatchExplanationType} onValueChange={setMismatchExplanationType}>
                  <SelectTrigger data-testid="mismatch-explanation-type">
                    <SelectValue placeholder="Choose a reason..." />
                  </SelectTrigger>
                  <SelectContent>
                    {referenceMismatches?.explanation_types?.map((type) => (
                      <SelectItem key={type.value} value={type.value}>
                        {getWorkerReferenceDifferenceText(type.label)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Explanation Text */}
              <div>
                <label className="text-sm font-medium text-slate-700 mb-2 block">
                  Please provide more details
                </label>
                <Textarea
                  value={mismatchExplanationText}
                  onChange={(e) => setMismatchExplanationText(e.target.value)}
                  placeholder="For example: I worked through an agency, so my referee is from the agency rather than the care home."
                  className="min-h-[120px]"
                  data-testid="mismatch-explanation-text"
                />
                <p className="text-xs text-slate-500 mt-1">
                  {mismatchExplanationText.length}/20 minimum characters
                </p>
              </div>

              {/* Review note */}
              <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-xs text-blue-700">
                  <Shield className="h-3 w-3 inline mr-1" />
                  {providerName} needs a clear explanation for any differences before your file can move forward.
                  Your explanation will be reviewed by the team and kept with your application.
                </p>
              </div>
            </div>
          )}

          <DialogFooter className="gap-2 pt-4 border-t">
            <Button variant="outline" onClick={() => setShowMismatchExplanationModal(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleSubmitMismatchExplanation}
              disabled={!mismatchExplanationType || mismatchExplanationText.length < 20 || submittingMismatchExplanation}
              className="gap-2 bg-amber-600 hover:bg-amber-700"
              data-testid="submit-mismatch-explanation-btn"
            >
              {submittingMismatchExplanation ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <MessageSquare className="h-4 w-4" />
              )}
              Send explanation
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Profile Completion Wizard for Offline PDF Imports */}
      <ProfileCompletionWizard
        open={showProfileWizard}
        onClose={() => setShowProfileWizard(false)}
        onComplete={() => {
          setShowProfileWizard(false);
          fetchDashboard(); // Refresh dashboard after completion
          toast.success('Profile completed successfully!');
        }}
      />

      <Dialog open={workerShiftDetailOpen} onOpenChange={setWorkerShiftDetailOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{isSelectedHistoricalShift ? 'Past shift record' : 'Shift details'}</DialogTitle>
          </DialogHeader>
          {selectedWorkerShift?.shift ? (
            <div className="space-y-3 text-sm text-slate-700">
              {isSelectedHistoricalShift ? (
                <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600">
                  This shift is in your history. Use View for reference; clock and daily-note actions apply only to current work.
                </div>
              ) : null}
              <div>
                <p className="text-xs text-slate-500">Date</p>
                <p className="font-medium">{formatDate(selectedWorkerShift.shift.start_at)}</p>
              </div>
              <div>
                <p className="text-xs text-slate-500">Time</p>
                <p className="font-medium">
                  {formatDateTime(selectedWorkerShift.shift.start_at).split(', ').pop()} - {formatDateTime(selectedWorkerShift.shift.end_at).split(', ').pop()}
                </p>
              </div>
              <div>
                <p className="text-xs text-slate-500">Location</p>
                <p className="font-medium">{selectedWorkerShift.shift.location_text || '—'}</p>
              </div>
              <div>
                <p className="text-xs text-slate-500">Role</p>
                <p className="font-medium">{selectedWorkerShift.shift.role_required || '—'}</p>
              </div>
              <div>
                <p className="text-xs text-slate-500">Notes</p>
                <p className="font-medium">{selectedWorkerShift.shift.notes || 'No notes'}</p>
              </div>
              <div>
                <p className="text-xs text-slate-500">Status</p>
                <Badge className={
                  selectedWorkerShift.current_attendance?.status === 'approved' ? 'bg-green-100 text-green-700' :
                  selectedWorkerShift.current_attendance?.status === 'submitted' ? 'bg-blue-100 text-blue-700' :
                  selectedWorkerShift.current_attendance?.status === 'rejected' ? 'bg-red-100 text-red-700' :
                  selectedWorkerShift.current_attendance?.status === 'open' ? 'bg-amber-100 text-amber-700' :
                  selectedWorkerShift.shift.status === 'assigned' ? 'bg-amber-100 text-amber-700' :
                  'bg-slate-100 text-slate-700'
                }>
                  {selectedWorkerShift.current_attendance?.status === 'approved'
                    ? (selectedWorkerShift.current_attendance?.approval_tier === 'corrected'
                      ? `Attendance approved - corrected by ${providerName}`
                      : 'Attendance approved')
                    :
                   selectedWorkerShift.current_attendance?.status === 'submitted' ? 'Attendance submitted — awaiting review' :
                   selectedWorkerShift.current_attendance?.status === 'rejected'
                     ? getWorkerRejectedAttendanceCardLabel(selectedWorkerShift.current_attendance)
                     : hasWorkerStuckOpenAttendance(selectedWorkerShift)
                       ? WORKER_STUCK_ATTENDANCE_MESSAGE
                       : selectedWorkerShift.current_attendance?.status === 'open' ? 'Clocked in' :
                   selectedWorkerShift.shift.status || selectedWorkerShift.assignment?.status || 'assigned'}
                </Badge>
              </div>
              <div>
                <p className="text-xs text-slate-500">Assignment status</p>
                <p className="font-medium">{selectedWorkerShift.assignment?.status || '—'}</p>
              </div>
              <div>
                <p className="text-xs text-slate-500">Attendance status</p>
                <Badge className={
                  selectedWorkerShift.current_attendance?.status === 'approved'
                    ? 'bg-green-100 text-green-700'
                    : selectedWorkerShift.current_attendance?.status === 'submitted'
                      ? 'bg-blue-100 text-blue-700'
                      : selectedWorkerShift.current_attendance?.status === 'open'
                        ? 'bg-amber-100 text-amber-700'
                        : selectedWorkerShift.current_attendance?.status === 'rejected'
                          ? 'bg-red-100 text-red-700'
                          : 'bg-slate-100 text-slate-700'
                }>
                  {selectedWorkerShift.current_attendance?.status || 'not started'}
                </Badge>
              </div>
              {selectedWorkerShift.current_attendance?.status === 'rejected' && !isSelectedHistoricalShift && (
                <div className="rounded-md border border-red-200 bg-red-50 p-3 space-y-1">
                  <p className="text-xs font-medium text-red-700">Attendance rejected by manager</p>
                  {selectedWorkerShift.current_attendance?.review_note && (
                    <p className="text-sm text-red-700">{selectedWorkerShift.current_attendance.review_note}</p>
                  )}
                  {workerMayResubmitRejectedAttendance(selectedWorkerShift.current_attendance) ? (
                    <p className="text-xs text-red-500">Please use Re-submit Attendance below to re-clock on site.</p>
                  ) : (
                    <p className="text-xs text-red-500">
                      {getWorkerRejectedAttendanceMessage(selectedWorkerShift.current_attendance)}
                    </p>
                  )}
                </div>
              )}
              {selectedWorkerShift.current_attendance?.status === 'rejected' && isSelectedHistoricalShift && (
                <div className="rounded-md border border-red-200 bg-red-50 p-3 space-y-1">
                  <p className="text-xs font-medium text-red-700">Attendance was rejected</p>
                  {selectedWorkerShift.current_attendance?.review_note && (
                    <p className="text-sm text-red-700">{selectedWorkerShift.current_attendance.review_note}</p>
                  )}
                </div>
              )}
              {hasWorkerStuckOpenAttendance(selectedWorkerShift) && !isSelectedHistoricalShift && (
                <div className="rounded-md border border-orange-200 bg-orange-50 p-3">
                  <p className="text-xs font-medium text-orange-800">{WORKER_STUCK_ATTENDANCE_MESSAGE}</p>
                  <p className="text-xs text-orange-700 mt-1">Your clock-in is on record but this shift assignment is no longer active. Do not attempt to clock out here.</p>
                </div>
              )}
              {(selectedWorkerShift.assignment?.unassign_reason || selectedWorkerShift.shift.cancelled_reason) ? (
                <div>
                  <p className="text-xs text-slate-500">Cancellation reason</p>
                  <p className="font-medium text-red-700">
                    {selectedWorkerShift.assignment?.unassign_reason || selectedWorkerShift.shift.cancelled_reason}
                  </p>
                </div>
              ) : null}
              <div className="border-t border-slate-200 pt-3">
                <p className="text-xs text-slate-500 mb-2">Daily note</p>
                {selectedWorkerShift.current_daily_note ? (
                  <div className="rounded-md border border-slate-200 bg-slate-50 p-3 space-y-3">
                    <p className="text-sm text-slate-700 whitespace-pre-wrap">
                      {selectedWorkerShift.current_daily_note.effective_note_text || selectedWorkerShift.current_daily_note.note_text}
                    </p>
                    {(Array.isArray(selectedWorkerShift.current_daily_note.effective_tags)
                      ? selectedWorkerShift.current_daily_note.effective_tags
                      : selectedWorkerShift.current_daily_note.tags || []).length > 0 && (
                      <div className="flex flex-wrap gap-1">
                        {(Array.isArray(selectedWorkerShift.current_daily_note.effective_tags)
                          ? selectedWorkerShift.current_daily_note.effective_tags
                          : selectedWorkerShift.current_daily_note.tags || []).map((tag) => (
                          <Badge key={`note-tag-${tag}`} className="bg-slate-200 text-slate-700 text-[11px]">
                            {tag}
                          </Badge>
                        ))}
                      </div>
                    )}
                    <p className="text-xs text-slate-500">
                      Saved at {formatDateTime(selectedWorkerShift.current_daily_note.timestamp)}
                      {selectedWorkerShift.current_daily_note.amendment_count > 0
                        ? ` · ${selectedWorkerShift.current_daily_note.amendment_count} correction${selectedWorkerShift.current_daily_note.amendment_count === 1 ? '' : 's'} recorded`
                        : ''}
                    </p>
                    {selectedWorkerShift.current_daily_note.latest_amendment?.reason ? (
                      <p className="text-xs text-amber-800">
                        Latest correction reason: {selectedWorkerShift.current_daily_note.latest_amendment.reason}
                      </p>
                    ) : null}
                    {workerDailyNoteCorrectionOpen ? (
                      <div className="space-y-2 rounded-lg border border-amber-200 bg-amber-50 p-3">
                        <p className="text-xs text-amber-900">
                          Corrections do not erase the original care record.
                        </p>
                        <Textarea
                          value={workerDailyNoteText}
                          onChange={(e) => setWorkerDailyNoteText(e.target.value)}
                          rows={3}
                          placeholder="Corrected daily note"
                        />
                        <div className="flex flex-wrap gap-1">
                          {DAILY_NOTE_TAG_OPTIONS.map((tag) => (
                            <Button
                              key={`daily-note-correction-tag-${tag}`}
                              type="button"
                              size="sm"
                              variant={workerDailyNoteTags.includes(tag) ? 'default' : 'outline'}
                              onClick={() => toggleWorkerDailyNoteTag(tag)}
                            >
                              {tag}
                            </Button>
                          ))}
                        </div>
                        <Textarea
                          value={workerDailyNoteCorrectionReason}
                          onChange={(e) => setWorkerDailyNoteCorrectionReason(e.target.value)}
                          rows={2}
                          placeholder="Why are you correcting this record?"
                        />
                        <div className="flex flex-wrap justify-end gap-2">
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            disabled={workerDailyNoteSubmitting}
                            onClick={() => {
                              const note = selectedWorkerShift.current_daily_note;
                              setWorkerDailyNoteText(note.effective_note_text || note.note_text || '');
                              setWorkerDailyNoteTags(Array.isArray(note.effective_tags) ? note.effective_tags : (note.tags || []));
                              setWorkerDailyNoteCorrectionReason('');
                              setWorkerDailyNoteCorrectionOpen(false);
                            }}
                          >
                            Cancel
                          </Button>
                          <Button
                            type="button"
                            size="sm"
                            disabled={
                              workerDailyNoteSubmitting
                              || (workerDailyNoteText || '').trim().length < 2
                              || (workerDailyNoteCorrectionReason || '').trim().length < 4
                            }
                            onClick={handleAmendWorkerDailyNote}
                          >
                            {workerDailyNoteSubmitting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                            Save correction
                          </Button>
                        </div>
                      </div>
                    ) : (
                      ['active', 'completed'].includes(selectedWorkerShift.assignment?.status)
                      && selectedWorkerShift.assignment?.worker_response_status === 'accepted' ? (
                        <div className="flex justify-end">
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            onClick={() => setWorkerDailyNoteCorrectionOpen(true)}
                          >
                            Correct note
                          </Button>
                        </div>
                      ) : null
                    )}
                  </div>
                ) : canWorkerEditDailyNoteForItem({
                  shift: selectedWorkerShift.shift,
                  assignment: selectedWorkerShift.assignment,
                  assignment_status: selectedWorkerShift.assignment?.status,
                  worker_response_status: selectedWorkerShift.assignment?.worker_response_status,
                  current_attendance: selectedWorkerShift.current_attendance,
                  current_daily_note: selectedWorkerShift.current_daily_note,
                }) ? (
                  <div className="space-y-2">
                    <Textarea
                      value={workerDailyNoteText}
                      onChange={(e) => setWorkerDailyNoteText(e.target.value)}
                      rows={3}
                      placeholder="Write care delivery note for this shift"
                    />
                    <div className="flex flex-wrap gap-1">
                      {DAILY_NOTE_TAG_OPTIONS.map((tag) => (
                        <Button
                          key={`daily-note-tag-${tag}`}
                          type="button"
                          size="sm"
                          variant={workerDailyNoteTags.includes(tag) ? 'default' : 'outline'}
                          onClick={() => toggleWorkerDailyNoteTag(tag)}
                        >
                          {tag}
                        </Button>
                      ))}
                    </div>
                    <div className="flex justify-end">
                      <Button
                        size="sm"
                        onClick={handleSubmitWorkerDailyNote}
                        disabled={workerDailyNoteSubmitting || (workerDailyNoteText || '').trim().length < 2}
                      >
                        {workerDailyNoteSubmitting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                        Save Daily Note
                      </Button>
                    </div>
                  </div>
                ) : (
                  <p className="text-xs text-slate-500">
                    {isSelectedHistoricalShift
                      ? 'No daily note recorded for this shift.'
                      : 'Daily note not required for this shift.'}
                  </p>
                )}
              </div>
              {(canWorkerClockIn(selectedWorkerShift) || canWorkerClockOut(selectedWorkerShift) || canWorkerReClock(selectedWorkerShift)) && !hasWorkerStuckOpenAttendance(selectedWorkerShift) && (
                <div className="space-y-2 pt-2">
                  <div className="flex items-center gap-2">
                    {canWorkerClockIn(selectedWorkerShift) && (
                      <Button
                        size="sm"
                        onClick={() => openSiteVerify(selectedWorkerShift, 'in')}
                        disabled={workerShiftClocking}
                      >
                        {workerShiftClocking ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                        Clock In
                      </Button>
                    )}
                    {canWorkerClockOut(selectedWorkerShift) && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => openSiteVerify(selectedWorkerShift, 'out')}
                        disabled={workerShiftClocking}
                      >
                        {workerShiftClocking ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                        Clock Out
                      </Button>
                    )}
                    {canWorkerReClock(selectedWorkerShift) && (
                      <Button
                        size="sm"
                        className="bg-amber-600 hover:bg-amber-700 text-white"
                        onClick={() => openSiteVerify(selectedWorkerShift, 'reclock')}
                        disabled={workerShiftClocking}
                      >
                        {workerShiftClocking ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                        Re-submit Attendance
                      </Button>
                    )}
                  </div>
                  {(canWorkerClockIn(selectedWorkerShift) || canWorkerReClock(selectedWorkerShift)) && (
                    <p className="text-xs text-slate-400">Your device location may be shared to confirm your attendance.</p>
                  )}
                </div>
              )}
            </div>
          ) : (
            <p className="text-sm text-slate-600">Shift detail unavailable.</p>
          )}
          <DialogFooter className="flex-col gap-2 sm:flex-col sm:space-x-0">
            <div className={`flex w-full flex-wrap gap-2 ${isSelectedHistoricalShift ? 'rounded-md border border-slate-200 bg-slate-50 p-2' : ''}`}>
              <Button variant="outline" className="flex-1 sm:flex-none" onClick={handleReportIncidentForShift}>
                {isSelectedHistoricalShift ? 'Report concern about this shift' : 'Report incident for this shift'}
              </Button>
              <Button variant="outline" className="flex-1 sm:flex-none" onClick={handleReportBodyMapForShift}>
                Record body map
              </Button>
            </div>
            <Button variant="outline" className="w-full sm:w-auto" onClick={() => setWorkerShiftDetailOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={workerShiftResponseOpen} onOpenChange={setWorkerShiftResponseOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{workerShiftResponseMode === 'reject' ? 'Reject shift' : 'Accept shift'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-slate-600">
              {workerShiftResponseMode === 'reject'
                ? 'Add an optional note for your manager when rejecting this shift.'
                : 'Add an optional note when accepting this shift.'}
            </p>
            <Textarea
              value={workerShiftResponseNote}
              onChange={(e) => setWorkerShiftResponseNote(e.target.value)}
              rows={3}
              placeholder="Optional note"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setWorkerShiftResponseOpen(false)}>
              Cancel
            </Button>
            <Button
              variant={workerShiftResponseMode === 'reject' ? 'destructive' : 'default'}
              onClick={handleWorkerShiftResponse}
              disabled={workerShiftResponding}
            >
              {workerShiftResponding ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              {workerShiftResponseMode === 'reject' ? 'Reject shift' : 'Accept shift'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={incidentModalOpen} onOpenChange={(open) => {
        setIncidentModalOpen(open);
        if (!open) setIncidentReportFromPastShift(false);
      }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{incidentReportFromPastShift ? 'Report concern about a shift' : 'Report incident'}</DialogTitle>
            {incidentReportFromPastShift ? (
              <p className="text-sm text-slate-600">You can still log a concern linked to a past shift for audit and safeguarding.</p>
            ) : null}
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Type</Label>
              <Select
                value={incidentForm.incident_type}
                onValueChange={(v) => setIncidentForm((prev) => ({
                  ...prev,
                  incident_type: v,
                  safeguarding_concern: v === 'safeguarding' ? true : prev.safeguarding_concern,
                }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="incident">Incident</SelectItem>
                  <SelectItem value="near_miss">Near miss</SelectItem>
                  <SelectItem value="concern">Concern</SelectItem>
                  <SelectItem value="safeguarding">Safeguarding concern</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Occurred at</Label>
              <Input
                type="datetime-local"
                value={incidentForm.occurred_at}
                onChange={(e) => setIncidentForm((prev) => ({ ...prev, occurred_at: e.target.value }))}
              />
            </div>
            <div>
              <Label>Location</Label>
              <Input
                value={incidentForm.location_text}
                onChange={(e) => setIncidentForm((prev) => ({ ...prev, location_text: e.target.value }))}
              />
            </div>
            <div>
              <Label>Related shift (optional)</Label>
              <Select
                value={incidentForm.related_shift_id || 'none'}
                onValueChange={(v) => {
                  if (v === 'none') {
                    setIncidentForm((prev) => ({ ...prev, related_shift_id: '', service_user_id: '' }));
                    return;
                  }
                  const selected = workerShifts.find((item) => item.shift?.id === v);
                  setIncidentForm((prev) => ({
                    ...prev,
                    related_shift_id: v,
                    service_user_id: selected?.shift?.service_user_id || '',
                    location_text: prev.location_text || selected?.shift?.location_text || '',
                  }));
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select a shift" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  {workerShifts.map((item) => (
                    <SelectItem key={item.shift?.id} value={item.shift?.id}>
                      {formatDate(item.shift?.start_at)} - {item.shift?.location_text || item.shift?.id}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Description</Label>
              <Textarea
                rows={4}
                value={incidentForm.description}
                onChange={(e) => setIncidentForm((prev) => ({ ...prev, description: e.target.value }))}
              />
            </div>
            <div>
              <Label>People involved (optional)</Label>
              <Input
                value={incidentForm.people_involved}
                onChange={(e) => setIncidentForm((prev) => ({ ...prev, people_involved: e.target.value }))}
              />
            </div>
            <div>
              <Label>Witnesses (optional)</Label>
              <Input
                value={incidentForm.witnesses}
                onChange={(e) => setIncidentForm((prev) => ({ ...prev, witnesses: e.target.value }))}
              />
            </div>
            <div>
              <Label>Immediate actions taken (optional)</Label>
              <Textarea
                rows={2}
                value={incidentForm.immediate_actions_taken}
                onChange={(e) => setIncidentForm((prev) => ({ ...prev, immediate_actions_taken: e.target.value }))}
              />
            </div>
            <div>
              <Label>Injury or harm (optional)</Label>
              <Textarea
                rows={2}
                value={incidentForm.injury_or_harm}
                onChange={(e) => setIncidentForm((prev) => ({ ...prev, injury_or_harm: e.target.value }))}
              />
            </div>
            <label className="flex items-center gap-2 text-sm text-slate-700">
              <input
                type="checkbox"
                checked={!!incidentForm.safeguarding_concern}
                onChange={(e) => setIncidentForm((prev) => ({ ...prev, safeguarding_concern: e.target.checked }))}
              />
              Safeguarding concern
            </label>
            <label className="flex items-center gap-2 text-sm text-slate-700">
              <input
                type="checkbox"
                checked={!!incidentForm.escalation_required}
                onChange={(e) => setIncidentForm((prev) => ({
                  ...prev,
                  escalation_required: e.target.checked,
                  escalation_details: e.target.checked ? prev.escalation_details : '',
                }))}
              />
              Escalation required
            </label>
            {incidentForm.escalation_required ? (
              <div>
                <Label>Escalation details</Label>
                <Textarea
                  rows={2}
                  value={incidentForm.escalation_details}
                  onChange={(e) => setIncidentForm((prev) => ({ ...prev, escalation_details: e.target.value }))}
                />
              </div>
            ) : null}
            <div>
              <Label>Learning outcome (optional)</Label>
              <Textarea
                rows={2}
                value={incidentForm.learning_outcome}
                onChange={(e) => setIncidentForm((prev) => ({ ...prev, learning_outcome: e.target.value }))}
              />
            </div>
            <div>
              <Label>Prevention actions (optional)</Label>
              <Textarea
                rows={2}
                value={incidentForm.prevention_actions}
                onChange={(e) => setIncidentForm((prev) => ({ ...prev, prevention_actions: e.target.value }))}
              />
            </div>
            <div>
              <Label>Note (optional)</Label>
              <Textarea
                rows={2}
                value={incidentForm.note}
                onChange={(e) => setIncidentForm((prev) => ({ ...prev, note: e.target.value }))}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIncidentModalOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSubmitIncident} disabled={incidentSubmitting}>
              {incidentSubmitting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              Submit report
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={incidentDetailOpen}
        onOpenChange={(open) => {
          setIncidentDetailOpen(open);
          if (!open) setSelectedIncident(null);
        }}
      >
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Incident details</DialogTitle>
          </DialogHeader>
          {selectedIncident ? (
            <div className="space-y-3 text-sm text-slate-700">
              <div className="flex items-center justify-between gap-2">
                <p className="font-medium text-slate-900">{selectedIncident.reference_number || selectedIncident.title || 'Incident'}</p>
                <Badge className={getWorkerIncidentStatusMeta(selectedIncident.status).className}>
                  {selectedIncident.status_label || getWorkerIncidentStatusMeta(selectedIncident.status).label}
                </Badge>
              </div>
              <div>
                <p className="text-xs text-slate-500">Incident type</p>
                <p className="font-medium">{(selectedIncident.incident_type || 'incident').replace(/_/g, ' ')}</p>
              </div>
              <div>
                <p className="text-xs text-slate-500">Occurred</p>
                <p className="font-medium">{formatDateTime(selectedIncident.date_occurred)}</p>
              </div>
              <div>
                <p className="text-xs text-slate-500">Location</p>
                <p className="font-medium">{selectedIncident.location || 'Location not set'}</p>
              </div>
              <div>
                <p className="text-xs text-slate-500">Description</p>
                <p className="font-medium whitespace-pre-wrap">{selectedIncident.description || '-'}</p>
              </div>
              <div>
                <p className="text-xs text-slate-500">Progress</p>
                <p className="font-medium">{selectedIncident.progress_summary || 'Your report has been submitted and is awaiting review.'}</p>
              </div>
              {selectedIncident.outcome_summary ? (
                <div>
                  <p className="text-xs text-slate-500">Outcome / follow-up summary</p>
                  <p className="font-medium whitespace-pre-wrap">{selectedIncident.outcome_summary}</p>
                </div>
              ) : null}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div>
                  <p className="text-xs text-slate-500">Submitted</p>
                  <p className="font-medium">{formatDateTime(selectedIncident.reported_at)}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500">Reviewed</p>
                  <p className="font-medium">{formatDateTime(selectedIncident.reviewed_at)}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500">Closed</p>
                  <p className="font-medium">{formatDateTime(selectedIncident.closed_at)}</p>
                </div>
              </div>
            </div>
          ) : (
            <p className="text-sm text-slate-600">Incident detail unavailable.</p>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setIncidentDetailOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Protected Raised Concern (Speak Up) Form Dialog */}
      <Dialog open={raisedConcernModalOpen} onOpenChange={setRaisedConcernModalOpen}>
        <DialogContent className="max-w-lg max-h-[90dvh] flex flex-col">
          {/* Make the long whistleblowing form scroll internally on small screens / mobile.
              Matches pattern used by other modals in this file (Shifts, Compliance, Body Map, etc.).
              Prevents "can't scroll or see content unless zoom out". The header + footer stay fixed, form fields scroll. */}
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-purple-900">
              <Shield className="h-5 w-5" />
              Raise a Protected Concern
            </DialogTitle>
            <p className="text-sm text-purple-800">
              This is a protected disclosure under the Public Interest Disclosure Act 1998 (PIDA). You will not suffer any detriment for raising a concern in good faith.
            </p>
            <p className="text-xs text-purple-600">
              Please review the current Whistleblowing Policy (see the Policies card above or in your assigned policies) for full guidance, the flow chart, named contacts (Freedom to Speak Up Guardian, DSL, RM) and escalation options.
            </p>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto space-y-4 text-sm pr-1">
            {/* Categories */}
            <div>
              <Label className="font-medium">What is this about? (select all that apply)</Label>
              <div className="mt-2 grid grid-cols-1 gap-2">
                {RAISED_CONCERN_CATEGORIES.map((cat) => (
                  <label key={cat} className="flex items-start gap-2 rounded border border-purple-200 p-2 hover:bg-purple-50 cursor-pointer">
                    <Checkbox
                      checked={raisedConcernForm.categories.includes(cat)}
                      onCheckedChange={() => toggleRaisedConcernCategory(cat)}
                    />
                    <span className="text-sm leading-tight">{cat}</span>
                  </label>
                ))}
              </div>
            </div>

            <div>
              <Label>Description of the concern (be as clear as possible)</Label>
              <Textarea
                value={raisedConcernForm.description}
                onChange={(e) => setRaisedConcernForm((prev) => ({ ...prev, description: e.target.value }))}
                placeholder="What happened? When? Who was involved? Any witnesses or evidence?"
                className="min-h-[100px]"
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label>When did it occur? (optional)</Label>
                <Input
                  type="datetime-local"
                  value={raisedConcernForm.occurred_at}
                  onChange={(e) => setRaisedConcernForm((prev) => ({ ...prev, occurred_at: e.target.value }))}
                />
              </div>
              <div>
                <Label>Location (optional)</Label>
                <Input
                  value={raisedConcernForm.location_text}
                  onChange={(e) => setRaisedConcernForm((prev) => ({ ...prev, location_text: e.target.value }))}
                  placeholder="e.g. Client home, office, during shift X"
                />
              </div>
            </div>

            {/* Anonymity - important per spec */}
            <div>
              <Label className="font-medium">How would you like to be contacted?</Label>
              <div className="mt-1 space-y-2 text-sm">
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    name="contact_pref"
                    checked={raisedConcernForm.contact_preference === 'full'}
                    onChange={() => setRaisedConcernForm((prev) => ({ ...prev, contact_preference: 'full', is_anonymous: false }))}
                  />
                  I am happy to be contacted (recommended for full investigation)
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    name="contact_pref"
                    checked={raisedConcernForm.contact_preference === 'reference_only'}
                    onChange={() => setRaisedConcernForm((prev) => ({ ...prev, contact_preference: 'reference_only', is_anonymous: false }))}
                  />
                  Contact me only using the reference number above
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    name="contact_pref"
                    checked={raisedConcernForm.contact_preference === 'none'}
                    onChange={() => setRaisedConcernForm((prev) => ({ ...prev, contact_preference: 'none', is_anonymous: true }))}
                  />
                  Completely anonymous (investigation may be harder)
                </label>
              </div>
            </div>

            <div className="rounded border border-purple-300 bg-purple-50 p-3 text-xs text-purple-800">
              <label className="flex items-start gap-2 cursor-pointer">
                <Checkbox
                  checked={raisedConcernForm.pida_ack}
                  onCheckedChange={(v) => setRaisedConcernForm((prev) => ({ ...prev, pida_ack: !!v }))}
                />
                <span>
                  I understand this is a protected disclosure in the public interest and that I am protected from detriment for raising it in good faith.
                </span>
              </label>
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setRaisedConcernModalOpen(false)} disabled={raisedConcernSubmitting}>
              Cancel
            </Button>
            <Button 
              onClick={handleSubmitRaisedConcern} 
              disabled={raisedConcernSubmitting || !raisedConcernForm.pida_ack}
              className="bg-purple-700 hover:bg-purple-800"
            >
              {raisedConcernSubmitting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Shield className="h-4 w-4 mr-2" />}
              Submit Protected Concern
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Body Map Dialog ─────────────────────────────────────────────────── */}
      <Dialog open={bodyMapModalOpen} onOpenChange={setBodyMapModalOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Record Body Map</DialogTitle>
            <p className="text-xs text-slate-500 mt-1">
              Record any injury, mark or bruising you have observed during normal care activities.
              Staff must not physically inspect an individual if harm is suspected — that is the role of a medical practitioner.
            </p>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Gender of service user <span className="text-red-500">*</span></Label>
              <select
                value={bodyMapForm.gender || ''}
                onChange={(e) => setBodyMapForm(prev => ({ ...prev, gender: e.target.value }))}
                className={`mt-1 w-full text-sm border rounded-lg px-3 py-2 bg-white ${
                  !bodyMapForm.gender ? 'border-red-300' : 'border-slate-300'
                }`}
              >
                <option value="">Select gender...</option>
                <option value="male">Male</option>
                <option value="female">Female</option>
              </select>
              <p className="text-xs text-slate-400 mt-1">This determines which body diagram is used in the exported PDF.</p>
            </div>
            <div>
              <Label>Date of injury (if known)</Label>
              <Input
                type="date"
                value={bodyMapForm.injury_date}
                onChange={(e) => setBodyMapForm(prev => ({ ...prev, injury_date: e.target.value }))}
                className="mt-1"
              />
            </div>
            <div>
              <Label>Reported to <span className="text-red-500">*</span></Label>
              <select
                value={bodyMapForm.reported_to || ''}
                onChange={(e) => setBodyMapForm(prev => ({ ...prev, reported_to: e.target.value }))}
                className={`mt-1 w-full text-sm border rounded-lg px-3 py-2 bg-white ${
                  !bodyMapForm.reported_to ? 'border-red-300' : 'border-slate-300'
                }`}
              >
                <option value="">Select admin/manager/supervisor...</option>
                {admins.map((admin) => (
                  <option key={admin} value={admin}>{admin}</option>
                ))}
              </select>
            </div>

            {/* Mark entry */}
            <div className="border border-slate-200 rounded-xl p-3 space-y-3 bg-slate-50">
              <p className="text-sm font-medium text-slate-700">Add a mark / injury location</p>
              <div>
                <Label className="text-xs">Where is the injury or mark?</Label>
                <select
                  value={bodyMapMarkDraft.region}
                  onChange={(e) => {
                    const region = e.target.value;
                    if (!region) {
                      setBodyMapMarkDraft(prev => ({ ...prev, region: '', view: null, x_pct: null, y_pct: null }));
                      return;
                    }
                    const view = regionDefaultView(region);
                    // View selection only — do NOT pre-populate a marker. The
                    // recorded location must come from the user's exact tap.
                    setBodyMapMarkDraft(prev => ({ ...prev, region, view, x_pct: null, y_pct: null }));
                  }}
                  className="mt-1 w-full text-sm border border-slate-300 rounded-lg px-3 py-2 bg-white"
                  data-testid="bodymap-region-select"
                >
                  <option value="">Select region...</option>
                  {[
                    "Head – top","Forehead","Left eye / cheek","Right eye / cheek","Nose","Mouth / lips","Left ear","Right ear","Chin / jaw",
                    "Neck – front","Neck – back","Left shoulder","Right shoulder",
                    "Chest – left","Chest – right","Abdomen – left","Abdomen – right",
                    "Upper back – left","Upper back – right","Lower back – left","Lower back – right",
                    "Buttocks – left","Buttocks – right","Sacrum / coccyx","Groin / perineal area",
                    "Left upper arm","Left elbow","Left forearm","Left wrist","Left hand / fingers",
                    "Right upper arm","Right elbow","Right forearm","Right wrist","Right hand / fingers",
                    "Left hip","Left thigh","Left knee","Left lower leg / shin","Left ankle","Left foot / toes",
                    "Right hip","Right thigh","Right knee","Right lower leg / shin","Right ankle","Right foot / toes",
                  ].map(r => <option key={r} value={r}>{r}</option>)}
                </select>
              </div>
              {bodyMapMarkDraft.region && (
                <div>
                  <Label className="text-xs">Tap the exact location</Label>
                  <div className="mt-1">
                    <BodyDiagramPicker
                      gender={bodyMapForm.gender}
                      region={bodyMapMarkDraft.region}
                      view={bodyMapMarkDraft.view}
                      xPct={bodyMapMarkDraft.x_pct}
                      yPct={bodyMapMarkDraft.y_pct}
                      marks={bodyMapForm.marks}
                      onChange={({ view, xPct, yPct }) =>
                        setBodyMapMarkDraft(prev => ({ ...prev, view, x_pct: xPct, y_pct: yPct }))
                      }
                    />
                  </div>
                </div>
              )}
              <div>
                <Label className="text-xs">What did you observe?</Label>
                <textarea
                  rows={2}
                  value={bodyMapMarkDraft.description}
                  onChange={(e) => setBodyMapMarkDraft(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="e.g. 2cm bruise, purple/yellow, circular — appears fresh"
                  className="mt-1 w-full text-sm border border-slate-300 rounded-lg px-3 py-2 resize-none"
                  data-testid="bodymap-description"
                />
              </div>
              <Button
                size="sm"
                variant="outline"
                onClick={handleAddBodyMapMark}
                disabled={bodyMapMarkDraft.x_pct == null || bodyMapMarkDraft.y_pct == null || !bodyMapMarkDraft.description.trim()}
                className="w-full disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="bodymap-add-mark"
              >
                + Add mark
              </Button>
              {bodyMapMarkDraft.region && (bodyMapMarkDraft.x_pct == null || bodyMapMarkDraft.y_pct == null) && (
                <p className="text-xs text-amber-600 text-center" data-testid="bodymap-tap-hint">
                  Tap the exact location on the body map to place the marker.
                </p>
              )}
            </div>

            {/* Recorded marks */}
            {bodyMapForm.marks.length > 0 && (
              <div className="space-y-2">
                <p className="text-sm font-medium text-slate-700">Recorded marks ({bodyMapForm.marks.length})</p>
                {bodyMapForm.marks.map((m, i) => (
                  <div key={i} className="flex items-start justify-between gap-2 bg-white border border-slate-200 rounded-xl px-3 py-2 text-sm">
                    <div>
                      <p className="font-medium text-slate-800">{i + 1}. {m.region}</p>
                      <p className="text-slate-500 text-xs mt-0.5">{m.description}</p>
                    </div>
                    <button onClick={() => handleRemoveBodyMapMark(i)} className="text-red-400 hover:text-red-600 mt-0.5 shrink-0">✕</button>
                  </div>
                ))}
              </div>
            )}

            <div>
              <Label>Additional information</Label>
              <textarea
                rows={3}
                value={bodyMapForm.additional_information}
                onChange={(e) => setBodyMapForm(prev => ({ ...prev, additional_information: e.target.value }))}
                placeholder="Any other relevant details..."
                className="mt-1 w-full text-sm border border-slate-300 rounded-lg px-3 py-2 resize-none"
              />
            </div>
          </div>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setBodyMapModalOpen(false)}>Cancel</Button>
            <Button onClick={handleSubmitBodyMap} disabled={bodyMapSubmitting}>
              {bodyMapSubmitting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              Submit Body Map
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ─── Site QR Verification Dialog ─────────────────────────────────────── */}
      <Dialog
        open={siteVerifyOpen}
        onOpenChange={(open) => {
          if (!open) { stopQrScanner(); setSiteManualInput(''); setSiteScanResult(null); }
          setSiteVerifyOpen(open);
        }}
      >
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <QrCode className="h-5 w-5 text-primary" />
              Site Verification (optional)
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 text-sm text-slate-700">
            <p>
              Scan the QR code posted at this care location to add site verification to your attendance record.
              This is optional — you can continue with GPS evidence only.
            </p>

            {/* Scanner area */}
            {siteScanResult ? (
              <div className="flex items-center gap-2 rounded-lg border border-green-300 bg-green-50 px-3 py-2 text-green-800">
                <CheckCircle className="h-4 w-4 shrink-0" />
                <span className="font-medium">QR code scanned successfully</span>
              </div>
            ) : (
              <div className="space-y-2">
                <div id="site-qr-reader" className={siteScanActive ? 'block' : 'hidden'} />
                {!siteScanActive && (
                  <Button
                    size="sm"
                    variant="outline"
                    className="w-full"
                    onClick={startQrScanner}
                  >
                    <Camera className="h-4 w-4 mr-2" />
                    Open Camera to Scan QR
                  </Button>
                )}
                {siteScanActive && (
                  <Button
                    size="sm"
                    variant="ghost"
                    className="w-full text-slate-500"
                    onClick={stopQrScanner}
                  >
                    Stop camera
                  </Button>
                )}
              </div>
            )}

            {/* Manual code input */}
            {!siteScanResult && (
              <div className="space-y-1">
                <label className="text-xs font-medium text-slate-600">Or enter code manually</label>
                <input
                  type="text"
                  className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="Paste or type the location code"
                  value={siteManualInput}
                  onChange={(e) => setSiteManualInput(e.target.value)}
                  autoComplete="off"
                  autoCorrect="off"
                  spellCheck={false}
                />
              </div>
            )}
          </div>

          <DialogFooter className="flex-col gap-2 sm:flex-col">
            <Button
              className="w-full"
              onClick={confirmSiteVerify}
              disabled={!siteScanResult && !siteManualInput.trim()}
            >
              <QrCode className="h-4 w-4 mr-2" />
              Confirm Verification &amp; {siteVerifyPending?.mode === 'in' ? 'Clock In' : siteVerifyPending?.mode === 'out' ? 'Clock Out' : 'Re-submit'}
            </Button>
            <Button
              variant="ghost"
              className="w-full text-slate-500"
              onClick={skipSiteVerify}
            >
              Continue with GPS only (skip QR)
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* ────────────────────────────────────────────────────────────────────── */}

      <StructuredAppraisalForm
        open={selfAssessmentOpen}
        onOpenChange={setSelfAssessmentOpen}
        token={localStorage.getItem('workerToken')}
        mode="worker"
        cycleId={activeAppraisalCycleId}
        initialTemplateId="self_assessment"
        onSuccess={() => {
          setSelfAssessmentOpen(false);
          fetchDashboard();
        }}
      />

    {/* App-style bottom navigation (Uber-like) - fixed for mobile app feel */}
    {isActiveEmployee && (
      <nav aria-label="Worker navigation" className="fixed bottom-0 left-0 right-0 z-[70] bg-white border-t border-slate-200 shadow-[0_-2px_10px_rgba(0,0,0,0.05)] safe-bottom">
        <div className="max-w-7xl mx-auto px-2">
          <div className="flex items-center justify-around h-14 text-xs">
            <button
              aria-current={workerMainView === 'today' ? 'page' : undefined}
              onClick={() => { setWorkerMainView('today'); }}
              className={`flex flex-col items-center justify-center flex-1 min-h-11 py-1 ${workerMainView === 'today' ? 'text-[#0F5C5E] font-medium' : 'text-slate-500 hover:text-slate-700'}`}
            >
              <Calendar className="h-5 w-5 mb-0.5" />
              Today
            </button>
            <button
              aria-current={workerMainView === 'shifts' ? 'page' : undefined}
              onClick={() => { setWorkerMainView('shifts'); }}
              className={`flex flex-col items-center justify-center flex-1 min-h-11 py-1 ${workerMainView === 'shifts' ? 'text-[#0F5C5E] font-medium' : 'text-slate-500 hover:text-slate-700'}`}
            >
              <Calendar className="h-5 w-5 mb-0.5" />
              Shifts
            </button>
            <button
              aria-current={workerMainView === 'compliance' ? 'page' : undefined}
              onClick={() => { setWorkerMainView('compliance'); }}
              className={`flex flex-col items-center justify-center flex-1 min-h-11 py-1 ${workerMainView === 'compliance' ? 'text-[#0F5C5E] font-medium' : 'text-slate-500 hover:text-slate-700'}`}
            >
              <ShieldCheck className="h-5 w-5 mb-0.5" />
              Alerts
            </button>
            <button
              aria-current={['more', 'file', 'id_card', 'speakup'].includes(workerMainView) ? 'page' : undefined}
              onClick={() => { setWorkerMainView('more'); }}
              className={`flex flex-col items-center justify-center flex-1 min-h-11 py-1 ${['more', 'file', 'id_card', 'speakup'].includes(workerMainView) ? 'text-[#0F5C5E] font-medium' : 'text-slate-500 hover:text-slate-700'}`}
            >
              <Menu className="h-5 w-5 mb-0.5" />
              More
            </button>
          </div>
        </div>
      </nav>
    )}

    </WorkerDashboardPage>
  );
}


